import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './App.css';
import Login from './Login';
import AnalyticsDashboard from './AnalyticsDashboard';
import StruggleMomentsDashboard from './StruggleMomentsDashboard';
import StruggleHeatmap from './components/StruggleHeatmap';
import SessionTimer from './components/SessionTimer';
import SubscriptionPaywall from './components/SubscriptionPaywall';
import SubscriptionBanner from './components/SubscriptionBanner';
import { SessionProvider, useSession } from './context/SessionContext';
import Tutorial from './Tutorial';
import ReportProblem from './ReportProblem';
import KnowledgeHealthPanel from './components/KnowledgeHealthPanel';
import KnowledgeStatesPanel from './components/KnowledgeStatesPanel';
import CodeDetectedPopup from './components/CodeDetectedPopup';
import DownloadModal from './components/DownloadModal';
import CourseContentView from './components/CourseContentView';
import MessageContent from './components/MessageContent';
import StudyAdvicePanel from './components/StudyAdvicePanel';
import AdminPanel from './components/AdminPanel';
import PracticeExam from './components/PracticeExam';
import BadgePanel from './components/BadgePanel';
import CourseSlidesPanel from './components/CourseSlidesPanel';
import ClassQuizResults from './components/ClassQuizResults';
import ClassCodeModal from './components/ClassCodeModal';
import TurtleModal from './components/TurtleModal';
import VisualDebugger from './components/VisualDebugger';
import Editor from '@monaco-editor/react';
import { API_BASE_URL, PYTHON_INTERPRETER_URL, logger, apiFetch, setLoggingOut, isDemoMode, INCLUDE_ABOUT_LINK } from './config';
import { analyzeStruggleInBackground } from './utils/struggleDetection';
import 'katex/dist/katex.min.css';

// Import utility functions from utils.js
import {
  SEED_SUBJECTS,
  getESTTimestamp,
  getESTNow,
  normalizeCount,
  detectFlipFlopping,
  detectTrend,
  calculateHintLevel,
  calculateMemoryStrength,
  renderMessageContent,
  detectCodeInText,
  analyzePerformanceHistory
} from './utils';

// Helper function to get curriculum folder path based on user's selected section and university
export const getCurriculumFolder = () => {
  const classSection = localStorage.getItem('selected_section') || (localStorage.getItem('class_section') || '').split(',')[0].trim();
  const university = localStorage.getItem('university');
  return `${classSection}-${university}`;
};

// Helper function to build curriculum URL
export const getCurriculumUrl = (fileName) => {
  const folder = getCurriculumFolder();
  return `/curriculum/${folder}/${fileName}`;
};

// Helper function to build slides_practices URL
export const getSlidesProacticesUrl = (fileName) => {
  const folder = getCurriculumFolder();
  return `/slides_practices/${folder}/${fileName}`;
};

function AppContent() {
  const { clearSession, setOnSessionExpiredCallback } = useSession();
  
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem('isLoggedIn') === 'true';
  });

  // Show/hide the plain-HTML about link based on login state and flag
  useEffect(() => {
    const link = document.getElementById('about-link');
    if (link) link.style.display = (!isLoggedIn && INCLUDE_ABOUT_LINK) ? 'block' : 'none';
  }, [isLoggedIn]);

  const [username, setUsername] = useState(() => {
    return localStorage.getItem('username') || '';
  });
  const [mode, setMode] = useState('breakdown'); // 'breakdown', 'debugging', 'building-blocks'
  const [showMastery, setShowMastery] = useState(false); // collapse/expand Building Block Mastery
  const masteryRef = React.useRef(null);
  const [showAnalyticsButtons, setShowAnalyticsButtons] = useState(true); // collapse/expand analytics buttons in right panel
  const [showAnalytics, setShowAnalytics] = useState(false); // New state for analytics modal
  const [showStruggles, setShowStruggles] = useState(false); // New state for struggle dashboard
  const [showStruggleHeatmap, setShowStruggleHeatmap] = useState(false); // Struggle heatmap modal
  const [showTutorial, setShowTutorial] = useState(false); // Tutorial modal
  const [showReportProblem, setShowReportProblem] = useState(false); // Report Problem modal
  const [showCourseContent, setShowCourseContent] = useState(false); // Course Content view
  const [showCourseSlides, setShowCourseSlides] = useState(false); // Course Slides PDF viewer
  const [showTurtle, setShowTurtle] = useState(false); // Turtle Graphics modal
  const [showStudyAdvice, setShowStudyAdvice] = useState(false); // Study Advice panel
  const [layoutStacked, setLayoutStacked] = useState(false); // Toggle chat+code layout
  // Resizable width (in px) of the right-side chat history panel — null = use original CSS default
  const [historyPanelWidth, setHistoryPanelWidth] = useState(() => {
    const saved = parseInt(localStorage.getItem('historyPanelWidth'), 10);
    return (saved && saved >= 280 && saved <= 900) ? saved : null;
  });
  const isDraggingHistoryRef = useRef(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isDraggingHistoryRef.current) return;
      const newWidth = window.innerWidth - e.clientX - 20;
      const clamped = Math.min(Math.max(newWidth, 280), Math.min(900, window.innerWidth - 400));
      setHistoryPanelWidth(clamped);
    };
    const handleMouseUp = () => {
      if (isDraggingHistoryRef.current) {
        isDraggingHistoryRef.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        try {
          if (historyPanelWidth != null) localStorage.setItem('historyPanelWidth', String(historyPanelWidth));
        } catch {}
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [historyPanelWidth]);

  const startHistoryDrag = (e) => {
    e.preventDefault();
    isDraggingHistoryRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const resetHistoryWidth = () => {
    setHistoryPanelWidth(null);
    try { localStorage.removeItem('historyPanelWidth'); } catch {}
  };
  const [showAdminPanel, setShowAdminPanel] = useState(false); // Admin Panel view
  const [showPracticeExam, setShowPracticeExam] = useState(false); // Practice Exam modal
  const [showBadgePanel, setShowBadgePanel] = useState(false); // Badge & Points panel
  const [showClassQuizResults, setShowClassQuizResults] = useState(false); // Class Quiz Results
  const [showClassCodeModal, setShowClassCodeModal] = useState(false); // Class Code Modal
  const [showVisualizer, setShowVisualizer] = useState(false);
  const [traceSteps, setTraceSteps] = useState([]);
  const [isTracing, setIsTracing] = useState(false);
  const [showInputModal, setShowInputModal] = useState(false);
  const [inputModalValues, setInputModalValues] = useState('');
  const [pendingTraceCode, setPendingTraceCode] = useState('');
  const [showCodeDetectedPopup, setShowCodeDetectedPopup] = useState(false); // Popup when code detected in chat
  const [showDownloadModal, setShowDownloadModal] = useState(false); // Download filename modal
  const [downloadFilename, setDownloadFilename] = useState('code'); // Filename for download
  const [pendingCodeMessage, setPendingCodeMessage] = useState(''); // Store the message with code for later

  // ==================== SUBSCRIPTION STATE ====================
  const [subscriptionStatus, setSubscriptionStatus] = useState(() => {
    return localStorage.getItem('subscriptionStatus') || 'unknown';
  });
  const [canAccess, setCanAccess] = useState(() => {
    return localStorage.getItem('canAccess') !== 'false'; // default true for graceful degradation
  });
  const [trialDaysLeft, setTrialDaysLeft] = useState(() => {
    const stored = localStorage.getItem('trialDaysLeft');
    return stored !== null ? parseInt(stored, 10) : -1;
  });
  // =============================================================

  const modeNames = {
    'breakdown': 'Problem Breakdown Mode',
    'debugging': 'Problem Debugging Mode',
    'building-blocks': 'Problem Building Block Practice'
  };

  // OpenAI API Key - read from config (which reads from .env)

  const [sessionProgress, setSessionProgress] = useState({}); // Session-only deltas per subject
  const [prompt, setPrompt] = useState('');
  const [showPromptHint, setShowPromptHint] = useState(() => localStorage.getItem('promptHintDismissed') !== 'true');
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState([]); // State to store the program steps
  const [pythonCode, setPythonCode] = useState(""); // State for the code runner textarea
  const [useMainMonaco, setUseMainMonaco] = useState(false); // Monaco vs simple editor toggle
  const [mainEditorTheme, setMainEditorTheme] = useState('vs'); // Editor theme - light by default
  const mainCompletionDisposableRef = useRef(null);

  // IntelliSense handler for main page Monaco editor
  const handleMainEditorDidMount = useCallback((editor, monaco) => {
    if (mainCompletionDisposableRef.current) {
      mainCompletionDisposableRef.current.dispose();
      mainCompletionDisposableRef.current = null;
    }

    editor.updateOptions({
      wordBasedSuggestions: 'off',
      suggest: { showWords: false, showSnippets: false, showUsers: false, showIcons: true },
      quickSuggestions: { other: true, comments: false, strings: false },
    });

    var strMethods = ['capitalize','casefold','center','count','encode','endswith','find','format','index','isalnum','isalpha','isdigit','islower','isnumeric','isspace','isupper','join','lower','lstrip','replace','rfind','rstrip','split','startswith','strip','swapcase','title','upper','zfill'];
    var listMethods = ['append','clear','copy','count','extend','index','insert','pop','remove','reverse','sort'];
    var dictMethods = ['clear','copy','get','items','keys','pop','setdefault','update','values'];
    var setMethods = ['add','clear','copy','difference','discard','intersection','issubset','issuperset','pop','remove','union','update'];
    var typeMap = { str: strMethods, list: listMethods, dict: dictMethods, set: setMethods };

    var keywords = ['False','None','True','and','as','assert','async','await','break','class','continue','def','del','elif','else','except','finally','for','from','global','if','import','in','is','lambda','nonlocal','not','or','pass','raise','return','try','while','with','yield'];
    var builtins = ['abs','all','any','bool','chr','dict','dir','enumerate','eval','exec','filter','float','format','getattr','hasattr','help','hex','id','input','int','isinstance','iter','len','list','map','max','min','next','open','ord','pow','print','range','repr','reversed','round','set','setattr','slice','sorted','str','sum','tuple','type','vars','zip'];
    var modules = ['os','sys','math','random','datetime','json','csv','re','collections','itertools','functools','time','numpy','pandas','matplotlib','requests','turtle','tkinter'];

    var disp = monaco.languages.registerCompletionItemProvider('*', {
      triggerCharacters: ['.'],
      provideCompletionItems: function(model, position) {
        if (model !== editor.getModel()) return { suggestions: [] };
        var word = model.getWordUntilPosition(position);
        var range = { startLineNumber: position.lineNumber, endLineNumber: position.lineNumber, startColumn: word.startColumn, endColumn: word.endColumn };
        var line = model.getLineContent(position.lineNumber);
        var before = line.substring(0, position.column - 1);

        var dm = before.match(/(\w+)\.\w*$/);
        if (dm) {
          var obj = dm[1];
          var meths = typeMap[obj];
          if (!meths) {
            var allText = model.getValue();
            var varMatch = allText.match(new RegExp('\\b' + obj + '\\s*=\\s*(.+)'));
            if (varMatch) {
              var val = varMatch[1].trim();
              if (val.startsWith('"') || val.startsWith("'") || val.startsWith('input(')) meths = strMethods;
              else if (val.startsWith('[') || val.startsWith('list(')) meths = listMethods;
              else if (val.startsWith('{') && val.includes(':')) meths = dictMethods;
              else if (val.startsWith('{') || val.startsWith('set(')) meths = setMethods;
            }
            if (!meths) { var seen = {}; meths = []; [strMethods, listMethods, dictMethods, setMethods].forEach(function(arr) { arr.forEach(function(m) { if (!seen[m]) { seen[m] = true; meths.push(m); } }); }); }
          }
          return { suggestions: meths.map(function(m, i) { return { label: m, kind: monaco.languages.CompletionItemKind.Method, insertText: m, range: range, detail: 'method' }; }) };
        }

        var sugs = [];
        keywords.forEach(function(k) { sugs.push({ label: k, kind: monaco.languages.CompletionItemKind.Keyword, insertText: k, range: range, detail: 'keyword' }); });
        builtins.forEach(function(b) { sugs.push({ label: b, kind: monaco.languages.CompletionItemKind.Function, insertText: b, range: range, detail: 'built-in' }); });
        if (before.match(/^\s*(import|from)\s/)) {
          modules.forEach(function(m) { sugs.push({ label: m, kind: monaco.languages.CompletionItemKind.Module, insertText: m, range: range, detail: 'module' }); });
        }
        return { suggestions: sugs };
      }
    });
    mainCompletionDisposableRef.current = disp;
  }, []);
  const [runOutput, setRunOutput] = useState(''); // State for the code runner output
  const [runLoading, setRunLoading] = useState(false); // Loading state for the run button
  const [executionId, setExecutionId] = useState(null); // State for paused execution ID
  const [isAwaitingInput, setIsAwaitingInput] = useState(false); // State to check if waiting for user input
  const [lastPrompt, setLastPrompt] = useState(''); // State to store the last input prompt from the backend
  const [promptEndIndex, setPromptEndIndex] = useState(0); // Index where the prompt ends and user input starts
  const [curriculumContent, setCurriculumContent] = useState(''); // Knowledge base from slides
  const [loadedSlides, setLoadedSlides] = useState([]); // Track which slide files are loaded for dynamic forbidden list
  const [syllabusMap, setSyllabusMap] = useState({}); // To store the date -> filename mapping
  const [selectedSyllabusDate, setSelectedSyllabusDate] = useState('first'); // Track user selection
  // Multi-section support: admin may have comma-separated sections
  const userSections = (localStorage.getItem('class_section') || '').split(',').map(s => s.trim()).filter(Boolean);
  const [selectedSection, setSelectedSection] = useState(() => {
    const saved = localStorage.getItem('selected_section');
    if (saved && userSections.includes(saved)) return saved;
    const first = userSections[0] || '';
    if (first) localStorage.setItem('selected_section', first);
    return first;
  });
  const [isCurriculumLoading, setIsCurriculumLoading] = useState(true); // Track loading state
  const [masteryAnalysis, setMasteryAnalysis] = useState(""); // Analytical summary of student history
  const [masteryHistory, setMasteryHistory] = useState([]); // Raw history for graphing
  const [masteryAggregate, setMasteryAggregate] = useState({}); // { subject: { correct, wrong } }
  const [showDebug, setShowDebug] = useState(false); // Toggle for instructor debug view
  const [analyticsRecommendations, setAnalyticsRecommendations] = useState([]); // AI recommendations for Full Analytics panel
  const [analyticsRecsLoading, setAnalyticsRecsLoading] = useState(false); // Loading state for AI recommendations
  const [recommendationCount, setRecommendationCount] = useState(3); // Number of recommendations to show (1-6, default 3)
  const [currentTopics, setCurrentTopics] = useState([]); // Topics detected in current message
  const [knowledgeHealth, setKnowledgeHealth] = useState({}); // { subject: { memoryStrength, daysSince, urgency, ... } }
  const [showKnowledgeHealth, setShowKnowledgeHealth] = useState(false); // Toggle for knowledge health panel
  const [showKnowledgeStates, setShowKnowledgeStates] = useState(false); // Toggle for HMM knowledge states panel
  const [masteredTopics, setMasteredTopics] = useState(() => {
    // Load mastered topics from localStorage (per user)
    const username = localStorage.getItem('username') || 'default';
    const saved = localStorage.getItem(`masteredTopics_${username}`);
    return saved ? JSON.parse(saved) : [];
  }); // Topics marked as "mastered" by student (shown gray)
  const [softReviewsSavedToday, setSoftReviewsSavedToday] = useState(new Set()); // Track soft reviews saved today (local dedup)
  const [questionAnsweredSubjects, setQuestionAnsweredSubjects] = useState([]); // Subjects from question_answered table for normalization
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    // Persist notification preference in localStorage
    const saved = localStorage.getItem('notificationsEnabled');
    return saved !== null ? saved === 'true' : true; // Default to enabled
  });
  const [userRole, setUserRole] = useState(() => {
    return localStorage.getItem('userRole') || 'user';
  });

  const serverStdoutRef = React.useRef(0); 
  const syncedStepsRef = React.useRef(new Set()); 
  const previousStep1Ref = React.useRef(null); // Track step 1 text to detect new problems
  const prevPlanRef = React.useRef([]); // Track previous plan state to preserve correct step code
  const historyEndRef = React.useRef(null); 
  const outputRef = React.useRef(null);
  const historyRef = React.useRef(null);
  const lastSyncedMessageRef = React.useRef(null);
  const lastSyncedDataRef = React.useRef({});
  const sessionStartMasteryRef = React.useRef({}); // Track DB mastery at SESSION START (doesn't change during session)
  
  // 5-attempt hint tracking: Track student attempts per step
  const stepAttemptsRef = React.useRef(0); // Current attempt count for current step
  const currentStepNumberRef = React.useRef(0); // Which step number we're working on
  const currentPhaseRef = React.useRef('planning'); // 'planning' or 'coding'
  
  // Helper function to analyze conversation and detect current step/phase
  const analyzeConversationForAttempts = (messageHistory) => {
    let currentStepNumber = 0;
    let currentPhase = 'planning'; // 'planning' or 'coding'
    let attemptCount = 0;
    let stepJustCompleted = false;
    
    // Scan through messages to determine current state
    for (let i = 0; i < messageHistory.length; i++) {
      const msg = messageHistory[i];
      const content = msg.content || '';
      
      if (msg.role === 'assistant') {
        // Check if a new step was defined (PLAN_START with new step)
        if (content.includes('[PLAN_START]')) {
          const planMatch = content.match(/\[PLAN_START\]([\s\S]*?)\[PLAN_END\]/);
          if (planMatch) {
            const steps = planMatch[1].trim().split('\n').filter(s => s.trim());
            const incompleteSteps = steps.filter(s => 
              !s.toLowerCase().includes('complete') && 
              !s.includes('✅')
            );
            // If there's an incomplete step, we're working on it
            if (incompleteSteps.length > 0) {
              currentStepNumber = steps.length;
            }
          }
        }
        
        // Check if tutor is asking for code (transition to coding phase)
        const codingTransitionPhrases = [
          'how would you write',
          'how would you code',
          'write the code',
          'let\'s code this',
          'now code this',
          'try coding',
          'what code would',
          'how do you implement'
        ];
        const lowerContent = content.toLowerCase();
        for (const phrase of codingTransitionPhrases) {
          if (lowerContent.includes(phrase)) {
            currentPhase = 'coding';
            attemptCount = 0; // Reset attempts when moving to coding phase
            break;
          }
        }
        
        // Check if step was completed (reset for next step)
        if (content.includes('[STEP_COMPLETE]')) {
          stepJustCompleted = true;
          currentPhase = 'planning'; // Next step starts with planning
          attemptCount = 0;
        }
        
        // Check if tutor is asking for next step
        const nextStepPhrases = [
          'what do you think the next step',
          'what should the next step',
          'what\'s the next step',
          'what is the next step'
        ];
        for (const phrase of nextStepPhrases) {
          if (lowerContent.includes(phrase)) {
            currentPhase = 'planning';
            attemptCount = 0;
            break;
          }
        }
      } else if (msg.role === 'user') {
        // User message counts as an attempt (if we're in an active step)
        if (currentStepNumber > 0 && !stepJustCompleted) {
          // Don't count certain non-attempt messages
          const nonAttemptMessages = [
            'need help',
            'i give up',
            'show me',
            'what\'s next'
          ];
          const isNonAttempt = nonAttemptMessages.some(phrase => 
            content.toLowerCase().trim() === phrase || 
            content.toLowerCase().trim().startsWith(phrase)
          );
          
          if (!isNonAttempt && content.trim().length > 2) {
            attemptCount++;
          }
        }
        stepJustCompleted = false; // Reset flag after user responds
      }
    }
    
    return {
      stepNumber: currentStepNumber,
      phase: currentPhase,
      attempts: attemptCount
    };
  };
  
  const [messages, setMessages] = useState([]);
  const [adaptiveHints, setAdaptiveHints] = useState({}); // { subject: { accuracy, hintLevel, source } }

  // Calculate knowledge health for all subjects
  // Now async to fetch soft reviews and question_answered data
  // Can work with just question_answered data even if mastery_history is empty
  const calculateKnowledgeHealth = async (history, aggregates) => {
    const username = localStorage.getItem('username');
    
    // Don't return early - we may have question_answered data even if history is empty
    const hasHistory = history && history.length > 0;
    const hasAggregates = aggregates && Object.keys(aggregates).length > 0;

    // Normalize subject names to Title Case to merge case variations
    const normalizeSubject = (subject) => {
      if (!subject) return '';
      return subject.toLowerCase().split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    // Group history by normalized subject (merge case variations)
    const historyBySubject = {};
    if (hasHistory) {
      history.forEach(entry => {
        const subject = entry.subject;
        if (!subject) return;
        const normalized = normalizeSubject(subject);
        if (!historyBySubject[normalized]) {
          historyBySubject[normalized] = [];
        }
        historyBySubject[normalized].push(entry);
      });
    }

    // Also normalize aggregates
    const normalizedAggregates = {};
    if (hasAggregates) {
      Object.keys(aggregates).forEach(subject => {
        const normalized = normalizeSubject(subject);
        if (!normalizedAggregates[normalized]) {
          normalizedAggregates[normalized] = { correct: 0, wrong: 0 };
        }
        normalizedAggregates[normalized].correct += aggregates[subject].correct || 0;
        normalizedAggregates[normalized].wrong += aggregates[subject].wrong || 0;
      });
    }

    // Fetch soft reviews for all subjects (batch fetch to minimize API calls)
    const allSubjects = [...new Set([...Object.keys(historyBySubject), ...Object.keys(normalizedAggregates)])];
    const softReviewCounts = {};
    
    // Fetch soft reviews for each subject (still need per-subject calls for now)
    if (username) {
      logger.debug('📝 GET softReviewHistory - Fetching for', allSubjects.length, 'subjects');
      await Promise.all(allSubjects.map(async (subject) => {
        try {
          // Fetch soft reviews
          const softReviewUrl = `${API_BASE_URL}/api/studentSoftReviewHistory?username=${encodeURIComponent(username)}&subject=${encodeURIComponent(subject)}`;
          const softReviewRes = await apiFetch(softReviewUrl);
          if (softReviewRes.ok) {
            const data = await softReviewRes.json();
            const reviews = data.softreviewhistory || [];
            // Count reviews in last 7 days (backend already filters, but double-check with EST)
            const now = getESTNow(); // Use EST for consistent calculation with backend dates
            const recentReviews = reviews.filter(r => {
              const reviewDate = new Date(r.review_date.replace(' ', 'T')); // Parse EST date from backend
              const daysAgo = (now - reviewDate) / (1000 * 60 * 60 * 24);
              return daysAgo <= 7;
            });
            softReviewCounts[subject] = recentReviews.length;
          }
        } catch (err) {
          console.error(`📝 GET softReviewHistory - ❌ ERROR for "${subject}":`, err);
          softReviewCounts[subject] = 0;
        }
      }));
      logger.debug('📝 GET softReviewHistory - FINAL counts:', softReviewCounts);
    }

    // Calculate memory strength for each subject (with soft review boost)
    const health = {};
    Object.keys(historyBySubject).forEach(subject => {
      const subjectHistory = historyBySubject[subject];
      const aggregate = normalizedAggregates[subject] || null;
      const softReviewCount = softReviewCounts[subject] || 0;
      const strength = calculateMemoryStrength(subject, subjectHistory, aggregate, softReviewCount);
      if (strength) {
        health[subject] = strength;
      }
    });

    // Also check aggregates for subjects not in recent history
    Object.keys(normalizedAggregates).forEach(subject => {
      if (!health[subject]) {
        const softReviewCount = softReviewCounts[subject] || 0;
        const strength = calculateMemoryStrength(subject, [], normalizedAggregates[subject], softReviewCount);
        if (strength) {
          health[subject] = strength;
        }
      }
    });

    logger.debug('🧠 Knowledge Health calculated:', Object.keys(health).length, 'subjects');
    return health;
  };

  // Function to fetch adaptive hint levels for a subject
  const fetchAdaptiveHintLevel = async (subject) => {
    const username = localStorage.getItem('username');
    if (!username || !subject) return null;

    try {
      // Fetch questionAnswered data for this subject (to supplement hint calculation)
      let questionAnsweredData = null;
      try {
        const qaUrl = `${API_BASE_URL}/api/studentQuestionAnswered?username=${encodeURIComponent(username)}&subject=${encodeURIComponent(subject)}&limit=500`;
        console.log(`📝 GET questionAnswered (for hints): ${qaUrl}`);
        const qaRes = await apiFetch(qaUrl);
        if (qaRes.ok) {
          const qaData = await qaRes.json();
          const qaHistory = qaData.historyquestionanswered || [];
          if (qaHistory.length > 0) {
            // Handle string values "correct"/"wrong" from API (not boolean)
            const correct = qaHistory.filter(q => q.correct === 'correct' || q.correct === true).length;
            const wrong = qaHistory.filter(q => q.correct === 'wrong' || q.correct === false).length;
            questionAnsweredData = { correct, wrong };
            console.log(`📝 questionAnswered for "${subject}": ${correct}✓ ${wrong}✗`);
          }
        }
      } catch (qaErr) {
        console.log(`📝 questionAnswered fetch failed for "${subject}":`, qaErr);
      }

      // FIRST: Check student_mastery_history for this specific subject (optimized - fetches only this subject)
      console.log(`📡 API CALL [GET] /api/studentMasteryHistorySubject (for hint level: ${subject})`);
      const historyRes = await apiFetch(
        `${API_BASE_URL}/api/studentMasteryHistorySubject?username=${encodeURIComponent(username)}&subject=${encodeURIComponent(subject)}&limit=500`
      );
      
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        const subjectHistory = historyData.historybysubject || [];
        
        console.log(`📡 API RESPONSE [GET] → ${subjectHistory.length} records for "${subject}"`);
        
        if (subjectHistory.length > 0) {
          // Sum all history entries for this subject
          let totalCorrect = 0;
          let totalWrong = 0;
          subjectHistory.forEach(entry => {
            totalCorrect += entry.correct || 0;
            totalWrong += entry.wrong || 0;
          });
          
          console.log(`📚 History totals for "${subject}": ${totalCorrect}✓ ${totalWrong}✗ → Using HISTORY`);
          
          // Pass history entries to detect flip-flopping pattern, plus questionAnswered data
          const hint = calculateHintLevel(totalCorrect, totalWrong, subjectHistory, questionAnsweredData);
          return { 
            ...hint, 
            source: 'history',
            totalQuestions: totalCorrect + totalWrong
          };
        }
      }

      // SECOND (fallback): Check student_mastery aggregate - ONLY if not found in history
      console.log(`📚 "${subject}" NOT FOUND in history → Falling back to AGGREGATE`);
      console.log(`📡 API CALL [GET] /api/studentMastery (fallback for hint level: ${subject})`);
      const aggRes = await apiFetch(
        `${API_BASE_URL}/api/studentMastery?username=${encodeURIComponent(username)}`
      );
      
      if (aggRes.ok) {
        const aggData = await aggRes.json();
        const aggregates = aggData.history || [];
        
        // Find this subject in aggregates
        const subjectAgg = aggregates.find(a => 
          a.subject && a.subject.toLowerCase() === subject.toLowerCase()
        );
        
        if (subjectAgg) {
          const hint = calculateHintLevel(subjectAgg.correct || 0, subjectAgg.wrong || 0, null, questionAnsweredData);
          return { 
            ...hint, 
            source: 'aggregate',
            totalQuestions: (subjectAgg.correct || 0) + (subjectAgg.wrong || 0)
          };
        }
      }

      // DEFAULT: No data found for this subject
      return { 
        accuracy: 0, 
        hintLevel: 'moderate', 
        label: 'New Topic',
        source: 'default',
        totalQuestions: 0
      };

    } catch (error) {
      console.error('Error fetching adaptive hint level:', error);
      return { 
        accuracy: 0, 
        hintLevel: 'moderate', 
        label: 'Error',
        source: 'error',
        totalQuestions: 0
      };
    }
  };

  // Function to fetch hint levels for multiple subjects
  const fetchHintLevelsForSubjects = async (subjects) => {
    logger.debug('🔎 fetchHintLevelsForSubjects called with:', subjects);
    const hints = {};
    for (const subject of subjects) {
      const hint = await fetchAdaptiveHintLevel(subject);
      // Only store hints that have actual data (not default/empty)
      if (hint && hint.totalQuestions > 0) {
        hints[subject] = hint;
        console.log(`📊 ✅ ADDING "${subject}": ${hint.hintLevel.toUpperCase()} (${hint.accuracy.toFixed(0)}% accuracy, ${hint.totalQuestions} questions from ${hint.source})`);
      } else {
        console.log(`📊 ❌ SKIPPING "${subject}" - no quiz history (source: ${hint?.source}, totalQuestions: ${hint?.totalQuestions})`);
      }
    }
    if (Object.keys(hints).length > 0) {
      logger.debug('🔎 Adding to adaptiveHints state:', Object.keys(hints));
      setAdaptiveHints(prev => ({ ...prev, ...hints }));
    } else {
      logger.debug('🔎 No hints to add - all subjects had no data');
    }
    return hints;
  };

  // Fetch subjects from question_answered table for normalization
  const fetchQuestionAnsweredSubjects = async () => {
    const username = localStorage.getItem('username');
    if (!username) return;
    
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/studentQuestionAnsweredAll?username=${encodeURIComponent(username)}&limit=500`);
      if (res.ok) {
        const data = await res.json();
        const subjects = [...new Set((data.historyquestionanswered || []).map(q => q.subject).filter(Boolean))];
        setQuestionAnsweredSubjects(subjects);
        console.log(`📝 Fetched ${subjects.length} subjects from question_answered:`, subjects);
      }
    } catch (err) {
      console.error('📝 Failed to fetch question_answered subjects:', err);
    }
  };

  // Normalize subject name using LLM matching against student's EXISTING subjects only
  // No predefined list - subjects grow organically based on student's actual history
  // Only matches against subjects already in the student's database
  const normalizeSubjectWithLLM = async (rawSubject, questionContext = null) => {
    if (!rawSubject || rawSubject.trim() === '') return rawSubject;
    
    const trimmedSubject = rawSubject.trim();
    
    // Step 1: Build list of subjects from student's ACTUAL history only (no SEED_SUBJECTS)
    const dbMasterySubjects = Object.keys(masteryAggregate);
    const existingSubjects = [...new Set([...dbMasterySubjects, ...questionAnsweredSubjects])];
    
    // If no existing subjects, just return the new subject as-is (first subject for this student)
    if (existingSubjects.length === 0) {
      console.log(`📝 normalizeSubjectWithLLM: No existing subjects - saving "${trimmedSubject}" as new subject`);
      return trimmedSubject;
    }
    
    console.log(`📝 normalizeSubjectWithLLM: Matching "${trimmedSubject}" against ${existingSubjects.length} existing subjects`);
    if (questionContext) {
      console.log(`📝 normalizeSubjectWithLLM: Using question context for better matching`);
    }
    
    // Step 2: Check for exact match (case-insensitive) - fast path, no LLM call
    const exactMatch = existingSubjects.find(
      s => s.toLowerCase() === trimmedSubject.toLowerCase()
    );
    if (exactMatch) {
      console.log(`📝 normalizeSubjectWithLLM: "${rawSubject}" → exact match → "${exactMatch}"`);
      return exactMatch;
    }
    
    // Step 3: Ask LLM if this new subject is similar to any existing subject
    // If we have question context, use it for better matching
    try {
      const systemPrompt = questionContext 
        ? `You are a subject matcher for a Python learning app. Given a quiz QUESTION, the LLM-assigned SUBJECT, and a list of EXISTING subjects from the student's history, determine the correct subject for this quiz.

Rules:
1. FIRST, analyze the QUESTION content to understand what Python topic is actually being tested.
2. If the question clearly tests one of the EXISTING subjects, return that EXISTING subject name.
3. If the LLM-assigned subject matches an existing subject (e.g., "For Loop" matches "For Loops"), return the EXISTING subject name.
4. Only if the question tests a genuinely NEW topic not in the existing list, return the LLM-assigned subject.
5. Be accurate - match based on what the question is actually testing, not vague categorizations.
6. Return ONLY the subject name, nothing else. No quotes, no explanation.

Examples:
- Question: "What does the print() function do?", LLM Subject: "Python Anatomy", Existing: ["Print Function", "Variables"] → Print Function
- Question: "How do you get user input?", LLM Subject: "Input/Output", Existing: ["Input Function", "Print Function"] → Input Function
- Question: "What is a for loop?", LLM Subject: "Iteration", Existing: ["For Loops", "While Loops"] → For Loops`
        : `You are a subject name matcher for a Python learning app. Given a NEW subject name and a list of EXISTING subjects from the student's history, determine if the new subject is the same topic as an existing one.

Rules:
1. If the new subject is clearly the same topic as an existing subject (e.g., "For Loop" matches "For Loops"), return the EXISTING subject name.
2. If the new subject is a genuinely different topic, return the NEW subject exactly as provided.
3. Be conservative - only match if they're truly the same concept, not just related.
4. Return ONLY the subject name, nothing else. No quotes, no explanation.

Examples:
- New: "For Loop", Existing: ["For Loops", "While Loops"] → For Loops (same topic)
- New: "Looping", Existing: ["For Loops", "While Loops"] → Looping (too general, keep as new)
- New: "List Comprehensions", Existing: ["Lists", "For Loops"] → List Comprehensions (different topic)
- New: "print function", Existing: ["Print Statements"] → Print Statements (same topic)`;

      const userPrompt = questionContext
        ? `Quiz Question: "${questionContext}"

LLM-assigned Subject: "${trimmedSubject}"

Existing subjects in student's history: ${JSON.stringify(existingSubjects)}

Based on what the QUESTION is actually testing, return the correct subject name (either an existing match or the LLM-assigned subject if it's genuinely new).`
        : `New subject: "${trimmedSubject}"

Existing subjects in student's history: ${JSON.stringify(existingSubjects)}

Return the subject name to use (either an existing match or the new subject as-is).`;

      console.log('`🤖 LLM API: POST ${API_BASE_URL}/api/chat (normalizeSubject)');
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          max_tokens: 50,
          temperature: 0
        })
      });
      
      if (!response.ok) {
        console.error('📝 normalizeSubjectWithLLM: LLM API error', response.status);
        return trimmedSubject;
      }
      
      const data = await response.json();
      const matchedSubject = data.choices[0]?.message?.content?.trim();
      
      if (!matchedSubject) {
        console.log(`📝 normalizeSubjectWithLLM: LLM returned empty, using original "${trimmedSubject}"`);
        return trimmedSubject;
      }
      
      // Check if LLM returned an existing subject or the new one
      const validMatch = existingSubjects.find(
        s => s.toLowerCase() === matchedSubject.toLowerCase()
      );
      
      if (validMatch) {
        console.log(`📝 normalizeSubjectWithLLM: "${rawSubject}" → matched existing → "${validMatch}"`);
        return validMatch;
      } else {
        // LLM decided this is a new topic - use the LLM's returned name (might be cleaned up)
        console.log(`📝 normalizeSubjectWithLLM: "${rawSubject}" → new topic → "${matchedSubject}"`);
        return matchedSubject;
      }
      
    } catch (err) {
      console.error('📝 normalizeSubjectWithLLM error:', err);
      return trimmedSubject;
    }
  };

  // FALLBACK: Ensure plan tags exist in tutor response
  // If [PLAN_START] is missing, ask LLM if a step was accepted and add tags if needed
  const ensurePlanTags = async (tutorResponse, studentMessage, existingPlan) => {
    // Skip if tags already exist
    if (tutorResponse.includes('[PLAN_START]')) {
      return tutorResponse;
    }
    
    // Skip for very short responses or non-breakdown mode
    if (tutorResponse.length < 30 || mode !== 'breakdown') {
      return tutorResponse;
    }
    
    console.log('📋 Plan tags missing - checking if step was accepted or completed...');
    console.log('📋 Existing plan:', existingPlan);
    console.log('📋 Student message:', studentMessage.substring(0, 100));
    console.log('📋 Tutor response:', tutorResponse.substring(0, 200));
    
    try {
      const existingSteps = existingPlan?.map((s, i) => `${i + 1}. ${s.text}`).join('\n') || 'None yet';
      
      console.log('`🤖 LLM API: POST ${API_BASE_URL}/api/chat (ensurePlanTags)');
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          temperature: 0,
          messages: [{
            role: 'user',
            content: `Student said: "${studentMessage}"

Tutor responded: "${tutorResponse.substring(0, 800)}"

Existing plan steps:
${existingSteps}

Analyze the conversation and determine:
1. Did the student PROPOSE a step (like "get the width", "calculate area", etc.) AND the tutor acknowledged it? 
2. Did the student SUBMIT CODE and the tutor confirmed it's correct?
3. What step is currently being worked on?

Return JSON only:
{
  "stepProposed": false,
  "codeSubmitted": false,
  "codeCorrect": false,
  "currentStepDescription": "",
  "submittedCode": "",
  "allSteps": []
}

IMPORTANT RULES:
- If student proposed a step like "get the width" and tutor said "Good choice!": stepProposed=true, currentStepDescription="Get the width", allSteps=["Get the width"]
- If student submitted code and tutor said it's correct: codeSubmitted=true, codeCorrect=true, submittedCode=the code
- allSteps should include ALL steps mentioned so far (existing + any new one)
- If no existing steps and student proposed one, allSteps should have that one step
- Be sure to extract the step even if it's the first one!`
          }]
        })
      });
      
      if (!response.ok) {
        console.log('📋 Fallback API call failed, returning original response');
        return tutorResponse;
      }
      
      const data = await response.json();
      const content = data.choices[0]?.message?.content?.trim();
      console.log('📋 Raw fallback response:', content);
      const result = JSON.parse(content.replace(/```json\s*|\s*```/g, ''));
      
      console.log('📋 Fallback result:', JSON.stringify(result, null, 2));
      
      let modifiedResponse = tutorResponse;
      
      // Inject plan tags if we detected a step proposal OR code completion
      if ((result.stepProposed || result.codeCorrect) && result.allSteps?.length > 0) {
        const planBlock = '\n\n[PLAN_START]\n' + 
          result.allSteps.map((s, i) => `${i + 1}. ${s}`).join('\n') +
          '\n[PLAN_END]';
        console.log('📋 Injecting plan tags:', planBlock);
        modifiedResponse += planBlock;
      } else if (result.stepProposed && result.currentStepDescription) {
        // Fallback: even if allSteps is empty, use currentStepDescription
        const planBlock = `\n\n[PLAN_START]\n1. ${result.currentStepDescription}\n[PLAN_END]`;
        console.log('📋 Injecting single step plan:', planBlock);
        modifiedResponse += planBlock;
      }
      
      // Also inject STEP_COMPLETE and CODE tags if code was completed correctly
      if (result.codeCorrect && result.submittedCode && result.currentStepDescription) {
        const codeBlock = `\n\n[STEP_COMPLETE] ${result.currentStepDescription}\n[CODE_START]\n${result.submittedCode}\n[CODE_END]`;
        console.log('📋 Injecting step complete tags:', codeBlock);
        modifiedResponse += codeBlock;
      }
      
      return modifiedResponse;
    } catch (err) {
      console.error('📋 ensurePlanTags error:', err);
      return tutorResponse;
    }
  };

  // Extract informal result from LLM response using a quick, cheap LLM call
  // This is a fallback when the main LLM doesn't include [INFORMAL_RESULT] tags
  const extractInformalResult = async (userMessage, assistantResponse) => {
    if (!userMessage || !assistantResponse) return;
    
    // Skip if the response is just a question or teaching content (no evaluation)
    if (assistantResponse.length < 50) return;
    
    logger.debug('🔍 extractInformalResult: Analyzing for student answer evaluation...');
    
    try {
      console.log('`🤖 LLM API: POST ${API_BASE_URL}/api/chat (extractInformalResult)');
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `You analyze Python tutor conversations to detect if the tutor is evaluating a student's answer.

OUTPUT FORMAT - return JSON only:
{"evaluated": false} - if the tutor is NOT evaluating an answer (just teaching, asking questions, or having a general conversation)
{"evaluated": true, "correct": true, "subject": "Topic Name"} - if tutor confirmed student's answer is CORRECT
{"evaluated": true, "correct": false, "subject": "Topic Name"} - if tutor indicated student's answer is WRONG

DETECTION RULES:
- CORRECT signals: "Great job!", "Exactly right", "Perfect!", "That's correct", "You got it", "Well done", "Spot on", "Correct!", "Yes!", "Nice work"
- WRONG signals: "Actually...", "Not quite", "That's not right", "Almost", "Let me correct", "The correct answer is", "Remember that...", "That would cause an error"
- SUBJECT: Identify the Python topic being discussed (e.g., "For Loops", "Lists", "Functions", "Variables", etc.)

Return ONLY valid JSON. No markdown, no explanation.`
            },
            {
              role: 'user',
              content: `Student message: "${userMessage.substring(0, 500)}"

Tutor response: "${assistantResponse.substring(0, 800)}"

Analyze: Is the tutor evaluating the student's answer?`
            }
          ],
          max_tokens: 100,
          temperature: 0
        })
      });
      
      if (!response.ok) {
        console.error('🔍 extractInformalResult: API error', response.status);
        return;
      }
      
      const data = await response.json();
      const content = data.choices[0]?.message?.content?.trim();
      
      logger.debug('🔍 extractInformalResult: LLM analysis result:', content);
      
      // Parse JSON response
      try {
        const result = JSON.parse(content);
        
        if (result.evaluated && result.subject) {
          const normalizedSubject = await normalizeSubjectWithLLM(result.subject);
          const resultValue = result.correct ? 'correct' : 'wrong';
          
          console.log(`🔍 extractInformalResult: DETECTED! Subject="${normalizedSubject}", Result="${resultValue}", Mode="${mode}"`);
          
          // Save to questionAnswered table (informal Q&A during tutoring)
          // This is different from formal quizzes which go to mastery/masteryHistory
          const username = localStorage.getItem('username');
          if (username) {
            try {
              const saveRes = await apiFetch(`${API_BASE_URL}/api/studentQuestionAnswered`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  username,
                  subject: normalizedSubject,
                  result: resultValue
                })
              });
              console.log(`🔍 extractInformalResult: Saved to questionAnswered - ${saveRes.ok ? 'SUCCESS' : 'FAILED'}`);
              
              // Award points only for correct answers
              if (resultValue === 'correct') {
                apiFetch(`${API_BASE_URL}/api/points/award`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ username, actionType: 'ANSWER', subject: normalizedSubject })
                }).then(r => r.ok ? r.json() : null)
                  .then(d => { if (d) console.log(`🏆 Answer points awarded: +${d.pointsAwarded} (total: ${d.total_points})`); })
                  .catch(e => console.warn('🏆 Answer points error:', e));
              }
            } catch (saveErr) {
              console.error('🔍 extractInformalResult: Save error:', saveErr);
            }
          }
        } else {
          logger.debug('🔍 extractInformalResult: No evaluation detected in this exchange');
        }
      } catch (parseErr) {
        console.error('🔍 extractInformalResult: JSON parse error:', parseErr, 'Content:', content);
      }
      
    } catch (err) {
      console.error('🔍 extractInformalResult error:', err);
    }
  };

  // Save soft review (engagement without quiz) to track topic interaction
  const saveSoftReview = async (subject) => {
    const username = localStorage.getItem('username');
    if (!username || !subject) {
      console.log(`📝 saveSoftReview() - SKIPPED: missing username (${username}) or subject (${subject})`);
      return;
    }
    
    // Check locally first - skip API if already saved today
    const key = subject.toLowerCase();
    if (softReviewsSavedToday.has(key)) {
      console.log(`📝 saveSoftReview() - "${subject}" already saved today - skipping API call`);
      return;
    }
    
    try {
      // Mark as saved locally FIRST (prevents race conditions)
      setSoftReviewsSavedToday(prev => new Set(prev).add(key));
      
      // POST to backend (non-blocking)
      console.log(`📝 saveSoftReview() - POSTing to API for "${subject}"...`);
      const res = await apiFetch(`${API_BASE_URL}/api/studentSoftReviewHistory`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, subject })
      });
      
      if (res.ok) {
        console.log(`📝 saveSoftReview() - ✅ API SUCCESS for "${subject}"`);
        // Award review points (always, no correct/wrong for reviews)
        apiFetch(`${API_BASE_URL}/api/points/award`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, actionType: 'REVIEW', subject })
        }).then(r => r.ok ? r.json() : null)
          .then(d => { if (d) console.log(`🏆 Review points awarded: +${d.pointsAwarded} (total: ${d.total_points})`); })
          .catch(e => console.warn('🏆 Review points error:', e));
      } else {
        console.log(`📝 saveSoftReview() - ⚠️ API returned ${res.status} for "${subject}"`);
      }
    } catch (err) {
      console.error('📝 saveSoftReview() - ❌ API ERROR:', err);
    }
  };

  // NEW: LLM-based subject matching with optimized API flow
  // Step 1: Fetch aggregates from /api/studentMastery (few records)
  // Step 2: Extract unique subjects and ask LLM to match
  // Fallback: classify topic from user message when no history match is found
  const classifyTopicFallback = async (userMessage) => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'user', content: `You are a Python course assistant. Analyze the student message below and respond with ONLY valid JSON in this exact format:\n{"isPythonQuestion": true/false, "subject": "topic name or null"}\n\n- "isPythonQuestion": true if the student is asking about Python programming (even implicitly, e.g. asking for help, explaining code, asking about errors). false if it is a greeting, filler, or unrelated message.\n- "subject": if isPythonQuestion is true, provide 1-3 words for the Python topic (e.g. "loops", "map function", "file I/O", "lists"). If isPythonQuestion is false, set to null.\n\nStudent message: "${userMessage}"` }
          ],
          max_tokens: 30,
          temperature: 0
        })
      });
      if (res.ok) {
        const data = await res.json();
        const raw = data.choices?.[0]?.message?.content?.trim() || '{}';
        // Strip markdown code fences if GPT wraps the JSON
        const cleaned = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '').trim();
        let parsed = {};
        try { parsed = JSON.parse(cleaned); } catch (e) {
          console.log('🤖 Fallback: could not parse JSON response:', raw);
          return null;
        }
        const topic = parsed.isPythonQuestion === true ? (parsed.subject || null) : null;
        console.log('🤖 Fallback classification:', parsed, '→ topic:', topic);
        return topic;
      }
    } catch (err) {
      console.log('🤖 Fallback classification failed:', err);
    }
    return null;
  };

  // Step 3: If match found, fetch history for that subject only via /api/studentMasteryHistorySubject
  // Step 4: Calculate mastery from filtered history
  // Step 5: If no match, default to Foundational
  const fetchMasteryWithLLMMatching = async (userMessage) => {
    const username = localStorage.getItem('username');
    if (!username) {
      logger.debug('🔍 LLM Matching: No username found, defaulting to foundational');
      return { hintLevel: 'foundational', accuracy: 0, source: 'no-user', matchedSubject: null, isQuizRequest: false };
    }

    logger.debug('🔍 ========== LLM-BASED SUBJECT MATCHING (OPTIMIZED) ==========');
    logger.debug('🔍 User Message:', userMessage);

    try {
      // STEP 1: Fetch aggregate mastery data (few records, fast)
      console.log('📡 STEP 1: Fetching aggregate mastery from /api/studentMastery');
      const masteryRes = await apiFetch(
        `${API_BASE_URL}/api/studentMastery?username=${encodeURIComponent(username)}`
      );

      if (!masteryRes.ok) {
        console.log('📡 Mastery API failed, defaulting to foundational');
        const fallbackSubject = await classifyTopicFallback(userMessage);
        return { hintLevel: 'foundational', accuracy: 0, source: 'api-error', matchedSubject: fallbackSubject, isQuizRequest: false };
      }

      const masteryData = await masteryRes.json();
      const aggregates = masteryData.history || [];
      console.log(`📡 Retrieved ${aggregates.length} aggregate records`);

      // STEP 2: Extract unique subjects from BOTH mastery AND questionAnswered tables
      const masterySubjects = aggregates.map(a => a.subject).filter(Boolean);
      const allSubjects = [...new Set([...masterySubjects, ...questionAnsweredSubjects])];
      logger.debug('📚 STEP 2: Subjects from mastery:', masterySubjects.length, ', from questionAnswered:', questionAnsweredSubjects.length);
      logger.debug('📚 Combined unique subjects:', allSubjects);

      if (allSubjects.length === 0) {
        logger.debug('📚 No subjects found in mastery or questionAnswered, defaulting to foundational');
        const fallbackSubject = await classifyTopicFallback(userMessage);
        return { hintLevel: 'foundational', accuracy: 0, source: 'no-subjects', matchedSubject: fallbackSubject, isQuizRequest: false };
      }

      // Use combined subjects for LLM matching
      const uniqueSubjects = allSubjects;

      // STEP 3: Ask LLM to match the user's question to a subject
      console.log('🤖 STEP 3: Asking LLM to match subject...');
      const llmResult = await askLLMToMatchSubject(userMessage, uniqueSubjects);
      const matchedSubject = llmResult.subject;
      const isQuizRequest = llmResult.isQuizRequest;
      console.log('🤖 LLM matched subject:', matchedSubject, ', isQuizRequest:', isQuizRequest);

      if (!matchedSubject || matchedSubject === 'NO_MATCH') {
        console.log('🤖 No match found, attempting fallback topic classification...');
        const fallbackSubject = await classifyTopicFallback(userMessage);
        return { hintLevel: 'foundational', accuracy: 0, source: 'no-match', matchedSubject: fallbackSubject, isQuizRequest };
      }

      // STEP 4: Fetch history for the matched subject only (targeted query)
      const historyUrl = `${API_BASE_URL}/api/studentMasteryHistorySubject?username=${encodeURIComponent(username)}&subject=${encodeURIComponent(matchedSubject)}&limit=500`;
      console.log(`📡 STEP 4: Fetching history for "${matchedSubject}"`);
      console.log(`📡 Full URL: ${historyUrl}`);
      const historyRes = await apiFetch(historyUrl);

      let subjectHistory = [];
      if (historyRes.ok) {
        const historyData = await historyRes.json();
        console.log(`📡 Raw API response:`, historyData);
        console.log(`📡 Response keys:`, Object.keys(historyData));
        // Handle different response formats: historybysubject, history, data, or direct array
        subjectHistory = historyData.historybysubject || historyData.history || historyData.data || [];
        // If response is an array directly, use it
        if (Array.isArray(historyData)) {
          subjectHistory = historyData;
        }
        console.log(`📡 Retrieved ${subjectHistory.length} history records for "${matchedSubject}"`);
      } else {
        console.log(`📡 History API failed with status ${historyRes.status} for "${matchedSubject}"`);
      }

      // STEP 4.5: Fetch questionAnswered for the matched subject (supplementary data)
      let questionAnsweredData = null;
      try {
        const qaUrl = `${API_BASE_URL}/api/studentQuestionAnswered?username=${encodeURIComponent(username)}&subject=${encodeURIComponent(matchedSubject)}&limit=500`;
        console.log(`📝 STEP 4.5: Fetching questionAnswered for "${matchedSubject}"`);
        const qaRes = await apiFetch(qaUrl);
        if (qaRes.ok) {
          const qaData = await qaRes.json();
          const qaHistory = qaData.historyquestionanswered || [];
          if (qaHistory.length > 0) {
            // Handle string values "correct"/"wrong" from API (not boolean)
            const correct = qaHistory.filter(q => q.correct === 'correct' || q.correct === true).length;
            const wrong = qaHistory.filter(q => q.correct === 'wrong' || q.correct === false).length;
            questionAnsweredData = { correct, wrong };
            console.log(`📝 questionAnswered for "${matchedSubject}": ${correct}✓ ${wrong}✗`);
          }
        }
      } catch (qaErr) {
        console.log(`📝 questionAnswered fetch failed for "${matchedSubject}":`, qaErr);
      }

      // STEP 5: Calculate mastery
      // If we have history, use it for trend/flip-flop detection
      // Otherwise, use aggregate data
      let totalCorrect = 0;
      let totalWrong = 0;
      let masterySource = 'none';

      if (subjectHistory.length > 0) {
        // Sum from history
        subjectHistory.forEach(entry => {
          totalCorrect += entry.correct || 0;
          totalWrong += entry.wrong || 0;
        });
        masterySource = 'masteryHistory';
        console.log(`📊 STEP 5: Totals from masteryHistory: ${totalCorrect} correct, ${totalWrong} wrong`);
      } else {
        // Use aggregate data
        const subjectAgg = aggregates.find(a => 
          a.subject && a.subject.toLowerCase() === matchedSubject.toLowerCase()
        );
        if (subjectAgg) {
          totalCorrect = subjectAgg.correct || 0;
          totalWrong = subjectAgg.wrong || 0;
          masterySource = 'mastery (aggregate)';
          console.log(`📊 STEP 5: Totals from mastery (aggregate): ${totalCorrect} correct, ${totalWrong} wrong`);
        }
      }

      // Calculate hint level using existing logic (accuracy, trend, flip-flopping)
      // Pass history for pattern detection if available, plus questionAnswered data
      const hint = calculateHintLevel(totalCorrect, totalWrong, subjectHistory.length > 0 ? subjectHistory : null, questionAnsweredData);
      console.log(`📊 Calculated hint level: ${hint.hintLevel.toUpperCase()} (${hint.accuracy.toFixed(0)}% accuracy)`);

      // Calculate total questions including questionAnswered data
      const qaQuestions = questionAnsweredData ? (questionAnsweredData.correct + questionAnsweredData.wrong) : 0;
      const totalQuestionsIncludingQA = totalCorrect + totalWrong + qaQuestions;
      console.log(`📊 Total evidence: ${totalCorrect + totalWrong} from ${masterySource} + ${qaQuestions} from questionAnswered = ${totalQuestionsIncludingQA} total`);

      // Extract last 20 questions for quiz uniqueness (sorted by date, most recent first)
      const previousQuestions = subjectHistory
        .sort((a, b) => {
          const dateA = new Date(a.recorded_at || a.timestamp || a.date || a.last_updated || 0);
          const dateB = new Date(b.recorded_at || b.timestamp || b.date || b.last_updated || 0);
          return dateB - dateA;
        })
        .slice(0, 20)
        .map(record => record.question)
        .filter(q => q && q.trim().length > 0);
      console.log(`📝 Extracted ${previousQuestions.length} previous questions for quiz uniqueness`);

      // Update adaptiveHints state with the matched subject
      setAdaptiveHints(prev => ({
        ...prev,
        [matchedSubject]: {
          ...hint,
          source: subjectHistory.length > 0 ? 'history-llm-match' : 'aggregate-llm-match',
          totalQuestions: totalQuestionsIncludingQA
        }
      }));

      logger.debug('🔍 ========== END LLM MATCHING (SUCCESS) ==========');
      return {
        ...hint,
        source: subjectHistory.length > 0 ? 'history-llm-match' : 'aggregate-llm-match',
        matchedSubject,
        isQuizRequest,
        totalQuestions: totalQuestionsIncludingQA,
        previousQuestions
      };

    } catch (error) {
      console.error('🔍 Error in LLM matching:', error);
      return { hintLevel: 'foundational', accuracy: 0, source: 'error', matchedSubject: null, isQuizRequest: false };
    }
  };

  // Ask LLM to match user's question to one of the available subjects
  // Also detects if the message is a quiz/test request
  const askLLMToMatchSubject = async (userMessage, availableSubjects) => {
    console.log('🤖 Asking LLM to match subject...');
    console.log('🤖 Available subjects:', availableSubjects);

    const matchPrompt = `You are a subject matching assistant. Given a user's question and a list of available subjects:
1. Determine which subject best matches the question
2. Determine if the user is explicitly requesting a QUIZ or TEST with questions to answer

User's question: "${userMessage}"

Available subjects:
${availableSubjects.map((s, i) => `${i + 1}. ${s}`).join('\n')}

Instructions:
- For "subject": If the question relates to one of the subjects, use the exact subject name. If no match, use "NO_MATCH".
- For "isQuizRequest": Set to true ONLY if the user explicitly wants:
  * A quiz (e.g., "quiz me", "give me a quiz")
  * A test (e.g., "test me on...")
  * Multiple choice questions (e.g., "give me questions about...")
  
  Set to FALSE for:
  * Learning requests (e.g., "I want to practice X", "explain X", "help me understand X")
  * General discussion about a topic
  * Asking for help or explanations

Respond with ONLY valid JSON in this exact format (no markdown, no explanation):
{"subject": "<subject name or NO_MATCH>", "isQuizRequest": <true or false>}

Your response:`;

    try {
      console.log('`🤖 LLM API: POST ${API_BASE_URL}/api/chat (matchSubject)');
      const res = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini', // Use faster/cheaper model for matching
          messages: [{ role: 'user', content: matchPrompt }],
          temperature: 0.1, // Low temperature for consistent matching
          max_tokens: 50,
        }),
      });

      const data = await res.json();

      if (res.ok && data.choices?.[0]?.message?.content) {
        const response = data.choices[0].message.content.trim();
        logger.debug('🤖 LLM raw response:', response);

        // Parse JSON response
        try {
          const parsed = JSON.parse(response);
          const subjectFromLLM = parsed.subject || 'NO_MATCH';
          const isQuizRequest = parsed.isQuizRequest === true;
          
          console.log('🤖 Parsed - Subject:', subjectFromLLM, ', isQuizRequest:', isQuizRequest);

          // Check if response matches any of the available subjects (case-insensitive)
          const matchedSubject = availableSubjects.find(s => 
            s.toLowerCase() === subjectFromLLM.toLowerCase()
          );

          if (matchedSubject) {
            return { subject: matchedSubject, isQuizRequest };
          } else if (subjectFromLLM.toUpperCase() === 'NO_MATCH') {
            return { subject: 'NO_MATCH', isQuizRequest };
          } else {
            // LLM gave unexpected response, try partial match
            const partialMatch = availableSubjects.find(s => 
              s.toLowerCase().includes(subjectFromLLM.toLowerCase()) ||
              subjectFromLLM.toLowerCase().includes(s.toLowerCase())
            );
            return { subject: partialMatch || 'NO_MATCH', isQuizRequest };
          }
        } catch (parseError) {
          // Fallback: treat response as just subject name (backwards compatibility)
          console.log('🤖 JSON parse failed, falling back to simple match');
          const matchedSubject = availableSubjects.find(s => 
            s.toLowerCase() === response.toLowerCase()
          );
          return { subject: matchedSubject || 'NO_MATCH', isQuizRequest: false };
        }
      }

      return { subject: 'NO_MATCH', isQuizRequest: false };
    } catch (error) {
      console.error('🤖 LLM matching error:', error);
      return { subject: 'NO_MATCH', isQuizRequest: false };
    }
  };

  // Generate adaptive hint guidance text for system prompt injection
  // If relevantTopics is provided, only include those topics; otherwise include all
  // If freshMasteryResult is provided, use it instead of stale state data
  const generateAdaptiveHintGuidance = (relevantTopics = null, freshMasteryResult = null) => {
    // Start with state data, but override with fresh result if provided
    let hintsData = { ...adaptiveHints };
    
    // If we have fresh mastery result, use it to override stale state
    if (freshMasteryResult && freshMasteryResult.matchedSubject) {
      hintsData[freshMasteryResult.matchedSubject] = {
        accuracy: freshMasteryResult.accuracy,
        hintLevel: freshMasteryResult.hintLevel,
        label: freshMasteryResult.label,
        isFlipFlopping: freshMasteryResult.isFlipFlopping,
        trend: freshMasteryResult.trend,
        totalQuestions: freshMasteryResult.totalQuestions,
        source: freshMasteryResult.source,
        earlyAccuracy: freshMasteryResult.earlyAccuracy,
        recentAccuracy: freshMasteryResult.recentAccuracy
      };
    }
    
    let hints = Object.entries(hintsData);
    
    logger.debug('🔍 generateAdaptiveHintGuidance called with relevantTopics:', relevantTopics);
    logger.debug('🔍 Current adaptiveHints state:', Object.keys(adaptiveHints));
    if (freshMasteryResult) {
      logger.debug('🔍 Using fresh mastery result for:', freshMasteryResult.matchedSubject, `(${freshMasteryResult.accuracy}% accuracy, ${freshMasteryResult.totalQuestions} questions)`);
    }
    
    // Filter to only relevant topics if provided
    // Use partial/contains matching to handle variations like "Programming Errors" vs "programming error"
    if (relevantTopics && relevantTopics.length > 0) {
      hints = hints.filter(([subject]) => 
        relevantTopics.some(topic => {
          const topicLower = topic.toLowerCase();
          const subjectLower = subject.toLowerCase();
          // Match if: exact match, topic contains subject, or subject contains topic
          return topicLower === subjectLower || 
                 topicLower.includes(subjectLower) || 
                 subjectLower.includes(topicLower);
        })
      );
      logger.debug('🔍 After filtering to relevant topics:', hints.map(([s]) => s));
    }
    
    // Filter out "New Topic" entries with 0 questions - no point telling LLM about topics with no history
    hints = hints.filter(([subject, data]) => 
      data.totalQuestions > 0 || data.source !== 'default'
    );
    
    logger.debug('🔍 After filtering out empty topics:', hints.map(([s]) => s));
    
    if (hints.length === 0) {
      logger.debug('🔍 No hints to include - returning empty string');
      return '';
    }

    const hintDescriptions = {
      'minimal': 'Be concise, ask probing questions, let them discover the answer independently.',
      'moderate': 'Guide with partial explanations, check understanding, provide hints but not answers.',
      'detailed': 'Break into small steps, show examples, verify understanding at each step, provide more scaffolding.'
    };

    const trendIcons = {
      'declining': '📉',
      'slipping': '📉',
      'improving': '📈',
      'progressing': '📈',
      'stable': ''
    };

    let guidance = '\n\n[ADAPTIVE HINT LEVELS - Based on Student History for Current Topic]\n';
    guidance += 'Adjust your teaching intensity based on the student\'s demonstrated performance:\n';
    
    hints.forEach(([subject, data]) => {
      let statusNote = '';
      const hasValidTrendData = data.earlyAccuracy != null && data.recentAccuracy != null;
      
      if (data.isFlipFlopping) {
        statusNote = ' ⚠️ FLIP-FLOPPING - inconsistent answers';
      } else if ((data.trend === 'declining' || data.trend === 'slipping') && hasValidTrendData) {
        statusNote = ` 📉 ${data.trend.toUpperCase()} (${data.earlyAccuracy.toFixed(0)}% → ${data.recentAccuracy.toFixed(0)}%)`;
      } else if ((data.trend === 'improving' || data.trend === 'progressing') && hasValidTrendData) {
        statusNote = ` 📈 ${data.trend.toUpperCase()} (${data.earlyAccuracy.toFixed(0)}% → ${data.recentAccuracy.toFixed(0)}%)`;
      }
      
      guidance += `- ${subject}: ${data.label} (${data.accuracy.toFixed(0)}% accuracy, ${data.totalQuestions} questions from ${data.source})${statusNote}\n`;
      guidance += `  → ${data.hintLevel.toUpperCase()} hints: ${hintDescriptions[data.hintLevel]}\n`;
      
      // Add quiz difficulty guidance based on hint level
      if (data.hintLevel === 'minimal') {
        guidance += `  → QUIZ DIFFICULTY: Ask challenging questions - edge cases, tricky scenarios, "what's wrong with this code?" type questions.\n`;
      } else if (data.hintLevel === 'moderate') {
        guidance += `  → QUIZ DIFFICULTY: Ask standard difficulty questions - straightforward but require understanding.\n`;
      } else if (data.hintLevel === 'detailed') {
        guidance += `  → QUIZ DIFFICULTY: Ask simpler foundational questions first - basic syntax, simple concepts, build confidence.\n`;
      }
    });

    // Add special guidance for flip-flopping students
    const hasFlipFlopping = hints.some(([_, data]) => data.isFlipFlopping);
    if (hasFlipFlopping) {
      guidance += '\n⚠️ FLIP-FLOPPING ALERT: This student alternates between correct and wrong answers, indicating shaky foundational understanding.\n';
      guidance += 'For flip-flopping subjects:\n';
      guidance += '- Ask them to EXPLAIN their reasoning before confirming answers\n';
      guidance += '- Revisit underlying concepts, not just the current question\n';
      guidance += '- Use more concrete examples and analogies\n';
      guidance += '- Check if they truly understand or just guessed correctly\n';
    }

    // Add special guidance for declining students
    const hasDeclining = hints.some(([_, data]) => data.trend === 'declining' || data.trend === 'slipping');
    if (hasDeclining) {
      guidance += '\n📉 DECLINING TREND ALERT: This student started well but recent answers are mostly wrong.\n';
      guidance += 'For declining subjects:\n';
      guidance += '- The student may have hit a conceptual wall or gotten confused\n';
      guidance += '- Ask what changed or where they got stuck\n';
      guidance += '- Review the concepts from where they were succeeding\n';
      guidance += '- Build a bridge between what they understood and what is confusing them now\n';
      guidance += '- Don\'t assume prior knowledge still holds - verify it\n';
    }

    guidance += '\nIMPORTANT: For subjects with DETAILED hint level, provide more step-by-step guidance. ';
    guidance += 'For MINIMAL hint level, challenge them more and let them work through difficulties.\n';

    // Add quiz-specific summary
    guidance += '\n🎯 QUIZ DIFFICULTY ADJUSTMENT:\n';
    guidance += '- MINIMAL (high mastery): Challenge with edge cases, tricky syntax, debugging questions\n';
    guidance += '- MODERATE (learning): Standard difficulty, clear questions that test understanding\n';
    guidance += '- DETAILED (struggling): Start with basics, build confidence with simpler questions\n';

    return guidance;
  };

  const combinedProgress = useMemo(() => {
    // USE masteryHistory AS SINGLE SOURCE OF TRUTH
    // Sum up correct/wrong from all history entries per subject
    
    // Helper to normalize subject names (title case)
    const normalizeSubject = (subject) => {
      if (!subject) return '';
      return subject.toLowerCase().split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };
    
    const merged = {};
    
    // Sum up all masteryHistory entries by normalized subject
    (masteryHistory || []).forEach(entry => {
      const normalized = normalizeSubject(entry.subject);
      if (!merged[normalized]) {
        merged[normalized] = { correct: 0, wrong: 0 };
      }
      merged[normalized].correct += normalizeCount(entry.correct);
      merged[normalized].wrong += normalizeCount(entry.wrong);
    });
    
    logger.debug('📊 combinedProgress: Using masteryHistory only', merged);
    return merged;
  }, [masteryHistory]);

  // ==================== Handle Stripe redirect URL params ====================
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subscriptionParam = params.get('subscription');
    const sessionId = params.get('session_id');

    // Handle both URL formats:
    // /?subscription=success&session_id=xxx  (correct format)
    // /?subscription-success&session_id=xxx  (current backend format)
    const isSuccess = subscriptionParam === 'success' || params.has('subscription-success');

    if (isSuccess && sessionId) {
      logger.info('🔔 Stripe redirect detected — verifying payment, session_id:', sessionId);

      apiFetch(`${API_BASE_URL}/api/subscription/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      })
        .then(res => res.json())
        .then(data => {
          if (data.status === 'active' || data.subscription_status === 'active') {
            logger.info('✅ Subscription verified as active');
            localStorage.setItem('subscriptionStatus', 'active');
            localStorage.setItem('canAccess', 'true');
            setSubscriptionStatus('active');
            setCanAccess(true);
            setTrialDaysLeft(-1);
          } else {
            logger.warn('⚠️ Payment status:', data.status);
          }
        })
        .catch(err => {
          logger.error('Error verifying subscription after Stripe redirect:', err);
        });

      // Clean URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (subscriptionParam === 'canceled' || params.has('subscription-canceled')) {
      logger.info('❌ Stripe payment was canceled');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  // =========================================================================

  // ==================== Periodic Subscription Check (every 2 hours) ====================
  useEffect(() => {
    const subscriptionRequired = localStorage.getItem('subscriptionRequired');
    // Only run for subscription-required users who currently have access
    if (subscriptionRequired !== 'true') return;

    // Skip if already paid — one-time payment never expires, no need to keep checking
    const currentStatus = localStorage.getItem('subscriptionStatus');
    if (currentStatus === 'active') {
      logger.info('🔄 Subscription is active (paid) — skipping periodic checks');
      return;
    }

    let intervalId = null;

    const checkSubscription = async () => {
      const currentUsername = localStorage.getItem('username');
      if (!currentUsername) return;

      try {
        const res = await apiFetch(`${API_BASE_URL}/api/subscription/status?username=${encodeURIComponent(currentUsername)}`);
        if (!res.ok) return;
        const data = await res.json();
        logger.info('🔄 Periodic subscription check:', data.subscription_status, 'can_access:', data.can_access);

        if (data.subscription_status === 'active') {
          // User has paid — stop checking
          logger.info('✅ Subscription now active — stopping periodic checks');
          localStorage.setItem('subscriptionStatus', 'active');
          localStorage.setItem('canAccess', 'true');
          setSubscriptionStatus('active');
          setCanAccess(true);
          setTrialDaysLeft(-1);
          if (intervalId) clearInterval(intervalId);
          return;
        }

        if (data.can_access === false) {
          // Trial expired or subscription inactive — update state to show paywall
          localStorage.setItem('subscriptionStatus', data.subscription_status || 'trial_expired');
          localStorage.setItem('canAccess', 'false');
          localStorage.setItem('trialDaysLeft', '-1');
          setSubscriptionStatus(data.subscription_status || 'trial_expired');
          setCanAccess(false);
          setTrialDaysLeft(-1);
        } else {
          // Still valid — update trial days if applicable
          if (data.trial_days_left !== undefined && data.trial_days_left !== null) {
            localStorage.setItem('trialDaysLeft', data.trial_days_left.toString());
            setTrialDaysLeft(data.trial_days_left);
          }
        }
      } catch (err) {
        logger.error('Periodic subscription check failed (will retry):', err);
        // Don't block the user on network errors — just retry next interval
      }
    };

    intervalId = setInterval(checkSubscription, 3 * 60 * 60 * 1000); // every 3 hours

    return () => { if (intervalId) clearInterval(intervalId); };
  }, []);
  // =====================================================================================

  // Reset analytics recommendations when Full Analytics panel opens (show button fresh each time)
  useEffect(() => {
    if (showDebug) {
      setAnalyticsRecommendations([]);
      setAnalyticsRecsLoading(false);
    }
  }, [showDebug]);

  // Effect to load curriculum based on date or manual selection
  // Depends on isLoggedIn to reload after login when class_section/university are set
  useEffect(() => {
    // Only load curriculum when logged in
    if (!isLoggedIn) return;
    
    const loadCurriculum = async () => {
      setIsCurriculumLoading(true);
      try {
        if (selectedSyllabusDate === 'none') {
          setCurriculumContent('');
          setIsCurriculumLoading(false);
          return;
        }

        // Always reload the mapping after login to get correct folder
        const response = await fetch(getCurriculumUrl('syllabus_mapping.json'));
        if (!response.ok) throw new Error('Failed to load syllabus map');
        const data = await response.json();
        // Sort the mapping by date before setting it
        const sortedMapping = Object.fromEntries(
          Object.entries(data).sort((a, b) => new Date(a[0]) - new Date(b[0]))
        );
        const mapping = sortedMapping;
        setSyllabusMap(mapping);
        
        // If 'first', resolve to the earliest date in the mapping and update dropdown
        let resolvedDate = selectedSyllabusDate;
        if (selectedSyllabusDate === 'first') {
          const firstDate = Object.keys(mapping).sort((a, b) => new Date(a) - new Date(b))[0];
          if (firstDate) {
            resolvedDate = firstDate;
            setSelectedSyllabusDate(firstDate); // Update dropdown to show actual slide
          }
        }
        
        // Determine the "Effective Date" (either today or the manually selected limit)
        const effectiveDateStr = selectedSyllabusDate === 'auto' 
          ? getESTTimestamp().split(' ')[0] 
          : resolvedDate;
        
        // Find all files that should be included (on or before the effective date)
        const sortedDates = Object.keys(mapping).sort((a, b) => new Date(a) - new Date(b));
        let cumulativeContent = '';
        const loadedFiles = []; // DEBUG: Track loaded files
        
        console.log('🔍 DEBUG [Curriculum Loading]:', {
          effectiveDate: effectiveDateStr,
          totalDatesInMapping: sortedDates.length,
          allDates: sortedDates
        });
        
        for (const date of sortedDates) {
          if (new Date(date) <= new Date(effectiveDateStr)) {
            const fileName = mapping[date];
            const url = getCurriculumUrl(fileName);
            console.log(`📄 DEBUG: Loading ${fileName} from ${url}`);
            const contentRes = await fetch(url);
            if (contentRes.ok) {
              const content = await contentRes.text();
              cumulativeContent += (cumulativeContent ? '\n\n' : '') + content;
              loadedFiles.push(fileName);
            } else {
              console.error(`❌ DEBUG: Failed to load ${fileName} - Status: ${contentRes.status}`);
            }
          } else {
            console.log(`⏭️ DEBUG: Skipping ${mapping[date]} (date ${date} > ${effectiveDateStr})`);
          }
        }
        
        console.log('✅ DEBUG [Curriculum Loading Complete]:', {
          filesLoaded: loadedFiles,
          totalFilesLoaded: loadedFiles.length,
          contentLength: cumulativeContent.length
        });
        
        setCurriculumContent(cumulativeContent || '');
        setLoadedSlides(loadedFiles); // Track loaded slides for dynamic forbidden list
      } catch (error) {
        console.error('Error loading curriculum:', error);
      } finally {
        setIsCurriculumLoading(false);
      }
    };
    loadCurriculum();
  }, [selectedSyllabusDate, isLoggedIn, selectedSection]);

  // Update system prompt whenever curriculum or adaptive hints update
  useEffect(() => {
    if (isCurriculumLoading) return; // Wait for curriculum to load
    
    // Skip if conversation has started AND we're not updating hints
    // Allow update if adaptiveHints changed (to inject new hint levels mid-conversation)
    const hasConversationStarted = messages.length > 1;
    const hasHints = Object.keys(adaptiveHints).length > 0;
    
    // If conversation started and no new hints to add, skip
    if (hasConversationStarted && !hasHints) return;

    // NOTE: Adaptive hints are NOT added here - they are added per-message in sendMessage()
    // based on the specific topics detected in the user's message

    // Generate dynamic forbidden list based on what's NOT in the loaded curriculum content
    // This is content-based (not hardcoded to slide numbers) so it works with any curriculum
    const generateForbiddenList = () => {
      const forbidden = [];
      const content = curriculumContent.toLowerCase();
      
      // Helper: check if concept keywords appear in loaded curriculum
      const curriculumHas = (...keywords) => keywords.some(kw => content.includes(kw.toLowerCase()));
      
      // Check for dictionaries
      if (!curriculumHas('dictionary', 'dictionaries', 'dict()', '.keys()', '.values()', '.items()')) {
        forbidden.push('- Dictionaries and dictionary methods ({}, dict(), .keys(), .values(), .items())');
      }
      
      // Check for sets
      if (!curriculumHas('sets', 'set()', 'set operations', 'union', 'intersection')) {
        forbidden.push('- Sets and set operations (set(), .add(), .remove(), union, intersection)');
      }
      
      // Check for 2D lists
      if (!curriculumHas('2d list', '2-d list', 'two-dimensional', 'multidimensional', 'nested list')) {
        forbidden.push('- 2D lists / nested lists / multidimensional arrays');
      }
      
      // Check for lists
      if (!curriculumHas('list', '.append()', '.pop()', '.sort()', '.reverse()', 'list comprehension')) {
        forbidden.push('- Lists and list methods ([], .append(), .pop(), .sort(), .reverse(), list comprehensions)');
        forbidden.push('- reversed() function');
      }
      
      // Check for string slicing/indexing
      if (!curriculumHas('slicing', 's[0]', 's[:', '[::-1]', 'indexing')) {
        forbidden.push('- String/sequence indexing and slicing (s[0], s[1:3], s[::-1], s[::2])');
      }
      
      // Check for string traversal
      if (!curriculumHas('string traversal', 'for char in', 'iterating through string', 'character traversal')) {
        forbidden.push('- String character traversal (for char in string:)');
      }
      
      // Check for string reversal
      if (!curriculumHas('string reversal', 'reverse a string', 'reversing string', '[::-1]')) {
        forbidden.push('- String reversal (s[::-1] or any method to reverse strings)');
      }
      
      // Check for advanced string methods
      if (!curriculumHas('.replace()', '.split()', '.join()', '.find()', '.rfind()')) {
        forbidden.push('- Advanced string methods (.replace(), .split(), .join(), .find(), .rfind(), .count())');
      }
      
      // Check for string checking methods
      if (!curriculumHas('.isalpha()', '.isdigit()', '.isalnum()', '.startswith()', '.endswith()')) {
        forbidden.push('- String checking methods (.isalpha(), .isdigit(), .isalnum(), .startswith(), .endswith())');
      }
      
      // Check for classes
      if (!curriculumHas('class', '__init__', 'self.', 'object-oriented', 'oop')) {
        forbidden.push('- Classes and objects (class, self, __init__, methods)');
      }
      
      // Check for user-defined functions
      if (!curriculumHas('def ', 'return ', 'function', 'parameter', 'argument')) {
        forbidden.push('- User-defined functions (def, return, parameters, arguments)');
      }
      
      // Check for loops
      if (!curriculumHas('while', 'for ', 'range()', 'loop')) {
        forbidden.push('- Loops (while, for, range(), break, continue)');
      }
      
      // Check for conditionals
      if (!curriculumHas('if ', 'elif', 'else', 'conditional', 'boolean')) {
        forbidden.push('- Conditionals (if, elif, else, and, or, not)');
      }
      
      return forbidden.length > 0 
        ? `\n\n🚫 EXPLICITLY FORBIDDEN (NOT YET COVERED IN CURRICULUM):\n${forbidden.join('\n')}\n\nIf you find yourself about to suggest ANY of the above, STOP and use only the allowed syntax listed above.`
        : '';
    };

    const forbiddenSection = generateForbiddenList();

    const curriculumHeader = curriculumContent 
      ? `\n\n[CURRICULUM LOCKDOWN - START]
🚨 CRITICAL: Your knowledge and guidance are STRICTLY LIMITED to the concepts and syntax listed below. 🚨

STRICT RULES:
1. ONLY the concepts explicitly listed in "Allowed Knowledge Base" below exist in your teaching. Everything else is FORBIDDEN.
2. If a concept (lists, dictionaries, classes, file I/O, etc.) is NOT explicitly mentioned below, you MUST pretend it doesn't exist and hasn't been covered yet.
3. If a student asks about or uses syntax/concepts NOT in the list below, you MUST say: "I see you're trying to use [concept], but we haven't covered that in our curriculum yet! Let's solve this using only the tools we've learned so far."
4. Do NOT provide code examples, fixes, or hints that use features NOT listed below - even if they would be "better" solutions.
5. When reviewing student code, your FIRST priority is checking against the allowed concepts below.
6. For collecting multiple values: If "lists" is NOT in the allowed knowledge below, use multiple variables or process values one at a time (running total, counter).

Your current "Allowed Knowledge Base" is:
${curriculumContent}
${forbiddenSection}

⚠️ ANYTHING NOT EXPLICITLY LISTED ABOVE IS FORBIDDEN ⚠️
[CURRICULUM LOCKDOWN - END]`
      : '\n\nIMPORTANT: Currently there are no curriculum restrictions loaded. You may use standard Socratic tutoring for Python.';

    const systemContentBase = mode === 'breakdown' 
      ? `You are an expert Python programming tutor. Your goal is to help students solve coding problems on their own using a structured, step-by-step Socratic method.

**⛔⛔⛔ CRITICAL OUTPUT FORMAT - EVERY RESPONSE MUST INCLUDE THESE TAGS ⛔⛔⛔**
Once ANY step has been accepted, EVERY response you give MUST end with:
[PLAN_START]
1. Step description
2. Next step description
[PLAN_END]

When a step's CODE is correct/complete, you MUST ALSO include:
[STEP_COMPLETE] The step description
[CODE_START]
the code
[CODE_END]

WITHOUT THESE TAGS, THE STEPS WON'T APPEAR IN THE UI! THIS IS MANDATORY!
**⛔⛔⛔ END OUTPUT FORMAT ⛔⛔⛔**

${curriculumHeader}

General Rules:
1. Your primary focus is Python programming.

**🚨🚨🚨 RULE #2 IS THE MOST IMPORTANT RULE - NEVER BREAK IT 🚨🚨🚨**
2. **CRITICAL: NEVER GIVE A BREAKDOWN OR LIST OF STEPS!** 
   - When a user presents a NEW problem, DO NOT explain how to solve it!
   - DO NOT say "we can follow these steps: 1... 2... 3..."
   - DO NOT list or preview future steps!
   - Your VERY FIRST response must ONLY be: "That sounds like a great project! To get started, what do you think the first logical step should be?"
   - Then WAIT for the student to suggest step 1 - DO NOT suggest it yourself!

**🚨🚨🚨 RULE #2.5 - NEVER GIVE AWAY THE ANSWER IN YOUR HINTS! 🚨🚨🚨**
   - When hinting, NEVER include the actual code, values, or specific content from the solution
   - WRONG: "How would you print the headers 'a', 'b', and 'a ** b'?" ← You just gave away the answer!
   - WRONG: "What code would display 'Hello World'?" ← You revealed the string!
   - CORRECT: "What do you think should be printed for the header row?"
   - CORRECT: "What values need to appear in that output?"
   - CORRECT: "Try writing the print statement - what should go inside the parentheses?"
   - Let the STUDENT figure out WHAT to print/write, not just HOW to print it
   - Your hints should guide their THINKING, not give them the content to type
   
3. You must work on EXACTLY ONE step at a time. Do not move to Step 2 until Step 1 is planned AND coded.

**🚨 CRITICAL: TWO SEPARATE PHASES - DO NOT MIX THEM! 🚨**
4. For the CURRENT step:
   a. **Phase 1 (PLANNING ONLY)**: 
      - Ask the user to describe what the step should do
      - Give hints if they struggle (check [STUDENT ATTEMPT TRACKING] section for attempt count!)
      - When the step description is finalized, say "Great! Now let's code this step. How would you write the code for this?"
      - **NEVER SHOW CODE DURING PLANNING PHASE!** Not even as an example!
   
   b. **Phase 2 (CODING ONLY)**:
      - Now ask the student to WRITE THE CODE themselves in the sandbox
      - **🚨 CRITICAL: Do NOT complete the step until ALL code for this step is written!**
      - If student writes PARTIAL code (e.g., just "import time" when step needs more), say "Good start! What else do you need?" and let them continue
      - Give hints and feedback on their attempts (check [STUDENT ATTEMPT TRACKING] section!)
      - Only after the student writes the COMPLETE code for the step OR after 5 failed attempts, you may show the correct code
   
   c. **🚨 5-ATTEMPT RULE FOR CODING (MANDATORY) 🚨**:
      - CHECK the [STUDENT ATTEMPT TRACKING] section in this prompt for the current attempt count
      - Each piece of code the student submits counts as 1 attempt
      - If attempts < 5: Give HINTS and FEEDBACK, NOT the complete answer!
        * If code is PARTIAL but correct so far: "Good! What's missing?" or "What else does this step need?"
        * If code has SYNTAX ERRORS: Describe the error (e.g., "missing parenthesis", "unmatched bracket") but NEVER show the corrected code! Ask them to fix it.
        * If code has LOGIC ERRORS: Explain what's wrong conceptually, ask guiding questions, but NEVER show the fix!
        * **🚨 NEVER DISPLAY CORRECTED CODE UNTIL ATTEMPT 5! 🚨** Not even "it should be X" or showing the fixed version!
      - If attempts >= 5 AND student is not progressing: You may provide the complete answer
      - "Need help" or "I don't know" does NOT skip the 5-attempt requirement - give a hint!
      
5. **CRITICAL: The "Program Steps" list should ONLY contain steps that have been finalized or the one currently being worked on. NEVER add future steps to the plan - wait for the student to define them!**
6. **CRITICAL: After completing a step, ask "What do you think the next step should be?" and WAIT for the student's response. Do NOT add the next step to [PLAN_START]...[PLAN_END] until the student defines it.**
7. **CRITICAL: The plan must have EXACTLY ONE incomplete step at a time. When you ask "what's the next step?", do NOT add anything to the plan until the student responds AND you finalize that step description.**
8. **🚨 MANDATORY: As soon as the student suggests a step AND you accept it (e.g., "Good choice!", "Great!"), you MUST output the [PLAN_START]...[PLAN_END] tags in that SAME response:**
[PLAN_START]
1. Get the width using input function
[PLAN_END]
**WITHOUT these tags, the step will NOT appear in the Program Steps panel! Include them EVERY TIME you accept a step!**
9. After a step is fully completed (Planning + Coding), move immediately to asking: "Great! Now, what do you think the next step should be?"
10. Never provide the full code for the entire problem at once.
11. **CRITICAL: You MUST include the [PLAN_START] and [PLAN_END] tags in EVERY SINGLE RESPONSE once the first step is identified. The plan is a simple list showing the steps.**
12. **CRITICAL: When a coding phase for a step is finished (success or 5 tries), you MUST output the following tags EXACTLY as shown at the VERY END of your message:**
[STEP_COMPLETE] The EXACT text of the step.
[CODE_START]
The correct Python code for this step.
[CODE_END]
13. **CRITICAL: The [STEP_COMPLETE] and [CODE_START]/[CODE_END] tags are the ONLY way the system knows to copy code to the sandbox. You MUST use them whenever a step's code is finalized. Do not wrap these tags in markdown code blocks.**

**🚨🚨🚨 WHEN STUDENT COMPLETES A STEP - MANDATORY OUTPUT 🚨🚨🚨**
When the student's code is correct and a step is complete, you MUST output ALL of these:
1. Acknowledge success: "Your code is correct!"
2. The [PLAN_START]...[PLAN_END] tags listing all steps
3. The [STEP_COMPLETE], [CODE_START], [CODE_END] tags
4. Ask for next step

EXAMPLE - Student completes "Get the length" step:
---
Your code correctly uses the input function to get the length. Great work!

[PLAN_START]
1. Get the width using input
2. Get the length using input
[PLAN_END]

[STEP_COMPLETE] Get the length using input
[CODE_START]
length = input("Enter the length:")
[CODE_END]

Now, what do you think the next step should be?
---

WITHOUT these tags, the step will NOT appear in the UI!

**🚨 PLAN TAGS ARE MANDATORY - EXAMPLE 🚨**
Student says: "get the width using input function"
You say: "Good choice! Now let's code this step..."

YOUR RESPONSE MUST END WITH:
[PLAN_START]
1. Get the width using input function
[PLAN_END]

WITHOUT these tags, the step will NOT appear in the Program Steps panel!

**🚨 DO NOT PREDICT FUTURE STEPS 🚨**
WRONG (predicting step 2 before student defines it):
[PLAN_START]
1. Get the number of students
2. Get each student's score and find the highest  ← WRONG! Student hasn't defined this yet!
[PLAN_END]

CORRECT (only show current step):
[PLAN_START]
1. Get the number of students
[PLAN_END]
Then ask: "What do you think the next step should be?" and WAIT for the student's response before adding step 2.

**🚨 NEVER GIVE A BREAKDOWN - EXAMPLES 🚨**

WRONG - Giving a breakdown when student presents a problem:
Student: "I want to generate a random letter using time"
You: "Great! Let's break it down. We need to: 1. Get current time 2. Convert to integer 3. Use modulo 4. Convert to character..."
❌ THIS IS WRONG! You just gave away the entire solution!

CORRECT - Asking the student to define steps:
Student: "I want to generate a random letter using time"
You: "That sounds like a great project! To get started, what do you think the first logical step should be?"
✅ Then WAIT for the student to suggest a step!

**🚨 NEVER GIVE AWAY CONTENT IN HINTS - EXAMPLES 🚨**

WRONG - Revealing the answer in your hint:
Student wants to print a table header with columns a, b, a**b
Student says: "print statement"
You: "How would you write the code to display the headers 'a', 'b', and 'a ** b'?"
❌ THIS IS WRONG! You just told them exactly what to print!

CORRECT - Guide without revealing content:
Student wants to print a table header
Student says: "print statement"
You: "Good thinking! What do you think needs to be printed for this header row?"
✅ Let the student figure out WHAT the content should be!

WRONG - Giving the string in your question:
You: "How would you print 'Hello, World!'?"
❌ You revealed the exact string!

CORRECT:
You: "What message do you want to display? Try writing the print statement."
✅ Student decides the content!

**🚨 CODING PHASE - DO NOT COMPLETE TOO EARLY 🚨**

WRONG - Completing step when code is PARTIAL:
Step is: "Get the current time using time module"
Student writes: "import time"
You: "Perfect! You've imported the time module. Now let's move to the next step..."
❌ THIS IS WRONG! The step needs MORE than just the import!

CORRECT - Keep asking until code is COMPLETE:
Step is: "Get the current time using time module"
Student writes: "import time"
You: "Good start! You've imported the time module. But this step also needs to GET the current time. What code would you add to actually get and store the current time?"
Student writes: "import time
num = time.time()"
You: "Excellent! Now you have both the import AND you're getting the time. To see the result, how would you display it?"
Student writes: "import time
num = time.time()
print(num)"
You: "Perfect! Now the step is complete with all the code needed. Let's add this to our plan and move on. What do you think the next step should be?"

[PLAN_START]
1. Get the current time using time module
[PLAN_END]

**🚨 QUIZ ANSWER DETECTION - READ THIS CAREFULLY 🚨**
When you receive a SHORT answer (like "A", "B", "C", "D", "True", "False", "int", "float", etc.) after you asked a quiz question:
1. This is the student answering your quiz
2. You MUST evaluate if they are correct or wrong
3. You MUST include the [QUIZ_RESULT] tag - NO EXCEPTIONS

**ABSOLUTELY CRITICAL - QUIZ RESULT TRACKING:**
Whenever you evaluate a student's answer to a quiz question (multiple choice, true/false, code output prediction, fill-in-the-blank, short factual answer), you MUST ALWAYS include this EXACT tag block at the END of your response:
[QUIZ_RESULT]
Subject: <the Python topic being tested, e.g., Loops, Variables, Functions, Lists, etc. NEVER use a date, syllabus date, or calendar date — use the topic name only>
Result: <correct OR wrong>
Question: <copy the EXACT quiz question you asked, including all answer options if it was multiple choice. IMPORTANT: preserve all code indentation/spacing exactly as written - do NOT flatten Python code onto one line>
[/QUIZ_RESULT]

**EXAMPLE - Student answers "A" to your quiz:**
You asked: "Which function converts to integer? A) int() B) str() C) float() D) bool()"
Student says: "A"
Your response MUST be:
"Correct! int() is used to convert values to integers. [explanation...]

[QUIZ_RESULT]
Subject: Type Conversion
Result: correct
Question: Which function converts to integer? A) int() B) str() C) float() D) bool()
[/QUIZ_RESULT]"

**THIS IS MANDATORY** - Without this tag, the system cannot track the student's progress. Include it EVERY TIME you tell the student their answer is right or wrong.

**INFORMAL QUESTION TRACKING - ALSO MANDATORY:**
Whenever you evaluate a student's answer to ANY question you asked (not just formal quizzes), you MUST include:
[INFORMAL_RESULT]
Subject: <the Python topic being discussed>
Result: <correct OR wrong>
[/INFORMAL_RESULT]

**THIS IS REQUIRED** - Include this tag EVERY TIME you acknowledge the student answered correctly OR incorrectly during conversation. This includes when you CORRECT a wrong answer!

Examples that REQUIRE [INFORMAL_RESULT]:

CORRECT answers:
- You: "How would you get user input?" → Student: "using input function" → You: "Correct!" → [INFORMAL_RESULT] Subject: Input Function, Result: correct
- You: "What data type for decimals?" → Student: "float" → You: "Right!" → [INFORMAL_RESULT] Subject: Data Types, Result: correct

WRONG answers (ALSO REQUIRE the tag!):
- You: "What data type for numbers?" → Student: "boolean" → You: "Actually, boolean is for True/False..." → [INFORMAL_RESULT] Subject: Data Types, Result: wrong
- You: "How would you convert to integer?" → Student: "str()" → You: "Not quite, str() is for strings..." → [INFORMAL_RESULT] Subject: Type Conversion, Result: wrong

Do NOT use [INFORMAL_RESULT] for formal quizzes - use [QUIZ_RESULT] instead.

**BEFORE YOU RESPOND - CHECK THIS:**
☐ Did I just finalize a step description? → ADD [PLAN_START]...[PLAN_END] tags!
☐ Did the student just answer a question I asked?
☐ Did I acknowledge if they were right or wrong?
→ If YES to both: I MUST add [INFORMAL_RESULT] tag at the end!

Example: If student said "int" and I said "Great choice!" → I must add:
[INFORMAL_RESULT]
Subject: Data Types
Result: correct
[/INFORMAL_RESULT]

**⚠️ FINAL CHECKLIST - EVERY RESPONSE:**
1. Did I finalize a step? → Include [PLAN_START]...[PLAN_END]
2. Did I evaluate a quiz answer? → Include [QUIZ_RESULT]
3. Did I evaluate an informal answer? → Include [INFORMAL_RESULT]
${forbiddenSection ? `\n\n⛔⛔⛔ FINAL REMINDER - FORBIDDEN CONCEPTS ⛔⛔⛔${forbiddenSection}\n⛔ DO NOT USE ANY OF THE ABOVE - THEY HAVE NOT BEEN TAUGHT YET! ⛔` : ''}`
      : mode === 'debugging'
      ? `You are an expert Python debugging tutor. Your goal is to help students find and fix bugs in their code using the Socratic method.
${curriculumHeader}

General Rules:
1. Your primary focus is debugging Python code.
2. When a user presents buggy code, ask them what they expect the code to do and what it is actually doing.
3. Guide them to use print statements or the sandbox to isolate the issue.
4. Give them 5 initial attempts to find and fix the bug themselves.
5. **PROGRESSIVE HINTS AFTER 5 ATTEMPTS**: After the 5th attempt, evaluate if the student is making progress:
   - Signs of PROGRESS: Identifying part of the bug, using debugging techniques you suggested, narrowing down the issue, showing partial understanding
   - Signs of NO PROGRESS: Repeating same wrong fixes, completely lost, giving up, random changes
   - If PROGRESSING: Give 1 more hint and check progress again after their next attempt. Repeat as long as they show progress.
   - If NOT PROGRESSING: Provide the corrected code AND immediately ask: "That was a tricky bug involving [Topic]. Would you like a quick practice question to master this specific error type?" If they say yes, provide ONE question at a time. Wait for their answer before giving another. (IMPORTANT: When they answer, you MUST evaluate using the [QUIZ_RESULT] tag.)
6. If they accept the practice scenarios and answer most incorrectly, you MUST strongly suggest: "It seems like this specific [Topic] is a sticking point. I recommend you try the 'Problem Building Block Practice' mode to get more comfortable with these concepts."
7. Work on one bug at a time.
8. Encourage them to explain their thought process.
9. Do not provide the full fixed code immediately.
10. Use the [PLAN_START] and [PLAN_END] tags to track the debugging steps if applicable.

**ABSOLUTELY CRITICAL - QUIZ RESULT TRACKING:**
Whenever you evaluate a student's answer to a question (identifying a bug, predicting output, etc.), you MUST ALWAYS include this EXACT tag block at the END of your response:
[QUIZ_RESULT]
Subject: <the Python topic being tested, e.g., Debugging, Syntax Errors, Logic Errors, Variables, Loops, etc.>
Result: <correct OR wrong>
Question: <copy the EXACT question you asked, including any code snippets>
[/QUIZ_RESULT]

**THIS IS MANDATORY** - Without this tag, the system cannot track the student's progress. Include it EVERY TIME you tell the student their answer is right or wrong.

**INFORMAL QUESTION TRACKING - ALSO MANDATORY:**
Whenever you evaluate a student's answer to ANY question you asked during debugging, you MUST include:
[INFORMAL_RESULT]
Subject: <the Python topic being discussed>
Result: <correct OR wrong>
[/INFORMAL_RESULT]

**THIS IS REQUIRED** - Include this tag when student answers correctly OR incorrectly!

CORRECT: You: "What's causing this error?" → Student: "Type mismatch" → You: "Exactly!" → [INFORMAL_RESULT] Subject: Type Errors, Result: correct
WRONG: You: "What's wrong here?" → Student: "Missing parenthesis" → You: "Actually, the issue is..." → [INFORMAL_RESULT] Subject: Debugging, Result: wrong

**BEFORE YOU RESPOND - CHECK THIS:**
☐ Did the student just answer a question I asked?
☐ Did I acknowledge if they were right or wrong?
→ If YES to both: I MUST add [INFORMAL_RESULT] tag at the end!
${forbiddenSection ? `\n\n⛔⛔⛔ FINAL REMINDER - FORBIDDEN CONCEPTS ⛔⛔⛔${forbiddenSection}\n⛔ DO NOT USE ANY OF THE ABOVE - THEY HAVE NOT BEEN TAUGHT YET! ⛔` : ''}`
      : `You are an expert Python tutor specializing in teaching the fundamental building blocks of programming. Your goal is to help students understand the concepts, logic, and syntax needed to solve a specific problem.
${curriculumHeader}

General Rules:
1. Ask the student what problem they want to solve.
2. Once they provide a problem, identify the key building blocks (e.g., loops, conditionals, data structures, specific functions) needed to solve it.
3. **CRITICAL - ONE QUESTION ONLY**:
   - When the user asks for a quiz or clicks "Quiz Me", you MUST give **exactly ONE question**. 
   - NEVER give 2, 3, or more questions at once. ONE question only.
   - Wait for the student to answer before asking another question.
   - When the user sends a normal message or clicks "Send to Tutor", engage in **conversational practice**. Ask them to explain a concept in their own words, predict what a code snippet will do, or write a small piece of code to practice the current building block. **Do NOT use multiple-choice format for normal practice.**
4. **Adaptive Quizzing**: 
   - Rotate through the different building blocks to ensure broad coverage.
   - **CRITICAL**: If the student has a high "Wrong" count for a specific subject, prioritize asking more questions about that subject until their understanding improves.
5. Use the Socratic method: guide them with hints rather than giving answers.
6. Only move to the next building block once they demonstrate understanding of the current one.
7. Keep the conversation focused on the 'why' and 'how' of the building blocks.
8. Do not provide the full solution to the problem immediately.
9. **ABSOLUTELY CRITICAL - YOU MUST DO THIS IN EVERY RESPONSE: As soon as you identify the building blocks for a problem, you MUST output the progress block at the END of your message. Include this in EVERY SINGLE RESPONSE after identifying the building blocks, even if all scores are 0. Use this EXACT format with no markdown formatting:**
[PROGRESS_START]
Input Function: Correct: 0, Wrong: 0
Type Conversion: Correct: 0, Wrong: 0
Variables: Correct: 0, Wrong: 0
[PROGRESS_END]

**IMPORTANT: Replace the example subjects above with the ACTUAL building blocks you identified for the student's problem. Do NOT use "Subject Name" or "Another Subject" - use real Python topic names like "Input Function", "Type Conversion", "Variables", "Arithmetic Operators", etc.**

**IMPORTANT: The [PROGRESS_START] and [PROGRESS_END] tags are REQUIRED in every response once you've identified the building blocks. Without these tags, the student cannot see their progress chart.**

**🚨 QUIZ ANSWER DETECTION - READ THIS CAREFULLY 🚨**
When you receive a SHORT answer (like "A", "B", "C", "D", "True", "False", "int", "float", etc.) after you asked a quiz question:
1. This is the student answering your quiz
2. You MUST evaluate if they are correct or wrong
3. You MUST include the [QUIZ_RESULT] tag - NO EXCEPTIONS

**ABSOLUTELY CRITICAL - QUIZ RESULT TRACKING:**
Whenever you evaluate a student's answer to a quiz question (multiple choice, true/false, code output prediction, fill-in-the-blank, short factual answer), you MUST ALWAYS include this EXACT tag block at the END of your response:
[QUIZ_RESULT]
Subject: <the Python topic being tested - should match one of the building blocks you identified. NEVER use a date, syllabus date, or calendar date — use the topic name only>
Result: <correct OR wrong>
Question: <copy the EXACT quiz question you asked, including all answer options if it was multiple choice. IMPORTANT: preserve all code indentation/spacing exactly as written - do NOT flatten Python code onto one line>
[/QUIZ_RESULT]

**THIS IS MANDATORY** - Without this tag, the system cannot track the student's progress. Include it EVERY TIME you tell the student their answer is right or wrong.

**INFORMAL QUESTION TRACKING - ALSO MANDATORY:**
Whenever you evaluate a student's answer to ANY question you asked during practice, you MUST include:
[INFORMAL_RESULT]
Subject: <the Python topic being discussed - should match a building block. NEVER use a date, syllabus date, or calendar date — use the topic name only>
Result: <correct OR wrong>
[/INFORMAL_RESULT]

**THIS IS REQUIRED** - Include this tag when student answers correctly OR incorrectly!

CORRECT: You: "How would you get user input?" → Student: "using input()" → You: "Correct!" → [INFORMAL_RESULT] Subject: Input Function, Result: correct
WRONG: You: "What data type for numbers?" → Student: "boolean" → You: "Actually, boolean is for True/False..." → [INFORMAL_RESULT] Subject: Data Types, Result: wrong

Do NOT use [INFORMAL_RESULT] for formal quizzes triggered by "Quiz Me" buttons - use [QUIZ_RESULT] instead.

**EXAMPLE - Student answers "A" to your quiz:**
You asked: "Which function converts to integer? A) int() B) str() C) float() D) bool()"
Student says: "A"
Your response MUST be:
"Correct! int() is used to convert values to integers. [explanation...]

[QUIZ_RESULT]
Subject: Type Conversion
Result: correct
Question: Which function converts to integer? A) int() B) str() C) float() D) bool()
[/QUIZ_RESULT]"

**BEFORE YOU RESPOND - CHECK THIS:**
☐ Did the student just answer a question I asked?
☐ Did I acknowledge if they were right or wrong?
→ If YES to both: I MUST add [INFORMAL_RESULT] or [QUIZ_RESULT] tag at the end!

Example: If student said "int" and I said "Great choice!" → I must add:
[INFORMAL_RESULT]
Subject: Data Types
Result: correct
[/INFORMAL_RESULT]

**⚠️ FINAL REMINDER - DO NOT FORGET:**
- If you just told a student their QUIZ answer was CORRECT or WRONG → ADD [QUIZ_RESULT] tag!
- If you just told a student their conversational answer was right/wrong → ADD [INFORMAL_RESULT] tag!
- The tags go at the END of your response, AFTER your explanation.
- WITHOUT these tags, the student's progress will NOT be tracked!
${forbiddenSection ? `\n\n⛔⛔⛔ FINAL REMINDER - FORBIDDEN CONCEPTS ⛔⛔⛔${forbiddenSection}\n⛔ DO NOT USE ANY OF THE ABOVE - THEY HAVE NOT BEEN TAUGHT YET! ⛔` : ''}`;
// NOTE: Adaptive hints are added dynamically in sendMessage based on detected topics

    // Update system prompt - either replace all messages or just update the first (system) message
    if (messages.length <= 1) {
      // No conversation yet - set initial system message
      logger.debug('📝 Setting initial system prompt (hints will be added per-message)');
      setMessages([{ role: 'system', content: systemContentBase }]);
    } else {
      // Conversation started - update only the system message (first message)
      // Don't log all adaptive hints here - they'll be filtered per-message in sendMessage
      logger.debug('📝 System prompt base updated (hints added per-message based on topic)');
      
      setMessages(prev => [
        { role: 'system', content: systemContentBase },
        ...prev.slice(1)
      ]);
    }
  }, [curriculumContent, mode, messages.length, isCurriculumLoading, loadedSlides]);

  async function logAction(action, userMessage = '', tutorMessage = '', usernameOverride = null, modeOverride = null) {
    const username = usernameOverride || localStorage.getItem('username') || 'anonymous';
    const currentMode = modeOverride || mode;
    const descriptiveMode = modeNames[currentMode] || currentMode;
    const payload = { 
      username, 
      action, 
      userMessage: userMessage?.substring(0, 100) + (userMessage?.length > 100 ? '...' : ''),
      tutorMessage: tutorMessage?.substring(0, 100) + (tutorMessage?.length > 100 ? '...' : ''),
      mode: descriptiveMode,
      record_at: getESTTimestamp()
    };
    console.log('📡 API: POST /api/logMessage');
    try {
      await apiFetch(`${API_BASE_URL}/api/logMessage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          username, 
          action, 
          userMessage, 
          tutorMessage, 
          mode: descriptiveMode,
          record_at: getESTTimestamp()
        }),
      });
      console.log('📡 API: POST /api/logMessage → Success');
    } catch (error) {
      console.error('📡 API ERROR [POST] /api/logMessage:', error);
    }
  }

  // Quick refresh of masteryAggregate from DB (for Building Block Mastery chart)
  const refreshMasteryFromDB = async () => {
    const currentUsername = localStorage.getItem('username');
    const username = (currentUsername && currentUsername !== 'undefined') ? currentUsername : 'admin';
    try {
      console.log('📡 Refreshing masteryAggregate from DB...');
      const aggRes = await apiFetch(`${API_BASE_URL}/api/studentMastery?username=${username}`);
      if (aggRes.ok) {
        const aggData = await aggRes.json();
        const aggArray = Array.isArray(aggData) ? aggData : (aggData.history || []);
        const aggregates = {};
        aggArray.forEach(item => {
          if (item.subject) {
            aggregates[item.subject] = { 
              correct: parseInt(item.correct) || 0, 
              wrong: parseInt(item.wrong) || 0 
            };
          }
        });
        setMasteryAggregate(aggregates);
        console.log('📡 masteryAggregate refreshed:', aggregates);
      }
    } catch (err) {
      console.error('📡 Failed to refresh masteryAggregate:', err);
    }
  };

  const fetchMasteryAnalysis = async () => {
    const currentUsername = localStorage.getItem('username');
    const username = (currentUsername && currentUsername !== 'undefined') ? currentUsername : 'admin';
    try {
      // 1. Fetch Aggregates
      console.log('📡 API: GET /api/studentMastery');
      const aggRes = await apiFetch(`${API_BASE_URL}/api/studentMastery?username=${username}`);
      let aggregates = {};
      if (aggRes.ok) {
        const aggData = await aggRes.json();
        const aggArray = Array.isArray(aggData) ? aggData : (aggData.history || []);
        aggArray.forEach(item => {
          if (item.subject) {
            aggregates[item.subject] = { 
              correct: parseInt(item.correct) || 0, 
              wrong: parseInt(item.wrong) || 0 
            };
          }
        });
        setMasteryAggregate(aggregates);
        console.log('📡 API: GET /api/studentMastery →', Object.keys(aggregates).length, 'subjects');
      }

      // 2. Fetch History
      console.log('📡 API: GET /api/studentMasteryHistory');
      const res = await apiFetch(`${API_BASE_URL}/api/studentMasteryHistory?username=${username}&limit=1000`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.history) {
        setMasteryHistory(data.history);
        console.log('📡 API: GET /api/studentMasteryHistory →', data.history.length, 'records');
        const analysis = analyzePerformanceHistory(data.history, aggregates);
        setMasteryAnalysis(analysis);
        
        // Calculate knowledge health (memory strength for each subject) - now async
        const health = await calculateKnowledgeHealth(data.history, aggregates);
        setKnowledgeHealth(health);
      }
    } catch (err) {
      console.error("Failed to fetch mastery history analysis:", err);
    }
  };

  // Generate AI recommendations for Full Analytics panel using masteryHistory
  const generateAnalyticsRecommendations = async () => {
    if (analyticsRecsLoading) return;
    setAnalyticsRecsLoading(true);
    setAnalyticsRecommendations([]);
    
    try {
      // Build performance summary from masteryHistory
      const subjectStats = {};
      masteryHistory.forEach(record => {
        const subject = record.subject;
        if (!subject) return;
        if (!subjectStats[subject]) {
          subjectStats[subject] = { correct: 0, wrong: 0, total: 0 };
        }
        subjectStats[subject].correct += record.correct || 0;
        subjectStats[subject].wrong += record.wrong || 0;
        subjectStats[subject].total += (record.correct || 0) + (record.wrong || 0);
      });
      
      // Also include masteryAggregate for completeness
      Object.entries(masteryAggregate).forEach(([subject, data]) => {
        if (!subjectStats[subject]) {
          subjectStats[subject] = { correct: 0, wrong: 0, total: 0 };
        }
        // Use aggregate if it has more data (avoid double counting by taking max)
        const aggTotal = (data.correct || 0) + (data.wrong || 0);
        if (aggTotal > subjectStats[subject].total) {
          subjectStats[subject].correct = data.correct || 0;
          subjectStats[subject].wrong = data.wrong || 0;
          subjectStats[subject].total = aggTotal;
        }
      });
      
      // Format for LLM - include trend data from knowledgeHealth
      const performanceSummary = Object.entries(subjectStats)
        .map(([subject, stats]) => {
          const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
          
          // Get trend info from knowledgeHealth if available
          const health = knowledgeHealth[subject];
          let trendInfo = '';
          if (health) {
            if (health.trend && health.trend !== 'stable') {
              const trendEmoji = (health.trend === 'improving' || health.trend === 'progressing') ? '📈' : '📉';
              trendInfo += ` | Trend: ${trendEmoji} ${health.trend}`;
              if (health.earlyAccuracy != null && health.recentAccuracy != null) {
                trendInfo += ` (${Math.round(health.earlyAccuracy)}%→${Math.round(health.recentAccuracy)}%)`;
              }
            }
            if (health.daysSince != null && health.daysSince > 0) {
              trendInfo += ` | Last practiced: ${health.daysSince} days ago`;
            }
          }
          
          return `- ${subject}: ${stats.correct}/${stats.total} correct (${accuracy}%)${trendInfo}`;
        })
        .join('\n');
      
      if (!performanceSummary) {
        setAnalyticsRecommendations([{ subject: 'No Data', recommendation: 'Start taking quizzes to get personalized recommendations!' }]);
        setAnalyticsRecsLoading(false);
        return;
      }
      
      const response = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `You are a Python learning advisor analyzing a student's quiz performance history.
Based on their performance data, provide exactly ${recommendationCount} specific, actionable recommendations.

Rules:
1. PRIORITIZE declining trends (📉) - students getting worse need immediate attention
2. Focus on subjects with LOW accuracy (<70%) or few attempts
3. Consider "days since practiced" - topics not practiced recently may need review
4. Each recommendation must reference a SPECIFIC subject from their data
5. Be encouraging but honest about areas needing work
6. Assign priority: "high" (declining trend OR accuracy <50%), "medium" (accuracy 50-70% or not practiced recently), "low" (improving or >70%)
7. Return ONLY a JSON array with exactly ${recommendationCount} objects: [{"subject": "Topic Name", "priority": "high|medium|low", "recommendation": "Your advice"}]`
            },
            {
              role: 'user',
              content: `Student's quiz performance by subject:\n${performanceSummary}\n\nProvide ${recommendationCount} personalized recommendations.`
            }
          ],
          temperature: 0.3,
          max_tokens: 800
        })
      });
      
      if (!response.ok) throw new Error('API request failed');
      
      const data = await response.json();
      const content = data.choices[0]?.message?.content || '[]';
      
      // Parse JSON response
      const jsonStr = content.replace(/```json\n?|\n?```/g, '').trim();
      const recommendations = JSON.parse(jsonStr);
      
      // Sort by priority: high first, then medium, then low
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      recommendations.sort((a, b) => {
        const aPriority = priorityOrder[a.priority?.toLowerCase()] ?? 2;
        const bPriority = priorityOrder[b.priority?.toLowerCase()] ?? 2;
        return aPriority - bPriority;
      });
      
      setAnalyticsRecommendations(recommendations.slice(0, recommendationCount));
    } catch (error) {
      console.error('Error generating analytics recommendations:', error);
      setAnalyticsRecommendations([{ subject: 'Error', recommendation: 'Failed to generate recommendations. Please try again.' }]);
    } finally {
      setAnalyticsRecsLoading(false);
    }
  };

  const renderMasteryDashboard = () => {
    // Demo mode: show descriptive message instead of empty dashboard
    if (isDemoMode()) {
      return (
        <div className="dashboard-overlay">
          <div className="dashboard-header">
            <div className="header-left">
              <h2>📊 Quiz Analytics</h2>
              <p>Locked in Demo Mode</p>
            </div>
            <button className="close-dashboard" onClick={() => setShowDebug(false)}>✕</button>
          </div>
          <div style={{ padding: '32px 40px', color: '#fffacd', whiteSpace: 'pre-wrap', lineHeight: '1.8', fontSize: '14px' }}>
            {`🔒 Quiz Analytics — Locked in Demo Mode

With a real account this dashboard tracks every quiz question you've ever answered across all Python topics.

For each topic you get:
  📈  Accuracy %  — how often you answer correctly overall
  🔥  Streak      — consecutive correct answers in your last sessions
  ⚠️  Flip-flop warning — if you keep getting the same topic right then wrong then right, the tutor flags it
  📅  History chart — a bar showing recent correct vs wrong answers over time

Example view after a week of quizzing:

  Topic              Correct  Wrong  Accuracy  Status
  ─────────────────────────────────────────────────────
  Variables            18       2      90%      ✅ Strong
  For Loops            10       8      56%      ⚠️  Struggling
  Functions             5       1      83%      📈 Improving
  If Statements         3       7      30%      🆘 Needs Work

The tutor uses this data to automatically quiz you more on weak topics
and back off on topics where you're consistently strong.

✨ Sign up to start building your quiz analytics!`}
          </div>
        </div>
      );
    }

    // 1. Identify all unique subjects from history, lifetime aggregates, AND current session
    // NORMALIZE: Merge subjects that differ only by case (e.g., "Variables" and "variables")
    const normalizeSubject = (subject) => {
      if (!subject) return '';
      // Convert to title case for consistent display
      return subject.toLowerCase().split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    };

    // Create a map to merge case-insensitive duplicates
    const subjectMap = new Map(); // normalized -> { displayName, sources: Set }
    
    const addSubject = (subject) => {
      if (!subject) return;
      const normalized = normalizeSubject(subject);
      if (!subjectMap.has(normalized)) {
        subjectMap.set(normalized, { displayName: normalized, originalNames: new Set() });
      }
      subjectMap.get(normalized).originalNames.add(subject);
    };

    // Collect all subjects
    Object.keys(masteryAggregate).forEach(addSubject);
    Object.keys(sessionProgress).forEach(addSubject);
    masteryHistory.forEach(h => addSubject(h.subject));

    // Filter to only include subjects with actual quiz data (at least 1 correct or 1 wrong)
    const allSubjects = Array.from(subjectMap.keys()).filter(normalizedSubject => {
      const originalNames = subjectMap.get(normalizedSubject)?.originalNames || new Set();
      let totalCorrect = 0, totalWrong = 0;
      
      // Check masteryAggregate
      originalNames.forEach(origName => {
        const agg = masteryAggregate[origName];
        if (agg) {
          totalCorrect += agg.correct || 0;
          totalWrong += agg.wrong || 0;
        }
      });
      
      // Check masteryHistory
      masteryHistory.forEach(h => {
        if (originalNames.has(h.subject)) {
          totalCorrect += h.correct || 0;
          totalWrong += h.wrong || 0;
        }
      });
      
      // Only include if there's at least 1 quiz answer
      return (totalCorrect + totalWrong) > 0;
    }).sort();

    // Show empty state if no data
    if (allSubjects.length === 0) {
      return (
        <div className="dashboard-overlay">
          <div className="dashboard-header">
            <div className="header-left">
              <h2>📊 Student Analytics Dashboard</h2>
              <p>Tracking performance across all building blocks</p>
            </div>
            <button className="close-dashboard" onClick={() => setShowDebug(false)}>✕</button>
          </div>
          <div className="empty-state" style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#666',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px',
            margin: '20px 0'
          }}>
            <p style={{ fontSize: '48px', marginBottom: '10px' }}>📭</p>
            <h3 style={{ marginBottom: '10px' }}>No Data Yet</h3>
            <p>Start answering quiz questions to see your analytics here!</p>
          </div>
        </div>
      );
    }

    // Helper to get data for a normalized subject (merges all case variants)
    const getOriginalNames = (normalizedSubject) => {
      return subjectMap.get(normalizedSubject)?.originalNames || new Set();
    };

    const historyBySubject = {};
    masteryHistory.forEach(item => {
      const normalized = normalizeSubject(item.subject);
      if (!historyBySubject[normalized]) historyBySubject[normalized] = [];
      historyBySubject[normalized].push(item);
    });

    // Helper function to determine performance label
    const getPerformanceLabel = (correct, wrong, sessionAccuracies = []) => {
      const total = correct + wrong;
      if (total === 0) return { label: 'New Topic', class: 'new', icon: '🆕' };
      
      const accuracy = correct / total;
      
      // IMPROVED FLIP-FLOP DETECTION
      // Since each session is just 1 question (100% or 0%), we need to look at PATTERNS
      // Use a sliding window approach: group consecutive answers and look for streaks
      
      let isFlipFlopping = false;
      
      if (sessionAccuracies.length >= 6 && accuracy < 0.65) {
        // Convert to binary: 1 = correct (100%), 0 = wrong (0%)
        const binary = sessionAccuracies.map(acc => acc >= 0.5 ? 1 : 0);
        
        // Count alternations (0→1 or 1→0)
        let alternations = 0;
        for (let i = 1; i < binary.length; i++) {
          if (binary[i] !== binary[i-1]) {
            alternations++;
          }
        }
        
        // Alternation ratio: how often does the answer change from previous?
        // Pure flip-flop: 0,1,0,1,0,1 = 5 alternations out of 5 possible = 100%
        // Consistent: 1,1,1,0,0,0 = 1 alternation out of 5 possible = 20%
        const alternationRatio = alternations / (binary.length - 1);
        
        // Also check for lack of streaks (consecutive correct or consecutive wrong)
        let maxStreak = 1;
        let currentStreak = 1;
        for (let i = 1; i < binary.length; i++) {
          if (binary[i] === binary[i-1]) {
            currentStreak++;
            maxStreak = Math.max(maxStreak, currentStreak);
          } else {
            currentStreak = 1;
          }
        }
        
        // Flip-flopping criteria:
        // - High alternation ratio (>60% of answers flip from previous)
        // - No significant streaks (max streak ≤ 2)
        // - Low overall accuracy (<65%)
        isFlipFlopping = alternationRatio >= 0.6 && maxStreak <= 2;
      }
      
      if (isFlipFlopping) {
        return { label: 'Flip-Flopping', class: 'inconsistent', icon: '🔄' };
      }

      // PRIORITY ORDER: High accuracy wins
      if (accuracy >= 0.8 && total >= 5) {
        return { label: 'Mastered', class: 'mastered', icon: '⭐' };
      } else if (accuracy >= 0.8 && total < 5) {
        return { label: 'Good Start', class: 'progressive', icon: '📈' };
      } else if (accuracy >= 0.5) {
        return { label: 'Learning', class: 'learning', icon: '📚' };
      } else if (accuracy >= 0.3) {
        return { label: 'Needs Practice', class: 'struggling', icon: '💪' };
      } else {
        return { label: 'Struggling', class: 'poor', icon: '⚠️' };
      }
    };

    return (
      <>
      <style>{`
        @keyframes glow {
          from { text-shadow: 0 0 8px rgba(126, 232, 250, 0.5), 0 0 16px rgba(126, 232, 250, 0.2); }
          to { text-shadow: 0 0 12px rgba(126, 232, 250, 0.8), 0 0 24px rgba(126, 232, 250, 0.4), 0 0 32px rgba(126, 232, 250, 0.2); }
        }
      `}</style>
      <div className="dashboard-overlay" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div className="dashboard-header" style={{ marginTop: '10px' }}>
          <div className="header-left">
            <h2>Quiz Progress Analytics</h2>
            <p>Tracking performance across all building blocks</p>
          </div>
          <button className="close-dashboard" onClick={() => setShowDebug(false)}>✕</button>
        </div>
        
        {/* Two-column layout */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left side - Subject cards */}
          <div style={{ flex: '1 1 65%', overflowY: 'auto', padding: '20px' }}>
            <div className="dashboard-grid">
          {allSubjects.map(subject => {
            // Get all original names for this normalized subject
            const originalNames = getOriginalNames(subject);
            
            // Merge history entries from all case variants
            const entries = historyBySubject[subject] || [];
            
            // Calculate totals from masteryHistory only
            let totalCorrect = 0, totalWrong = 0;
            entries.forEach(entry => {
              totalCorrect += entry.correct || 0;
              totalWrong += entry.wrong || 0;
            });
            
            const totalQuestions = totalCorrect + totalWrong;
            const lifetimeAccuracy = totalQuestions > 0 ? (totalCorrect / totalQuestions) * 100 : 0;

            // Calculate session accuracies for trend analysis
            // Sort by timestamp (oldest first for chronological order)
            // masteryHistory uses 'date' field, mastery uses 'last_updated'
            const sorted = [...entries].sort((a, b) => {
              const dateA = new Date(a.date || a.last_updated || 0).getTime();
              const dateB = new Date(b.date || b.last_updated || 0).getTime();
              return dateA - dateB;
            });

            const sessionAccuracies = [];
            const sessions = [];
            
            // Each masteryHistory record is ONE quiz answer (correct:1/wrong:0 or correct:0/wrong:1)
            // NOT cumulative - so use the values directly
            for (let i = 0; i < sorted.length; i++) {
              const correct = sorted[i].correct || 0;
              const wrong = sorted[i].wrong || 0;
              const total = correct + wrong;
              
              if (total > 0) {
                const acc = correct / total;
                sessionAccuracies.push(acc);
                sessions.push({
                  correct: correct,
                  wrong: wrong,
                  acc: acc * 100,
                  total: total
                });
              }
            }

            // Get performance label
            const performance = getPerformanceLabel(totalCorrect, totalWrong, sessionAccuracies);

            return (
              <div key={subject} className="subject-card">
                <h3>{subject}</h3>
                <div className={`status-badge ${performance.class}`}>
                  {performance.icon} {performance.label}
                </div>
                
                <div className="stats-row">
                  <div className="stat-item correct-stat">
                    <span className="stat-value">{totalCorrect}</span>
                    <span className="stat-label">Correct</span>
                  </div>
                  <div className="stat-item wrong-stat">
                    <span className="stat-value">{totalWrong}</span>
                    <span className="stat-label">Wrong</span>
                  </div>
                  <div className="stat-item accuracy-stat">
                    <span className="stat-value">{lifetimeAccuracy.toFixed(0)}%</span>
                    <span className="stat-label">Accuracy</span>
                  </div>
                </div>

                <div className="history-graph">
                  {(() => {
                    // Build bars from sessions array - expand each session into individual answer bars
                    // If a session has correct: 1, wrong: 2, we show 3 bars: 2 red then 1 green
                    // (wrong answers first, then correct within each session)
                    const historyBars = [];
                    sessions.forEach((session) => {
                      // Add wrong bars first (red)
                      for (let i = 0; i < session.wrong; i++) {
                        historyBars.push({ isCorrect: false, label: historyBars.length + 1 });
                      }
                      // Add correct bars (green)
                      for (let i = 0; i < session.correct; i++) {
                        historyBars.push({ isCorrect: true, label: historyBars.length + 1 });
                      }
                    });
                    
                    if (historyBars.length > 0) {
                      return (
                        <div className="bars-container" style={{ 
                          display: 'flex', 
                          gap: '4px', 
                          alignItems: 'flex-end', 
                          height: '60px',
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          paddingBottom: '5px',
                          maxWidth: '100%'
                        }}>
                          {historyBars.map((s, idx) => {
                            // Simple bar: green for correct, red for wrong
                            const barColor = s.isCorrect ? '#2ea043' : '#f85149';
                            const symbol = s.isCorrect ? '✓' : '✗';
                            
                            return (
                              <div 
                                key={idx} 
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '2px'
                                }}
                                title={`Quiz ${s.label}: ${s.isCorrect ? 'Correct' : 'Wrong'}`}
                              >
                                <div style={{ fontSize: '10px', color: barColor }}>{symbol}</div>
                                <div 
                                  style={{ 
                                    width: '16px',
                                    height: '40px',
                                    backgroundColor: barColor,
                                    borderRadius: '3px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                  }}
                                ></div>
                                <div style={{ fontSize: '9px', color: '#888' }}>{s.label}</div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    } else {
                      return (
                        <div className="no-sessions" style={{ color: '#888', fontSize: '12px', textAlign: 'center', padding: '10px' }}>
                          {totalQuestions > 0 ? "Lifetime data available" : "No quiz history yet"}
                        </div>
                      );
                    }
                  })()}
                </div>
                <div className="card-footer">
                  Total Questions: {totalQuestions}
                </div>
              </div>
            );
          })}
            </div>
          </div>
        
          {/* Right side - AI Recommendations */}
          <div style={{ 
            flex: '1 1 35%', 
            padding: '20px', 
            overflowY: 'auto', 
            backgroundColor: '#0d1117',
            border: '1px solid rgba(0, 212, 170, 0.4)',
            borderRadius: '8px',
            margin: '10px'
          }}>
            <h3 style={{ 
              margin: '0 0 16px 0', 
              color: '#00d4aa', 
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              🤖 AI Study Recommendations
            </h3>
            
            {analyticsRecsLoading ? (
              <div style={{ 
                textAlign: 'center', 
                color: '#8b949e', 
                padding: '40px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px'
              }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid #30363d',
                  borderTopColor: '#d29922',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <style>{`
                  @keyframes spin {
                    to { transform: rotate(360deg); }
                  }
                `}</style>
                <div style={{ fontSize: '16px', color: '#d29922' }}>Analyzing your patterns...</div>
                <div style={{ fontSize: '13px' }}>Generating personalized recommendations</div>
              </div>
            ) : analyticsRecommendations.length === 0 ? (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                padding: '40px 20px',
                textAlign: 'center'
              }}>
                <button
                  onClick={generateAnalyticsRecommendations}
                  style={{
                    backgroundColor: '#2ee8be',
                    color: '#0a0e1a',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '20px 30px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'background-color 0.2s, transform 0.1s',
                    boxShadow: '0 4px 12px rgba(46, 232, 190, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#5af0d0';
                    e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#2ee8be';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                >
                  <span style={{ fontSize: '28px' }}>🔍</span>
                  <span>Click to Analyze Your Quiz History</span>
                  <span style={{ fontSize: '12px', fontWeight: '400', opacity: 0.9 }}>
                    Generate personalized recommendations
                  </span>
                </button>
                <div style={{ 
                  marginTop: '20px', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <label style={{ color: '#c9d1d9', fontSize: '14px' }}>
                    Number of recommendations: <strong style={{ color: '#00b892' }}>{recommendationCount}</strong>
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="6"
                    value={recommendationCount}
                    onChange={(e) => setRecommendationCount(parseInt(e.target.value))}
                    style={{
                      width: '150px',
                      accentColor: '#00b892',
                      cursor: 'pointer'
                    }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '150px', fontSize: '12px', color: '#c9d1d9' }}>
                    <span>1</span>
                    <span>3</span>
                    <span>6</span>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <p style={{
                  margin: '0 0 8px 0',
                  color: '#7ee8fa',
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  textShadow: '0 0 8px rgba(126, 232, 250, 0.6), 0 0 16px rgba(126, 232, 250, 0.3)',
                  animation: 'glow 2s ease-in-out infinite alternate'
                }}>
                  ✨ Click any recommendation to start a quiz
                </p>
                {analyticsRecommendations.map((rec, idx) => {
                  const priorityStyles = {
                    high: { bg: '#f85149', label: '🔴 High' },
                    medium: { bg: '#d29922', label: '🟡 Medium' },
                    low: { bg: '#3fb950', label: '🟢 Low' }
                  };
                  const priority = priorityStyles[rec.priority] || priorityStyles.medium;
                  return (
                  <div
                    key={idx}
                    onClick={() => {
                      if (rec.subject && rec.subject !== 'No Data' && rec.subject !== 'Error') {
                        setShowDebug(false);
                        if (mode !== 'building-blocks') {
                          setMode('building-blocks');
                        }
                        const practiceMessage = `I want to practice ${rec.subject}. Can you give me a quiz question?`;
                        sendMessage(practiceMessage, 'Practice from Full Analytics');
                        logAction('Analytics Recommendation Clicked', rec.subject, rec.recommendation);
                      }
                    }}
                    style={{
                      padding: '14px 16px',
                      backgroundColor: '#161b22',
                      borderRadius: '10px',
                      cursor: rec.subject !== 'No Data' && rec.subject !== 'Error' ? 'pointer' : 'default',
                      transition: 'all 0.2s ease',
                      border: '1px solid #30363d'
                    }}
                    onMouseEnter={(e) => {
                      if (rec.subject !== 'No Data' && rec.subject !== 'Error') {
                        e.currentTarget.style.backgroundColor = '#21262d';
                        e.currentTarget.style.borderColor = '#d29922';
                        e.currentTarget.style.transform = 'translateX(4px)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#161b22';
                      e.currentTarget.style.borderColor = '#30363d';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                      <span style={{
                        color: '#e6edf3',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}>
                        {rec.subject}
                      </span>
                      <span style={{
                        backgroundColor: priority.bg,
                        color: '#fff',
                        padding: '3px 10px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        fontWeight: '600',
                        marginLeft: '12px',
                        flexShrink: 0
                      }}>
                        {priority.label}
                      </span>
                    </div>
                    <div style={{ color: '#c9d1d9', fontSize: '14px', lineHeight: '1.5' }}>
                      {rec.recommendation}
                    </div>
                  </div>
                );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
      </>
    );
  };

  useEffect(() => {
    if (isLoggedIn) {
      fetchMasteryAnalysis();
      fetchQuestionAnsweredSubjects(); // Fetch subjects for normalization
    }
  }, [isLoggedIn]);

  const handleModeChange = (newMode) => {
    if (newMode === mode) return;
    logAction(`Switch to ${modeNames[newMode] || newMode}`, '', '', null, mode);
    setMode(newMode);
    
    // Reset specific session states but NOT necessarily sessionProgress if we want smooth transition
    setPlan([]);
    setPythonCode("");
    setRunOutput('');
    setExecutionId(null);
    setIsAwaitingInput(false);
    syncedStepsRef.current.clear();
    previousStep1Ref.current = null; // Clear step 1 tracking
    lastSyncedMessageRef.current = null;
    lastSyncedDataRef.current = {};
    sessionStartMasteryRef.current = {}; // Reset session start mastery for new conversation
    
    // Reset 5-attempt tracking
    stepAttemptsRef.current = 0;
    currentStepNumberRef.current = 0;
    currentPhaseRef.current = 'planning';
    
    setMessages([]); // Clearing messages will trigger the useEffect below to set the new system prompt
  };

  // Detect and fetch hint levels for subjects currently being practiced
  const refreshAdaptiveHints = async () => {
    // Get subjects from BOTH masteryAggregate (all known subjects) AND combinedProgress
    // BUT only include subjects that have actual quiz data (at least 1 correct or wrong)
    const allKnownSubjects = Array.from(new Set([
      ...Object.keys(masteryAggregate),
      ...Object.keys(combinedProgress)
    ])).filter(subject => {
      const agg = masteryAggregate[subject];
      const progress = combinedProgress[subject];
      const totalQuestions = (agg?.correct || 0) + (agg?.wrong || 0) + 
                            (progress?.correct || 0) + (progress?.wrong || 0);
      return totalQuestions > 0;
    });
    
    if (allKnownSubjects.length > 0) {
      logger.debug('Fetching adaptive hint levels for subjects with quiz data:', allKnownSubjects);
      await fetchHintLevelsForSubjects(allKnownSubjects);
    } else {
      logger.debug('No subjects with quiz data found - skipping adaptive hints fetch');
    }
  };

  // Refresh adaptive hints when masteryAggregate loads (on login) or combinedProgress changes
  useEffect(() => {
    const aggCount = Object.keys(masteryAggregate).length;
    const progressCount = Object.keys(combinedProgress).length;
    console.log(`📊 Adaptive hints useEffect triggered: isLoggedIn=${isLoggedIn}, aggCount=${aggCount}, progressCount=${progressCount}`);
    logger.debug('📊 masteryAggregate subjects:', Object.keys(masteryAggregate));
    logger.debug('📊 combinedProgress subjects:', Object.keys(combinedProgress));
    logger.debug('📊 sessionProgress subjects:', Object.keys(sessionProgress));
    
    if (isLoggedIn && (aggCount > 0 || progressCount > 0)) {
      refreshAdaptiveHints();
    }
  }, [isLoggedIn, Object.keys(masteryAggregate).join(','), Object.keys(combinedProgress).join(',')]);

  const sendMessage = async (messageContent, action = 'Send to Tutor', contextForMatching = null, skipAdaptiveHints = false) => {
    if (loading) return;
    // Allow empty message in demo mode so the demo info still shows
    if (!messageContent && localStorage.getItem('demoMode') !== 'true') return;

    // Demo mode: never call the backend — show a mode-specific upsell instead
    if (localStorage.getItem('demoMode') === 'true') {
      const demoMessages = {
        'breakdown': "👋 You're in **Problem Breakdown Mode** — but the AI tutor is locked in demo mode.\n\n🔒 **What this mode does:**\nYou paste a programming problem and the tutor guides you through the solution step by step. At each step it asks you a question and waits for your answer. If you need a hint, it provides one — and the hint level is automatically adjusted based on your combined history: past quiz answers **and** how well you've answered questions about that topic in chat conversations.\n\nExample exchange:\n> You: *\"Write a program that asks the user for two numbers and prints their sum.\"*\n> Tutor: *\"Let's start. Step 1: how do you read input from the user in Python?\"*\n> You: `input()`\n> Tutor: *\"Right — but `input()` returns a string. What do you need to wrap it in to make it a number?\"*\n> *(you've struggled with type conversion in both quizzes and chat, so the tutor adds a hint automatically)*\n> Tutor hint: *\"Think about `int()` or `float()`...\"*\n> You: `int(input())`\n> Tutor: *\"Exactly! Now step 2: do that for the second number, then add them and print the result.\"*\n\n✨ **Sign up** to solve problems step-by-step with the AI tutor!",

        'debugging': "👋 You're in **Problem Debugging Mode** — but the AI tutor is locked in demo mode.\n\n🔒 **What this mode does:**\nYou paste code that isn't working, describe the error or wrong output, and the tutor walks you through finding and fixing the bug — asking guided questions rather than just handing you the fix.\n\nExample exchange:\n> You: *\"My code prints nothing. Here it is: `for i in range(5): print i`\"*\n> Tutor: *\"Good start! There's a syntax issue on your print statement. In Python 3, print is a function — what do you think the fix is?\"*\n> You: `print(i)`\n> Tutor: *\"Exactly! Try running it now.\"*\n\n✨ **Sign up** to debug your code with the AI tutor!",

        'building-blocks': "👋 You're in **Building Block Practice Mode** — but the AI tutor is locked in demo mode.\n\n🔒 **What this mode does:**\nYou paste a programming problem and the tutor first identifies ALL the building blocks (concepts) needed to solve it and presents them as a numbered list. Then it works through each block with you one at a time — asking you to write the code for that piece before moving on, until all blocks are assembled into a complete solution.\n\nExample exchange:\n> You: *\"Write a program that reads 5 numbers and prints the largest.\"*\n> Tutor: *\"Here are the building blocks we need:*\n>   *1. Input Function*\n>   *2. Variables*\n>   *3. For Loop*\n>   *4. Conditional (if)*\n>   *5. Tracking a running maximum\"*\n> Tutor: *\"Let's start with block 1. Write a line that reads one number from the user.\"*\n> You: `n = int(input())`\n> Tutor: *\"Perfect. Now block 2 — before the loop, create a variable to hold the largest number seen so far. What would you initialize it to?\"*\n> *(continues through each block until the full solution is assembled)*\n\nThree buttons appear below the chat:\n- **📝 Quiz This Topic** — fires a multiple-choice question on the block you're currently working on\n- **🎲 Quiz Any Topic** — picks a random block from the problem's list and quizzes you on it\n- **▶️ Continue** — tells the tutor to keep going with the building blocks (the tutor decides whether to stay on the current block or move forward based on the conversation so far)\n\n✨ **Sign up** to build solutions block-by-block with the AI tutor!"
      };

      const demoReply = demoMessages[mode] || demoMessages['breakdown'];
      setMessages(prev => [...prev,
        ...(messageContent ? [{ role: 'user', content: messageContent }] : []),
        { role: 'assistant', content: demoReply }
      ]);
      setPrompt('');
      return;
    }

    // ⚡ Show user message instantly before any processing
    setMessages(prev => [...prev, { role: 'user', content: messageContent }]);
    setLoading(true);

    logger.debug('🚀 ========== SEND MESSAGE START ==========');
    logger.debug('💬 User Message:', messageContent);
    logger.debug('📊 Mode:', mode);

    // Check if user is starting a NEW problem (in breakdown mode)
    // Detect phrases like "write a program", "create a program", problem descriptions, etc.
    if (mode === 'breakdown') {
      const lowerMessage = messageContent.toLowerCase();
      const newProblemIndicators = [
        'write a program',
        'create a program',
        'make a program',
        'build a program',
        'i want to write',
        'i want to create',
        'i want to make',
        'i want to build',
        'new problem',
        'new question',
        'different problem',
        'another problem',
        'let\'s work on',
        'help me write',
        'help me create',
        'i need to',
        'i want to'
      ];
      
      // Also detect problem descriptions: "get X and Y and calculate Z" pattern
      // This indicates a new multi-step problem
      const looksLikeProblemDescription = (
        // Has "and" connecting multiple actions (multi-step problem)
        (lowerMessage.match(/\band\b/g) || []).length >= 2 &&
        // Contains action verbs typical in problem descriptions
        /\b(get|calculate|find|compute|print|display|read|input|output)\b/.test(lowerMessage)
      );
      
      const isNewProblem = newProblemIndicators.some(phrase => lowerMessage.includes(phrase)) || 
                           looksLikeProblemDescription;
      
      // If user is asking a new problem AND we already have a plan with completed steps, reset
      if (isNewProblem && plan.length > 0 && plan.some(s => s.complete)) {
        console.log('🆕 User starting new problem! Resetting plan, code, and conversation...');
        setPlan([]);
        setPythonCode("");
        setRunOutput('');
        syncedStepsRef.current.clear();
        previousStep1Ref.current = null;
        stepAttemptsRef.current = 0;
        currentStepNumberRef.current = 0;
        currentPhaseRef.current = 'planning';
        // IMPORTANT: Clear conversation history so old plan tags aren't re-parsed
        setMessages(prev => prev.length > 0 ? [prev[0]] : []); // Keep only system message
      }
    }

    // Determine what context to use for LLM matching
    // If contextForMatching is provided (e.g., for help button), use that instead of the current message
    const matchingContext = contextForMatching || messageContent;
    logger.debug('🔍 Context for matching:', contextForMatching ? `"${matchingContext}" (from previous context)` : `"${matchingContext}" (from current message)`);

    // NEW: Use LLM-based subject matching
    // Step 1: Fetch 500 records from /api/studentMasteryHistory
    // Step 2: Extract unique subjects and ask LLM to match
    // Step 3: If match found, calculate mastery from filtered history
    // Step 4: If no match, fall back to /api/studentMastery
    // Step 5: If still no match, default to Foundational
    logger.debug('🔍 Starting LLM-based subject matching...');
    const masteryResult = await fetchMasteryWithLLMMatching(matchingContext);
    logger.debug('🔍 Mastery result:', masteryResult);

    // Fire-and-forget hint log — only log if a Python subject was identified
    if (!masteryResult.matchedSubject) {
      logger.debug('🔍 Skipping hintLog — no Python subject identified (greeting or non-question)');
    } else
    apiFetch(`${API_BASE_URL}/api/hintLog`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: localStorage.getItem('username'),
        subject: masteryResult.matchedSubject || 'unknown',
        hintLevel: masteryResult.hintLevel || 'foundational',
        accuracy: parseFloat((masteryResult.accuracy || 0).toFixed(2)),
        trend: masteryResult.trend || 'stable',
        label: masteryResult.label || '',
        isFlipFlopping: masteryResult.isFlipFlopping || false,
        totalQuestions: masteryResult.totalQuestions || 0
      })
    }).then(res => {
      if (!res.ok) res.text().then(t => console.error('❌ hintLog failed:', res.status, t));
      else logger.debug('✅ hintLog saved:', masteryResult.hintLevel, 'for', masteryResult.matchedSubject || 'no-match');
    }).catch(err => console.error('❌ hintLog error:', err));

    // Build the matched topic for hint guidance
    const matchedSubject = masteryResult.matchedSubject;
    const isQuizRequest = masteryResult.isQuizRequest || false;
    const allDetectedTopics = matchedSubject ? [matchedSubject] : [];

    // Save soft review if:
    // 1. Subject was matched
    // 2. NOT a quiz request (LLM detected)
    // 3. NOT a quiz-related action (button clicks)
    const quizActions = ['Quiz Me (Current)', 'Quiz Me (Random)', 'Continue Practice', 'A', 'B', 'C', 'D'];
    const isQuizAction = quizActions.includes(action);
    
    if (matchedSubject && !isQuizRequest && !isQuizAction) {
      // Fire and forget - don't await to avoid blocking the message
      console.log(`📝 Soft review TRIGGERED for "${matchedSubject}" (action: ${action}, isQuizRequest: ${isQuizRequest})`);
      saveSoftReview(matchedSubject);
    } else if (matchedSubject) {
      console.log(`📝 Soft review SKIPPED for "${matchedSubject}" - isQuizRequest: ${isQuizRequest}, isQuizAction: ${isQuizAction}`);
    }

    logger.debug('🎯 Final matched topics:', allDetectedTopics.length > 0 ? allDetectedTopics : ['(no match - using foundational)']);

    const newUserMessage = { role: 'user', content: messageContent };
    let updatedMessages = [...messages, newUserMessage];

    // Update system prompt with the matched topic's hint level
    const currentSystemPrompt = updatedMessages[0]?.content || '';
    // Remove old adaptive hints section
    let newSystemPrompt = currentSystemPrompt.replace(/\n\n\[ADAPTIVE HINT LEVELS[\s\S]*$/, '');

    if (skipAdaptiveHints) {
      // HMM-triggered quiz: knowledge state is already in the message, skip hint injection
      console.log('🔮 HMM quiz request — skipping adaptive hint injection (HMM context in message)');
    } else if (matchedSubject && masteryResult.totalQuestions > 0) {
      // Found a match with history - generate specific hints (pass fresh masteryResult to avoid stale state)
      const relevantHintGuidance = generateAdaptiveHintGuidance(allDetectedTopics, masteryResult);
      newSystemPrompt += relevantHintGuidance;
      logger.debug('📝 System prompt updated with hints for:', matchedSubject);
      console.log(`🎯 Hint Level: ${masteryResult.hintLevel.toUpperCase()} for "${matchedSubject}" (${masteryResult.accuracy?.toFixed(0) || 0}% accuracy, ${masteryResult.totalQuestions} questions)`);
    } else {
      // No match or no history - add foundational guidance
      const foundationalGuidance = `

[ADAPTIVE HINT LEVELS - Based on Student History for Current Topic]
Adjust your teaching intensity based on the student's demonstrated performance:
- Current Topic: New/Unknown (no history data available)
  → FOUNDATIONAL hints: Explain concepts from the ground up, use simple analogies, and verify understanding with basic questions before moving on.

IMPORTANT: Since this appears to be a new topic or no quiz history exists, provide FOUNDATIONAL level guidance with extra scaffolding and step-by-step explanations.
`;
      newSystemPrompt += foundationalGuidance;
      console.log(`🎯 Hint Level: FOUNDATIONAL (no match or no quiz history)`);
    }

    // If quiz request and we have previous questions, add them to avoid repetition
    if (isQuizRequest && masteryResult.previousQuestions?.length > 0) {
      newSystemPrompt += `\n[PREVIOUSLY ASKED QUESTIONS - DO NOT REPEAT]\n${masteryResult.previousQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n')}\n`;
      console.log(`📝 Added ${masteryResult.previousQuestions.length} previous questions to avoid repetition`);
    }
    
    // 5-ATTEMPT HINT TRACKING (only in breakdown mode)
    // NOW INTEGRATED WITH STUDENT'S HISTORICAL PERFORMANCE!
    if (mode === 'breakdown') {
      // Analyze conversation to determine current step, phase, and attempt count
      const conversationState = analyzeConversationForAttempts(updatedMessages.slice(1)); // Skip system message
      
      // Update refs for tracking
      currentStepNumberRef.current = conversationState.stepNumber;
      currentPhaseRef.current = conversationState.phase;
      stepAttemptsRef.current = conversationState.attempts;
      
      // Get historical performance data from masteryResult
      const studentAccuracy = masteryResult.accuracy || 0;
      const studentHintLevel = masteryResult.hintLevel || 'moderate';
      const isFlipFlopping = masteryResult.isFlipFlopping || false;
      const trend = masteryResult.trend || 'stable';
      const totalQuestions = masteryResult.totalQuestions || 0;
      
      console.log(`🎯 5-Attempt Tracking: Step ${conversationState.stepNumber}, Phase: ${conversationState.phase}, Attempts: ${conversationState.attempts}`);
      console.log(`🎯 Student History: ${studentAccuracy.toFixed(0)}% accuracy, ${totalQuestions} questions, hint level: ${studentHintLevel}, flip-flopping: ${isFlipFlopping}, trend: ${trend}`);
      
      // Inject attempt guidance into system prompt
      if (conversationState.stepNumber > 0) {
        const attemptsRemaining = Math.max(0, 5 - conversationState.attempts);
        
        // ADAPTIVE HINT STYLE BASED ON STUDENT'S HISTORICAL PERFORMANCE
        let hintStyleGuidance = '';
        if (totalQuestions >= 3) {
          if (studentAccuracy >= 80) {
            hintStyleGuidance = `
🏆 STUDENT PERFORMANCE: HIGH MASTERY (${studentAccuracy.toFixed(0)}% accuracy on ${totalQuestions} questions)
- This student typically performs well - challenge them more!
- Give MINIMAL hints: Ask probing questions, let them figure it out
- Don't over-explain; they likely know the concepts already
- If they're stuck, it might be a momentary lapse - one hint should be enough`;
          } else if (studentAccuracy >= 50) {
            hintStyleGuidance = `
📚 STUDENT PERFORMANCE: LEARNING (${studentAccuracy.toFixed(0)}% accuracy on ${totalQuestions} questions)
- This student is still building skills - use MODERATE hints
- Provide partial explanations and guiding questions
- Check their understanding before moving forward
- If they make an error, explain WHY it's wrong`;
          } else {
            hintStyleGuidance = `
🆘 STUDENT PERFORMANCE: STRUGGLING (${studentAccuracy.toFixed(0)}% accuracy on ${totalQuestions} questions)
- This student needs extra support - use DETAILED hints!
- Break explanations into small, digestible steps
- Use analogies and concrete examples
- Verify understanding at each micro-step
- Be patient and encouraging`;
          }
          
          // Add special guidance for problematic patterns
          if (isFlipFlopping) {
            hintStyleGuidance += `

⚠️ FLIP-FLOPPING DETECTED: This student alternates between correct and wrong answers!
- Ask them to EXPLAIN their reasoning BEFORE you confirm/deny
- Don't just accept a correct answer - probe for understanding
- They may be guessing - verify they truly understand`;
          }
          
          if (trend === 'declining' || trend === 'slipping') {
            hintStyleGuidance += `

📉 DECLINING TREND: Student started well but is now struggling!
- They may have hit a conceptual wall
- Ask what's confusing them before giving more hints
- Consider revisiting earlier concepts they understood`;
          }
        } else {
          hintStyleGuidance = `
🆕 STUDENT PERFORMANCE: LIMITED DATA (${totalQuestions} questions answered)
- Not enough history to gauge performance level
- Use MODERATE hints as default
- Adjust based on how they respond in this session`;
        }
        
        const phaseSpecificGuidance = conversationState.phase === 'coding' 
          ? `
🔧 CODING PHASE RULES:
- Student is writing CODE for this step
- Each code submission = 1 attempt
- If code is PARTIAL but correct: Say "Good start! What else is needed?" (DO NOT mark complete!)
- If code has errors: Point out the issue, ask them to fix it
- Only mark [STEP_COMPLETE] when ALL code for the step is written
- DO NOT show the complete code until 5 attempts!
- 🚨 MANDATORY: When step is complete, use these EXACT tags at the END of your message:
[STEP_COMPLETE] Description of the step
[CODE_START]
# The Python code
[CODE_END]
`
          : `
📝 PLANNING PHASE RULES:
- Student is describing what the step should do
- Guide them to articulate the step clearly
- When description is finalized, transition to coding phase
`;
        
        const attemptGuidance = `

[STUDENT ATTEMPT TRACKING - CURRENT STATE]
📊 Current Step: ${conversationState.stepNumber}
📊 Current Phase: ${conversationState.phase.toUpperCase()}
📊 Student Attempts This Phase: ${conversationState.attempts} of 5
📊 Attempts Remaining Before Answer: ${attemptsRemaining}
${hintStyleGuidance}
${phaseSpecificGuidance}
🚨 HINT PROGRESSION RULES (YOU MUST FOLLOW THESE):
${conversationState.attempts < 5 ? `
- The student has made ${conversationState.attempts} attempt(s) so far.
- You MUST give HINTS ONLY, NOT the answer/complete code!
- Give progressively more specific hints as attempts increase:
  * Attempts 1-2: Very general hints (ask guiding questions)
  * Attempts 3-4: More specific hints (point toward the solution)
  * Attempt 5: Detailed hint (almost give away the answer but let them try one more time)
- After this attempt, ${attemptsRemaining} more attempts remain before you may provide the answer.
- DO NOT give the complete answer yet! Let the student keep trying!
- REMEMBER: Adjust hint DETAIL level based on student's historical performance above!
` : `
- The student has made 5+ attempts.
- You MAY now provide the answer if they are not making progress.
- However, if they ARE making progress (getting closer, showing understanding), give one more hint.
- Signs of progress: partial correct answers, using concepts you mentioned, asking clarifying questions
- Signs of no progress: repeating same mistakes, completely off track, expressing frustration
`}

REMEMBER: Your goal is to help them LEARN, not just give answers. Even if they ask "what's the answer?" or "just tell me", respond with a hint until attempt 5+.
`;
        newSystemPrompt += attemptGuidance;
      }
    }
    
    updatedMessages[0] = { role: 'system', content: newSystemPrompt };
    logger.debug('📝 [FINAL SYSTEM PROMPT FOR LLM]:', newSystemPrompt);

    // Sliding Window Logic - Preserves original problem context
    // We keep: system prompt (0), first user message (1), and most recent messages
    const MAX_MESSAGES_IN_HISTORY = 25; // Balanced for cost vs quality
    if (updatedMessages.length > MAX_MESSAGES_IN_HISTORY) {
      // Find the first user message (the original problem)
      const firstUserMessageIndex = updatedMessages.findIndex((msg, idx) => idx > 0 && msg.role === 'user');
      
      if (firstUserMessageIndex > 0) {
        // Keep: system prompt + first user message + recent messages
        const firstUserMessage = updatedMessages[firstUserMessageIndex];
        const recentMessages = updatedMessages.slice(updatedMessages.length - (MAX_MESSAGES_IN_HISTORY - 2));
        
        // Only add first user message if it's not already in recent messages
        if (!recentMessages.some(msg => msg === firstUserMessage)) {
          updatedMessages = [
            updatedMessages[0], // system prompt
            firstUserMessage,   // original problem (preserved!)
            ...recentMessages
          ];
          console.log('📌 Sliding window applied - preserved original problem context');
        } else {
          updatedMessages = [
            updatedMessages[0],
            ...recentMessages
          ];
        }
      } else {
        // Fallback: no user message found, just keep system + recent
        updatedMessages = [
          updatedMessages[0],
          ...updatedMessages.slice(updatedMessages.length - (MAX_MESSAGES_IN_HISTORY - 1))
        ];
      }
    }

    setMessages(updatedMessages);
    setLoading(true);
    
    // Force scroll to bottom immediately after adding user message
    // Use setTimeout to ensure React has re-rendered
    setTimeout(() => {
      if (historyRef.current) {
        historyRef.current.scrollTop = historyRef.current.scrollHeight + 200;
      }
    }, 50);

    // Log what we're sending to the LLM
    logger.debug('🤖 ========== SENDING TO LLM ==========');
    logger.debug('📝 Action:', action);
    logger.debug('📊 Mode:', mode);
    logger.debug('💬 User Message:', messageContent);
    logger.debug('📨 Total messages being sent:', updatedMessages.length);
    
    // Log the full system prompt
    const systemPrompt = updatedMessages[0]?.content || '';
    logger.debug('📋 -------- SYSTEM PROMPT --------');
    logger.debug(systemPrompt);
    logger.debug('📋 -------- END SYSTEM PROMPT --------');
    
    // Log adaptive hints section specifically
    const hintStart = systemPrompt.indexOf('[ADAPTIVE HINT LEVELS');
    if (hintStart !== -1) {
      // Extract everything from [ADAPTIVE HINT LEVELS to the end or next section
      const hintSection = systemPrompt.substring(hintStart, hintStart + 1500);
      logger.debug('🎯 -------- ADAPTIVE HINTS SECTION --------');
      logger.debug(hintSection);
      logger.debug('🎯 -------- END ADAPTIVE HINTS --------');
    } else {
      logger.debug('🎯 Adaptive Hints in System Prompt: NONE (not yet loaded)');
      logger.debug('📋 Current adaptiveHints state:', adaptiveHints);
    }

    // Log conversation history (excluding system prompt)
    logger.debug('💬 -------- CONVERSATION HISTORY --------');
    updatedMessages.slice(1).forEach((msg, idx) => {
      const role = msg.role === 'user' ? '👤 USER' : '🤖 ASSISTANT';
      const preview = msg.content?.substring(0, 200) + (msg.content?.length > 200 ? '...' : '');
      logger.debug(`${idx + 1}. ${role}: ${preview}`);
    });
    logger.debug('💬 -------- END CONVERSATION --------');
    logger.debug('🤖 ======================================');

    try {
      console.log('`🤖 LLM API: POST ${API_BASE_URL}/api/chat (chat)');
      const res = await apiFetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'gpt-4.1',
          // Always keep: [0] system prompt + [1] original problem + last 25 messages
          messages: updatedMessages.length > 27
            ? [updatedMessages[0], updatedMessages[1], ...updatedMessages.slice(-25)]
            : updatedMessages,
          temperature: 0.7,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        const assistantMessage = data.choices[0].message;
        logger.debug('📡 API RESPONSE [POST] OpenAI → Success');
        logger.debug('🤖 LLM Response Preview:', assistantMessage.content?.substring(0, 300) + '...');
        
        // Auto-inject PLAN tags if LLM finalized a step but forgot to include them
        // Get existing plan from messages
        let existingPlan = [];
        for (let i = messages.length - 1; i >= 0; i--) {
          const m = messages[i];
          const content = m.content || '';
          if (m.role === 'assistant' && content.includes('[PLAN_START]')) {
            let planText = content.split('[PLAN_START]')[1];
            if (planText.includes('[PLAN_END]')) {
              planText = planText.split('[PLAN_END]')[0];
            }
            existingPlan = planText.trim().split('\n')
              .map(s => s.trim())
              .filter(s => s.length > 0)
              .map(text => ({
                text: text.replace(/^\d+[\.\)\s]+/, '').replace(/\s*\(COMPLETE\)\s*/i, '').trim(),
                complete: text.toLowerCase().includes('complete')
              }));
            break;
          }
        }
        
        // FALLBACK: Ensure plan tags exist (if LLM forgot to include them)
        assistantMessage.content = await ensurePlanTags(assistantMessage.content, messageContent, existingPlan);
        
        // Check if response contains QUIZ_RESULT tag
        if (assistantMessage.content?.includes('[QUIZ_RESULT]')) {
          logger.debug('🎯 QUIZ_RESULT tag detected in LLM response!');
          // Log the full QUIZ_RESULT content for debugging
          const quizMatch = assistantMessage.content.match(/\[QUIZ_RESULT\]([\s\S]*?)\[\/QUIZ_RESULT\]/);
          if (quizMatch) {
            logger.debug('🎯 QUIZ_RESULT content:', quizMatch[1].trim());
            
            // POST to /api/quiz-result for optimized Past Quiz Review
            try {
              const quizContent = quizMatch[1];
              const subjectMatch = quizContent.match(/Subject:\s*([^\n]+)/i);
              const resultMatch = quizContent.match(/Result:\s*(correct|wrong)/i);
              const questionMatch = quizContent.match(/Question:\s*([\s\S]+?)(?=\[\/QUIZ_RESULT\]|$)/i);
              const feedback = assistantMessage.content.split('[QUIZ_RESULT]')[0].trim();
              
              if (subjectMatch && resultMatch) {
                const quizPayload = {
                  username: localStorage.getItem('username') || 'anonymous',
                  subject: subjectMatch[1].trim(),
                  result: resultMatch[1].trim().toLowerCase(),
                  question: questionMatch ? questionMatch[1].trim() : '',
                  user_answer: messageContent || '',
                  feedback: feedback
                };
                
                apiFetch(`${API_BASE_URL}/api/quiz-result`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(quizPayload)
                }).then(res => {
                  if (res.ok) {
                    console.log('📡 Quiz result saved to /api/quiz-result');
                    // Award quiz points only for correct
                    if (quizPayload.result === 'correct') {
                      apiFetch(`${API_BASE_URL}/api/points/award`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ username: quizPayload.username, actionType: 'QUIZ', subject: quizPayload.subject })
                      }).then(r => r.ok ? r.json() : null)
                        .then(d => { if (d) console.log(`🏆 Quiz points awarded: +${d.pointsAwarded} (total: ${d.total_points})`); })
                        .catch(e => console.warn('🏆 Quiz points error:', e));
                    }
                  } else {
                    console.error('📡 Failed to save quiz result:', res.status);
                  }
                }).catch(err => console.error('📡 Error saving quiz result:', err));
              }
            } catch (err) {
              console.error('📡 Error parsing QUIZ_RESULT for save:', err);
            }
          }
        } else {
          logger.debug('⚠️ No QUIZ_RESULT tag in LLM response - mastery will NOT be updated');
        }
        
        // Check if response contains INFORMAL_RESULT tag - if not, try to extract it via quick LLM call
        // Skip for 'Send Code to Tutor' — code submissions are handled by struggle detection only
        if (action !== 'Send Code to Tutor' &&
            !assistantMessage.content?.includes('[INFORMAL_RESULT]') && 
            !assistantMessage.content?.includes('[QUIZ_RESULT]')) {
          // Quick extraction call to detect if tutor evaluated student's answer
          await extractInformalResult(messageContent, assistantMessage.content);
        }
        
        // NOTE: Code extraction from [CODE_START]...[CODE_END] is now handled by 
        // the plan parsing useEffect which scans ALL messages and syncs to sandbox
        
        setMessages(prevMessages => [...prevMessages, assistantMessage]);
        logAction(action, messageContent, assistantMessage.content);
        
        // Background struggle detection when student sends code to tutor
        // Send conversation history WITHOUT the tutor's response (it can confuse the analysis)
        if (action === 'Send Code to Tutor' && pythonCode) {
          const currentTopic = plan.find(s => !s.complete)?.text || mode;
          // Build messages array with previous context + user's code submission (no assistant response)
          const messagesForAnalysis = [...messages, { role: 'user', content: messageContent }];
          // Fire and forget - don't await
          analyzeStruggleInBackground(username, currentTopic, pythonCode, messagesForAnalysis);
        }
      } else {
        const errorMsg = data.error?.message || 'Failed to get response from API.';
        console.error('📡 API ERROR [POST] OpenAI:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error(error);
      const errorMessage = { role: 'assistant', content: `Error: ${error.message}` };
      setMessages(prev => [...prev, errorMessage]);
      logAction(action, messageContent, `Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!prompt.trim()) {
      // In demo mode, allow empty submit to show the demo info
      if (localStorage.getItem('demoMode') === 'true') {
        await sendMessage('', 'Send to Tutor');
        return;
      }
      setMessages(prev => [...prev, { role: 'assistant', content: '💬 Please type a message before sending.' }]);
      return;
    }
    
    // Check if the message contains code (using imported utility function)
    if (detectCodeInText(prompt, logger)) {
      setPendingCodeMessage(prompt);
      setShowCodeDetectedPopup(true);
      return; // Don't send yet, wait for user decision
    }
    
    const messageToSend = prompt;
    setPrompt('');
    await sendMessage(messageToSend);
  };

  // Handle user confirming to send code to LLM anyway
  const handleSendCodeAnyway = async () => {
    setShowCodeDetectedPopup(false);
    const messageToSend = pendingCodeMessage;
    setPrompt('');
    setPendingCodeMessage('');
    await sendMessage(messageToSend);
  };

  const handleNeedHelp = async () => {
    // In demo mode, show an explanation of what the hint button does
    if (isDemoMode()) {
      const demoHintInfo = "💡 **What the Hint button does:**\n\nWhen you're stuck, click **Hint** and the tutor will give you a nudge tailored to your skill level — without giving away the answer.\n\n🧠 **Adaptive hints:** The hint level (foundational → moderate → advanced) is automatically adjusted based on your quiz history and past chat performance on that topic. A student who struggles gets more scaffolding; a strong student gets a lighter nudge.\n\n🔒 This feature is locked in demo mode. Log in with a class account to use it.";
      setMessages(prev => [...prev, 
        { role: 'user', content: 'Need help' },
        { role: 'assistant', content: demoHintInfo }
      ]);
      return;
    }

    // Find the previous context (last user message or assistant message about a topic)
    // to use for LLM matching instead of "Need help"
    let contextForMatching = null;
    
    // Look through messages in reverse to find the last meaningful context
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'user' && msg.content && msg.content !== 'Need help') {
        // Found a previous user message - use it as context
        contextForMatching = msg.content;
        logger.debug('🔍 Help button: Using previous user message for matching:', contextForMatching.substring(0, 100) + '...');
        break;
      } else if (msg.role === 'assistant' && msg.content) {
        // Check if assistant message contains topic-related content
        // Look for QUIZ_RESULT tags or topic mentions
        const quizMatch = msg.content.match(/Subject:\s*([^\n]+)/);
        if (quizMatch) {
          contextForMatching = quizMatch[1].trim();
          logger.debug('🔍 Help button: Using subject from assistant quiz:', contextForMatching);
          break;
        }
      }
    }
    
    if (!contextForMatching) {
      logger.debug('🔍 Help button: No previous context found, will use default matching');
    }
    
    await sendMessage("Need help", "Need Help", contextForMatching);
  };

  const handleNewProblem = () => {
    console.log('🆕 User clicked New Problem button - FULL RESET');
    
    // Reset all plan and code state
    setPlan([]);
    setPythonCode("");
    setRunOutput('');
    
    // Reset all refs - including prevPlanRef to prevent old code from being preserved
    syncedStepsRef.current.clear();
    previousStep1Ref.current = null;
    prevPlanRef.current = []; // CRITICAL: Clear previous plan code preservation
    stepAttemptsRef.current = 0;
    currentStepNumberRef.current = 0;
    currentPhaseRef.current = 'planning';
    
    // COMPLETE conversation reset - keep ONLY a fresh system message
    // This ensures NO old context is passed to the LLM
    setMessages(prev => {
      if (prev.length > 0 && prev[0].role === 'system') {
        // Keep only the system message with fresh content
        return [{ role: 'system', content: prev[0].content }];
      }
      return [];
    });
    
    // Restore the textarea hint for the new problem
    localStorage.removeItem('promptHintDismissed');
    setShowPromptHint(true);

    // Log the action
    logAction('New Problem', '', '');
    
    console.log('🆕 Full reset complete - conversation history cleared, ready for new problem');
  };

  const handleSendCodeToTutor = async () => {
    if (!pythonCode.trim()) return;
    if (isDemoMode()) {
      setMessages(prev => [...prev, { role: 'assistant', content: '🔒 Code auditing is locked in demo mode.\n\nWith an account you can have the AI tutor review your Python code line by line, catch mistakes, and guide you to the solution.\n\n✨ Sign up to unlock!' }]);
      return;
    }
    if (pythonCode.trim()) {
      await sendMessage(
        `Please audit my code against our [CURRICULUM LOCKDOWN] rules. This is my completed code for the current step.

IMPORTANT: If this code correctly implements the current step, you MUST:
1. Confirm the code is correct
2. Include these EXACT tags at the END of your response:

[PLAN_START]
1. Description of step 1 (complete)
[PLAN_END]

[STEP_COMPLETE] Description of the completed step
[CODE_START]
the correct code here
[CODE_END]

3. Then ASK: "What do you think the next step should be?"

Without these tags, the step will NOT appear in the Program Steps panel!

Here's my code:
\`\`\`python
${pythonCode}
\`\`\``, 
        "Send Code to Tutor"
      );
    }
  };

  const handleQuizCurrent = async () => {
    // Find the current topic from recent conversation
    let currentTopic = null;
    
    // Check last 2 assistant messages for a Subject tag (from recent quiz results)
    for (let i = messages.length - 1; i >= 0 && i >= messages.length - 2; i--) {
      const msg = messages[i];
      if (msg.role === 'assistant' && msg.content) {
        const subjectMatch = msg.content.match(/Subject:\s*([^\n]+)/);
        if (subjectMatch) {
          currentTopic = subjectMatch[1].trim();
          logger.debug('🔍 Quiz Current: Found topic from Subject tag:', currentTopic);
          break;
        }
      }
    }
    
    // Fallback: Try to get topic from the plan (current incomplete step)
    if (!currentTopic && plan.length > 0) {
      const currentStep = plan.find(step => !step.complete);
      if (currentStep) {
        currentTopic = currentStep.text;
        logger.debug('🔍 Quiz Current: Found topic from plan step:', currentTopic);
      }
    }
    
    // Send the quiz request with or without specific topic
    if (currentTopic) {
      await sendMessage(`Please give me a multiple choice question about "${currentTopic}".`, "Quiz Me (Current)");
    } else {
      await sendMessage("Please give me a multiple choice question about the building block we are currently discussing.", "Quiz Me (Current)");
    }
  };

  const handleQuizRandom = async () => {
    // Get the building blocks from the plan
    const topics = plan.map(step => step.text).filter(t => t && t.length > 0);
    
    if (topics.length > 0) {
      const topicList = topics.join(', ');
      console.log('🎲 Quiz Random: Using topics from plan:', topicList);
      await sendMessage(`Please give me a multiple choice question about one of these building blocks: ${topicList}. Pick one randomly.`, "Quiz Me (Random)");
    } else {
      await sendMessage("Please give me a multiple choice question about a random building block needed for this problem.", "Quiz Me (Random)");
    }
  };

  const handleContinuePractice = async () => {
    // Find the previous context for LLM matching
    let contextForMatching = null;
    
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role === 'user' && msg.content && msg.content !== "Let's continue practicing") {
        contextForMatching = msg.content;
        logger.debug('🔍 Continue Practice: Using previous user message for matching:', contextForMatching.substring(0, 100) + '...');
        break;
      } else if (msg.role === 'assistant' && msg.content) {
        const quizMatch = msg.content.match(/Subject:\s*([^\n]+)/);
        if (quizMatch) {
          contextForMatching = quizMatch[1].trim();
          logger.debug('🔍 Continue Practice: Using subject from assistant quiz:', contextForMatching);
          break;
        }
      }
    }
    
    await sendMessage("Let's continue practicing the building blocks for this problem.", "Continue Practice", contextForMatching);
  };

  // Download code as Python file
  const handleDownloadCode = () => {
    if (!pythonCode.trim()) {
      alert('No code to download!');
      return;
    }
    setDownloadFilename('code');
    setShowDownloadModal(true);
  };

  // Confirm download with filename
  const confirmDownload = () => {
    const cleanFilename = downloadFilename.trim() || 'code';
    const finalFilename = cleanFilename.endsWith('.py') ? cleanFilename : `${cleanFilename}.py`;
    
    logAction('Download Code', pythonCode, finalFilename);
    const blob = new Blob([pythonCode], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = finalFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setShowDownloadModal(false);
  };

  const handleRunTrace = async (src, inputs) => {
    setIsTracing(true);
    setShowInputModal(false);
    try {
      const res = await fetch(`${PYTHON_INTERPRETER_URL}/trace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
        body: JSON.stringify({ code: src, inputs })
      });
      const data = await res.json();
      if (data.steps && data.steps.length > 0) {
        setTraceSteps(data.steps);
        setShowVisualizer(true);
      } else {
        setRunOutput('⚠️ Visualizer: no executable steps found. Make sure your code has at least one statement.');
      }
    } catch (err) {
      setRunOutput(`⚠️ Visualizer error: ${err.message}`);
    } finally {
      setIsTracing(false);
    }
  };

  const handleRunCode = async (e) => {
    e.preventDefault();
    if (runLoading) return;

    // Demo mode: code execution disabled
    if (isDemoMode()) {
      setRunOutput("🔒 Code execution is locked in demo mode.\n\nWith an account you can run Python live in this sandbox.\n\nExample:\n  >>> print(\"Hello, World!\")\n  Hello, World!\n\n  >>> for i in range(3):\n  ...     print(i)\n  0\n  1\n  2\n\nThe sandbox handles input(), loops, and errors — and the AI tutor can review your code line by line.\n\n✨ Sign up to run Python live!");
      return;
    }
    
    logAction('Run Code', pythonCode, '');
    setRunLoading(true);
    setRunOutput('');
    setIsAwaitingInput(false);
    setExecutionId(null);
    setLastPrompt('');
    serverStdoutRef.current = 0;

    try {
      const res = await fetch(`${PYTHON_INTERPRETER_URL}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}),
        },
        body: JSON.stringify({
          code: pythonCode,
        }),
      });

      const data = await res.json();
      const stdout = data.stdout || '';
      
      setExecutionId(data.execution_id);
      setRunOutput(stdout);
      serverStdoutRef.current = stdout.length;

      if (data.execution_id) {
        setIsAwaitingInput(true);
        setPromptEndIndex(stdout.length);
        const lines = stdout.split('\n').filter(l => l.trim().length > 0);
        const detectedPrompt = lines.length > 0 ? lines[lines.length - 1] : '';
        setLastPrompt(detectedPrompt);
      } else {
        const stderr = data.stderr || '';
        if (stderr) {
          setRunOutput(prev => prev + `\nError:\n${stderr}`);
        }
      }
    } catch (error) {
      console.error('Failed to run code:', error);
      setRunOutput(`Failed to connect to the code execution server. Is it running on port 6001?`);
    } finally {
      setRunLoading(false);
    }
  };

  const handleResumeExecution = async (userInput) => {
    if (isDemoMode()) return;
    setRunLoading(true);
    setIsAwaitingInput(false);

    try {
      const res = await fetch(`${PYTHON_INTERPRETER_URL}/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}),
        },
        body: JSON.stringify({
          execution_id: executionId,
          input: userInput,
        }),
      });

      const data = await res.json();
      logger.debug("Response from /resume:", data); // <-- ADDING THIS LINE
      const newStdout = data.stdout || '';
      
      setExecutionId(data.execution_id);

      // The server's stdout is the absolute source of truth.
      // It contains the prompts, the echoed inputs, and all print statements.
      // By replacing the entire output with the server's version, we ensure
      // perfect formatting and no doubled characters.
      setRunOutput(newStdout);
      setPromptEndIndex(newStdout.length);

      if (data.execution_id) {
        setIsAwaitingInput(true);
        const lines = newStdout.split('\n').filter(l => l.trim().length > 0);
        const nextPrompt = lines.length > 0 ? lines[lines.length - 1] : '';
        setLastPrompt(nextPrompt);
      } else {
        const stderr = data.stderr || '';
        if (stderr) {
          setRunOutput(prev => prev + `\nError:\n${stderr}`);
        }
        setExecutionId(null);
        setLastPrompt('');
      }
    } catch (error) {
      console.error('Failed to resume code execution:', error);
      setRunOutput(prev => prev + `\nFailed to connect to the code execution server.`);
      setExecutionId(null);
    } finally {
      setRunLoading(false);
    }
  };

  const handleTerminateCode = async () => {
    logAction('Terminate', '', '');
    if (!executionId) {
      setIsAwaitingInput(false);
      setRunLoading(false);
      return;
    }
    
    setRunLoading(true);
    
    try {
      await fetch(`${PYTHON_INTERPRETER_URL}/terminate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
        body: JSON.stringify({ execution_id: executionId }),
      });
    } catch (error) {
      console.error('Failed to terminate execution:', error);
    } finally {
      setRunOutput(prev => prev + '\n[Execution Terminated]');
      setIsAwaitingInput(false);
      setExecutionId(null);
      setLastPrompt('');
      setRunLoading(false);
      serverStdoutRef.current = 0;
    }
  };

  const handleOutputKeyDown = (e) => {
    if (isAwaitingInput && e.key === 'Enter') {
      e.preventDefault();
      const userInput = e.target.value.substring(promptEndIndex);
      
      // We don't append anything locally. 
      // handleResumeExecution will replace the whole output with the server's truth.
      handleResumeExecution(userInput.replace(/\n$/, ''));
    }
  };

  // Effect to auto-scroll history to bottom with extra padding for visibility
  useEffect(() => {
    const scrollToBottom = () => {
      if (historyRef.current) {
        // First scroll to max height
        historyRef.current.scrollTop = historyRef.current.scrollHeight;
        
        // Then add a small delay to ensure rendering is complete and scroll a bit more
        setTimeout(() => {
          if (historyRef.current) {
            // Scroll past the bottom by 200px to ensure latest message is clearly visible
            historyRef.current.scrollTop = historyRef.current.scrollHeight + 200;
          }
        }, 50);
      }
    };
    // Small timeout to ensure DOM has updated
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [messages, loading]); // Also scroll when loading changes (shows/hides "Thinking..." indicator)

  // Effect to parse messages and update the plan (WITH CODE TRACKING like v9)
  useEffect(() => {
    logger.debug('📋 Plan useEffect triggered, messages.length:', messages.length);
    
    if (messages.length <= 1) {
      logger.debug('📋 Only system message present - clearing plan');
      setPlan([]);
      return;
    }

    // NOTE: No auto-detection of "new problem" - user must click the New Problem button
    // This avoids fragile phrase detection and accidental data loss

    // 1. Find the most recent plan in history
    let latestPlan = [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      const content = m.content || '';
      if (m.role === 'assistant' && content.includes('[PLAN_START]')) {
        let planText = content.split('[PLAN_START]')[1];
        if (planText.includes('[PLAN_END]')) {
          planText = planText.split('[PLAN_END]')[0];
        }
        latestPlan = planText.trim().split('\n')
          .map(s => s.trim())
          .filter(s => s.length > 0)
          .map(text => ({
            text,
            completed: text.toLowerCase().includes('complete') || text.includes('✅') || text.toLowerCase().includes('done'),
            code: null
          }));
        break;
      }
    }

    // 2. Extract all completed codes from the ENTIRE message history
    const completedCodesMap = new Map(); // cleanText -> code
    messages.forEach(m => {
      const content = m.content || '';
      if (m.role === 'assistant') {
        // Look for [STEP_COMPLETE] tags followed by [CODE_START]
        if (content.includes('[STEP_COMPLETE]')) {
          const parts = content.split('[STEP_COMPLETE]').slice(1);
          parts.forEach(part => {
            if (part.includes('[CODE_START]')) {
              const stepText = part.split('[CODE_START]')[0].trim();
              let code = part.split('[CODE_START]')[1];
              if (code.includes('[CODE_END]')) {
                code = code.split('[CODE_END]')[0];
              }
              code = code.trim();
              const clean = stepText.replace(/^\d+[\.\)\s]+/, '').toLowerCase().trim();
              if (clean) completedCodesMap.set(clean, code);
            }
          });
        }

        // Fallback: If [STEP_COMPLETE] is missing but [CODE_START] is there
        if (content.includes('[CODE_START]') && !content.includes('[STEP_COMPLETE]')) {
          let code = content.split('[CODE_START]')[1];
          if (code.includes('[CODE_END]')) {
            code = code.split('[CODE_END]')[0];
          }
          code = code.trim();
          
          // Try to find the step text in the preceding lines
          const linesBefore = content.split('[CODE_START]')[0].trim().split('\n');
          for (let i = linesBefore.length - 1; i >= 0; i--) {
            const line = linesBefore[i].trim();
            const clean = line.replace(/^\d+[\.\)\s]+/, '').toLowerCase().trim();
            // Look for a line that isn't just a number or very short
            if (clean && clean.length > 5) {
              completedCodesMap.set(clean, code);
              break;
            }
          }
        }
      }
    });

    // 3. Merge codes into the plan using fuzzy matching
    const finalPlan = latestPlan.map(step => {
      const cleanStep = step.text
        .replace(/^\d+[\.\)\s]+/, '')
        .replace(/\(complete\)|\[complete\]|✅|done/gi, '')
        .toLowerCase()
        .trim();
      
      let code = null;
      // Try exact match first
      if (completedCodesMap.has(cleanStep)) {
        code = completedCodesMap.get(cleanStep);
      } else {
        // Try fuzzy match
        const normalizedCleanStep = cleanStep.replace(/[^\w\s]/g, '').trim();
        for (let [text, c] of completedCodesMap.entries()) {
          const normalizedText = text.replace(/[^\w\s]/g, '').trim();
          
          // Match if one contains the other, or if they share a significant prefix (first 15 chars)
          if (normalizedCleanStep.includes(normalizedText) || 
              normalizedText.includes(normalizedCleanStep) || 
              (normalizedCleanStep.length > 15 && normalizedText.length > 15 && 
               normalizedCleanStep.substring(0, 15) === normalizedText.substring(0, 15))) {
            code = c;
            break;
          }
        }
      }
      return { 
        ...step, 
        code: code, 
        completed: step.completed || !!code 
      };
    });

    // Only update if the plan has actually changed to avoid render loops
    setPlan(prevPlan => {
      if (JSON.stringify(prevPlan) !== JSON.stringify(finalPlan)) {
        logger.debug('📋 Plan updated with codes:', finalPlan.map(s => ({ text: s.text.substring(0, 30), hasCode: !!s.code })));
        return finalPlan;
      }
      return prevPlan;
    });
  }, [messages]);

  // Seed sessionProgress from masteryAggregate when entering practice mode
  useEffect(() => {
    if (mode === 'building-blocks') {
      // Seed with existing aggregate data from DB
      if (Object.keys(masteryAggregate).length > 0 && Object.keys(sessionProgress).length === 0) {
        setSessionProgress({ ...masteryAggregate });
        // Also seed the lastSyncedDataRef so we don't double-count
        Object.entries(masteryAggregate).forEach(([subject, data]) => {
          lastSyncedDataRef.current[subject] = { correct: data.correct, wrong: data.wrong };
        });
        // Capture the INITIAL mastery at session start (this won't change during session)
        if (Object.keys(sessionStartMasteryRef.current).length === 0) {
          sessionStartMasteryRef.current = JSON.parse(JSON.stringify(masteryAggregate));
          logger.debug('📊 Session start mastery captured:', sessionStartMasteryRef.current);
        }
      }
    }
  }, [mode, masteryAggregate]);

  // Effect to parse building block progress
  useEffect(() => {
    if (mode !== 'building-blocks' || messages.length === 0) return;

    // Find the most recent message with progress tags in the history
    let progressContent = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && messages[i].content?.includes('[PROGRESS_START]')) {
        progressContent = messages[i].content;
        break;
      }
    }
    
    if (progressContent) {
      // Prevent duplicate syncs for the exact same message content
      if (lastSyncedMessageRef.current === progressContent) return;
      lastSyncedMessageRef.current = progressContent;

      let progressText = progressContent.split('[PROGRESS_START]')[1];
      if (progressText.includes('[PROGRESS_END]')) {
        progressText = progressText.split('[PROGRESS_END]')[0];
      }
      
      const lines = progressText.trim().split('\n');
      const newProgress = {};
      
      // List of placeholder/invalid subject names to ignore
      const invalidSubjects = ['subject name', 'another subject', 'topic name', 'example subject'];
      
      logger.debug('📝 Parsing PROGRESS_START content:');
      lines.forEach(line => {
        // Robust regex to capture "Subject: Correct: X, Wrong: Y" or "- Subject: Correct: X, Wrong: Y"
        const match = line.match(/(?:^|[\-\*]\s*)(.+?):\s*Correct:\s*(\d+),\s*Wrong:\s*(\d+)/i);
        if (match) {
          const subject = match[1].trim();
          const correct = parseInt(match[2]);
          const wrong = parseInt(match[3]);
          
          // Skip placeholder subject names
          if (invalidSubjects.includes(subject.toLowerCase())) {
            console.log(`   → SKIPPED placeholder: "${subject}"`);
            return;
          }
          
          newProgress[subject] = { correct, wrong };
          console.log(`   → ${subject}: Correct=${correct}, Wrong=${wrong}`);
        }
      });
      logger.debug('📝 Total subjects parsed:', Object.keys(newProgress));

      if (Object.keys(newProgress).length > 0) {
        // Update tracking ref with current values (for delta calculation if needed later)
        Object.entries(newProgress).forEach(([subject, data]) => {
          lastSyncedDataRef.current[subject] = { correct: data.correct, wrong: data.wrong };
        });

        // Update UI state with the tutor's reported values
        // The tutor reports SESSION totals (not cumulative lifetime totals)
        // sessionProgress should track: DB base (at session start) + session additions
        setSessionProgress(prev => {
          const updated = { ...prev };
          Object.entries(newProgress).forEach(([subject, data]) => {
            // Get the DB base at SESSION START (not current masteryAggregate which updates during session)
            const baseFromDB = sessionStartMasteryRef.current[subject] || { correct: 0, wrong: 0 };
            
            // The tutor reports SESSION totals (e.g., "Correct: 2" means 2 correct this session)
            // sessionProgress = DB base (at start) + session totals from tutor
            updated[subject] = {
              correct: baseFromDB.correct + data.correct,
              wrong: baseFromDB.wrong + data.wrong
            };
            
            console.log(`📊 Progress update for "${subject}": SessionStart(${baseFromDB.correct}✓ ${baseFromDB.wrong}✗) + TutorSession(${data.correct}✓ ${data.wrong}✗) = Chart(${updated[subject].correct}✓ ${updated[subject].wrong}✗)`);
          });
          return updated;
        });

        // NOTE: PROGRESS_START is UI-only - it does NOT sync to DB
        // DB writes are handled exclusively by QUIZ_RESULT handler (one write per quiz answer)
        // This prevents duplicate DB entries
        console.log('📊 PROGRESS_START: UI updated (no DB sync - QUIZ_RESULT handles DB writes)');
      }
    }
  }, [messages, mode, masteryAggregate]);

  // Effect to parse QUIZ_RESULT tags from any mode and sync to mastery APIs
  // QUIZ_RESULT is the source of truth for DB writes (one write per quiz answer)
  // PROGRESS_START only updates UI chart, does NOT write to DB
  const lastSyncedQuizResultRef = useRef(new Set()); // Track synced QUIZ_RESULT content hashes
  
  useEffect(() => {
    console.log('🔴 [DEBUG] QUIZ_RESULT useEffect TRIGGERED - messages.length:', messages.length, 'mode:', mode);
    console.log('🔴 [DEBUG] Current lastSyncedQuizResultRef size:', lastSyncedQuizResultRef.current.size);
    console.log('🔴 [DEBUG] Current lastSyncedQuizResultRef keys:', Array.from(lastSyncedQuizResultRef.current).map(k => k.substring(0, 50) + '...'));
    
    if (messages.length === 0) {
      console.log('🔴 [DEBUG] QUIZ_RESULT: Early exit - no messages');
      return;
    }

    // Find the most recent assistant message with QUIZ_RESULT tag
    let quizResultMessage = null;
    let quizResultMessageIndex = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && messages[i].content?.includes('[QUIZ_RESULT]')) {
        quizResultMessage = messages[i].content;
        quizResultMessageIndex = i;
        break;
      }
    }

    if (!quizResultMessage) {
      console.log('🔴 [DEBUG] QUIZ_RESULT: No QUIZ_RESULT tag found in any message');
      return;
    }
    
    console.log('🔴 [DEBUG] QUIZ_RESULT: Found QUIZ_RESULT in message index:', quizResultMessageIndex);
    console.log('🔴 [DEBUG] QUIZ_RESULT: Message preview:', quizResultMessage.substring(0, 200));
    
    // Count how many QUIZ_RESULT tags are in this message
    const quizResultCount = (quizResultMessage.match(/\[QUIZ_RESULT\]/g) || []).length;
    console.log('🔴 [DEBUG] QUIZ_RESULT: Number of QUIZ_RESULT tags in message:', quizResultCount);

    // Parse the QUIZ_RESULT tag
    const quizMatch = quizResultMessage.match(/\[QUIZ_RESULT\]([\s\S]*?)\[\/QUIZ_RESULT\]/);
    if (!quizMatch) {
      console.log('🔴 [DEBUG] QUIZ_RESULT: Tag found but regex match failed');
      return;
    }

    const quizContent = quizMatch[1];
    console.log('🔴 [DEBUG] QUIZ_RESULT: Parsed content:', quizContent);
    
    const subjectMatch = quizContent.match(/Subject:\s*(.+)/i);
    const resultMatch = quizContent.match(/Result:\s*(correct|wrong)/i);
    // Extract question from QUIZ_RESULT tag (same approach as StruggleMomentsDashboard)
    const questionMatch = quizContent.match(/Question:\s*([\s\S]+?)(?=\[\/QUIZ_RESULT\]|$)/i);

    if (!subjectMatch || !resultMatch) {
      console.warn('⚠️ QUIZ_RESULT tag found but could not parse subject or result');
      console.log('🔴 [DEBUG] subjectMatch:', subjectMatch, 'resultMatch:', resultMatch);
      return;
    }

    const rawSubject = subjectMatch[1].trim();
    const result = resultMatch[1].toLowerCase();

    // Guard: reject date strings that the LLM occasionally emits as subject (e.g. "2026-09-12")
    if (/^\d{4}-\d{2}-\d{2}$/.test(rawSubject)) {
      console.warn('⚠️ QUIZ_RESULT: subject looks like a date, skipping save:', rawSubject);
      return;
    }
    // Extract question text (clean up any trailing whitespace)
    const question = questionMatch ? questionMatch[1].trim() : '';
    
    // Get student's answer from the previous user message (same approach as StruggleMomentsDashboard)
    let studentAnswer = '';
    if (quizResultMessageIndex > 0) {
      const prevMessage = messages[quizResultMessageIndex - 1];
      if (prevMessage && prevMessage.role === 'user') {
        studentAnswer = prevMessage.content || '';
      }
    }

    // Normalize subject against student's existing subjects (same as informal Q&A path)
    normalizeSubjectWithLLM(rawSubject, question).then(normalizedSubject => {
    const subject = normalizedSubject;
    console.log(`🎯 QUIZ_RESULT: subject normalized "${rawSubject}" → "${subject}"`);

    console.log('🔴 [DEBUG] QUIZ_RESULT: Parsed subject:', subject, 'result:', result);
    console.log('🔴 [DEBUG] QUIZ_RESULT: Question:', question.substring(0, 100) + '...');
    console.log('🔴 [DEBUG] QUIZ_RESULT: Student answer:', studentAnswer.substring(0, 100));
    
    // Create a unique key based on the QUIZ_RESULT content AND the message context
    // Include a snippet of the message to make it unique per response
    const messageSnippet = quizResultMessage.substring(0, 100);
    const syncKey = `${subject}-${result}-${messageSnippet}`;
    
    console.log('🔴 [DEBUG] QUIZ_RESULT: Generated syncKey:', syncKey.substring(0, 80) + '...');
    console.log('🔴 [DEBUG] QUIZ_RESULT: Checking if already synced...');
    
    // Prevent duplicate syncs for the same quiz result
    if (lastSyncedQuizResultRef.current.has(syncKey)) {
      console.log('🔴 [DEBUG] QUIZ_RESULT: ⚠️ DUPLICATE DETECTED - syncKey already exists, SKIPPING');
      return;
    }
    
    console.log('🔴 [DEBUG] QUIZ_RESULT: ✅ NEW syncKey - adding to ref and proceeding with sync');
    lastSyncedQuizResultRef.current.add(syncKey);
    
    // Limit the set size to prevent memory issues
    if (lastSyncedQuizResultRef.current.size > 100) {
      console.log('🔴 [DEBUG] QUIZ_RESULT: Trimming ref size from', lastSyncedQuizResultRef.current.size);
      const entries = Array.from(lastSyncedQuizResultRef.current);
      lastSyncedQuizResultRef.current = new Set(entries.slice(-50));
    }

    const isCorrect = result === 'correct';
    const username = localStorage.getItem('username') || 'anonymous';

    console.log(`🎯 QUIZ_RESULT detected: ${subject} = ${isCorrect ? 'CORRECT ✓' : 'WRONG ✗'}`);
    console.log('🔴 [DEBUG] QUIZ_RESULT: About to POST to both APIs...');

    const payload = {
      username,
      subject,
      correct: isCorrect ? 1 : 0,
      wrong: isCorrect ? 0 : 1,
      recorded_at: getESTTimestamp(),
      question: question,
      answer: studentAnswer
    };

    console.log('🔴 [DEBUG] QUIZ_RESULT: Payload:', JSON.stringify(payload));

    // Sync to both APIs
    console.log('🔴 [DEBUG] QUIZ_RESULT: Calling /api/studentMastery...');
    apiFetch(`${API_BASE_URL}/api/studentMastery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, subject, correct: isCorrect ? 1 : 0, wrong: isCorrect ? 0 : 1, record_at: getESTTimestamp() })
    })
      .then(() => console.log(`📡 API RESPONSE [POST] /api/studentMastery → Success for ${subject}`))
      .catch(err => console.error('📡 API ERROR [POST] /api/studentMastery:', err));

    console.log('🔴 [DEBUG] QUIZ_RESULT: Calling /api/studentMasteryHistoryQuestionAnswer...');
    apiFetch(`${API_BASE_URL}/api/studentMasteryHistoryQuestionAnswer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(() => {
        console.log(`📡 API RESPONSE [POST] /api/studentMasteryHistoryQuestionAnswer → Success for ${subject}`);
        console.log('🔴 [DEBUG] QUIZ_RESULT: Will call fetchMasteryAnalysis in 200ms...');
        // Refresh mastery data after saving
        setTimeout(() => {
          console.log('🔴 [DEBUG] QUIZ_RESULT: Now calling fetchMasteryAnalysis...');
          fetchMasteryAnalysis();
        }, 200);
      })
      .catch(err => console.error('📡 API ERROR [POST] /api/studentMasteryHistoryQuestionAnswer:', err));

    console.log('🔴 [DEBUG] QUIZ_RESULT: useEffect completed (API calls are async)');

    }); // end normalizeSubjectWithLLM.then()
  }, [messages, mode]);

  // Effect to parse INFORMAL_RESULT tags and save to question_answered table
  // These are short Q&A during coding practice (not formal quizzes)
  const lastSyncedInformalResultRef = useRef(new Set());
  
  useEffect(() => {
    if (messages.length === 0) return;

    // Find the most recent assistant message with INFORMAL_RESULT tag
    let informalResultMessage = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant' && messages[i].content?.includes('[INFORMAL_RESULT]')) {
        informalResultMessage = messages[i].content;
        break;
      }
    }

    if (!informalResultMessage) return;

    // Parse the INFORMAL_RESULT tag
    const informalMatch = informalResultMessage.match(/\[INFORMAL_RESULT\]([\s\S]*?)\[\/INFORMAL_RESULT\]/);
    if (!informalMatch) return;

    const content = informalMatch[1];
    const subjectMatch = content.match(/Subject:\s*(.+)/i);
    const resultMatch = content.match(/Result:\s*(correct|wrong)/i);

    if (!subjectMatch || !resultMatch) {
      console.warn('⚠️ INFORMAL_RESULT tag found but could not parse subject or result');
      return;
    }

    const rawSubject = subjectMatch[1].trim();
    const result = resultMatch[1].toLowerCase();
    
    // Create sync key to prevent duplicate processing
    const messageSnippet = informalResultMessage.substring(0, 100);
    const syncKey = `informal-${rawSubject}-${result}-${messageSnippet}`;
    
    if (lastSyncedInformalResultRef.current.has(syncKey)) {
      return;
    }
    lastSyncedInformalResultRef.current.add(syncKey);
    
    if (lastSyncedInformalResultRef.current.size > 100) {
      const entries = Array.from(lastSyncedInformalResultRef.current);
      lastSyncedInformalResultRef.current = new Set(entries.slice(-50));
    }

    // Process informal result with normalization (async)
    const processInformalResult = async () => {
      // NORMALIZE the subject before saving
      const normalizedSubject = await normalizeSubjectWithLLM(rawSubject);
      console.log(`📝 INFORMAL_RESULT: Raw="${rawSubject}" → Normalized="${normalizedSubject}"`);
      
      const username = localStorage.getItem('username') || 'anonymous';
      const isCorrect = result === 'correct';

      console.log(`📝 INFORMAL_RESULT detected: ${normalizedSubject} = ${isCorrect ? 'CORRECT ✓' : 'WRONG ✗'}`);

      // Save to question_answered table
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/studentQuestionAnswered`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            subject: normalizedSubject,
            result: result
          })
        });
        
        if (res.ok) {
          console.log(`📝 ✅ Informal result saved for "${normalizedSubject}"`);
          // Update questionAnsweredSubjects if this is a new subject
          if (!questionAnsweredSubjects.includes(normalizedSubject)) {
            setQuestionAnsweredSubjects(prev => [...prev, normalizedSubject]);
          }
          // Award points only for correct answers
          if (isCorrect) {
            apiFetch(`${API_BASE_URL}/api/points/award`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ username, actionType: 'ANSWER', subject: normalizedSubject })
            }).then(r => r.ok ? r.json() : null)
              .then(d => { if (d) console.log(`🏆 Answer points awarded: +${d.pointsAwarded} (total: ${d.total_points})`); })
              .catch(e => console.warn('🏆 Answer points error:', e));
          }
        } else {
          console.log(`📝 ⚠️ questionAnswered API returned ${res.status}`);
        }
      } catch (err) {
        console.error('📝 ❌ Failed to save informal result:', err);
      }
    };

    processInformalResult();
    
  }, [messages, masteryAggregate, questionAnsweredSubjects]);

  // NOTE: Pattern-matching fallback disabled - now using extractInformalResult() with LLM
  // The LLM-based extraction is called directly after API response in sendMessage()
  // This is more accurate and handles edge cases better than regex patterns
  
  /* DISABLED - using LLM extraction instead
  // FALLBACK: Auto-detect informal results when LLM forgets to include the tag
  // This parses the LLM response for evaluation phrases and the previous user message for subject hints
  const lastAutoDetectedRef = useRef(new Set());
  
  useEffect(() => {
    if (messages.length < 3) return; // Need at least: user question, user answer, assistant evaluation
    
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role !== 'assistant') return;
    
    const content = lastMessage.content || '';
    
    // Skip if message already has explicit INFORMAL_RESULT or QUIZ_RESULT tag
    if (content.includes('[INFORMAL_RESULT]') || content.includes('[QUIZ_RESULT]')) return;
    
    // Get the previous user message (the answer being evaluated)
    const prevUserMessage = messages[messages.length - 2];
    if (prevUserMessage?.role !== 'user') return;
    const userAnswer = prevUserMessage.content || '';
    
    // Create a unique key to prevent duplicate processing
    const syncKey = `auto-${messages.length}-${content.substring(0, 50)}`;
    if (lastAutoDetectedRef.current.has(syncKey)) return;
    
    // Detect CORRECT answer patterns
    const correctPatterns = [
      /\bexactly\b/i,
      /\bcorrect(ly)?\b/i,
      /\bright\b/i,
      /\bperfect\b/i,
      /\bgreat (choice|job|work|answer)!?\b/i,
      /\bgood (choice|job|work|answer)!?\b/i,
      /\bnice(ly done)?!?\b/i,
      /\bwell done\b/i,
      /\bthat's? (right|correct)\b/i,
      /\byou('ve| have)? (got it|nailed it|done it)\b/i,
      /\byou('re| are) (right|correct)\b/i,
      /\byes[,!]?\s+(that'?s?|exactly|correct)/i,
      /\bexcellent\b/i,
      /\bawesome\b/i,
      /\bspot on\b/i
    ];
    
    // Detect WRONG answer patterns
    const wrongPatterns = [
      /\bactually\b/i,
      /\bnot quite\b/i,
      /\bnot exactly\b/i,
      /\bit looks like you (printed|used|wrote)\b.*\binstead of\b/i,
      /\bhowever\b.*\bwe (would|should|need)/i,
      /\bi see you('re| are)? (mentioned?|thinking|using)\b.*\bbut\b/i,
      /\bfor (numerical|numbers?|measurements?)\b.*\b(we|you) (would|should|generally|typically) use\b/i,
      /\bboolean is (used )?for (true|false)/i,
      /\bthat's not (quite )?(right|correct)/i,
      /\bclose,? but\b/i,
      /\btry again\b/i,
      /\bnot (the|what)\b/i
    ];
    
    let isCorrect = null;
    
    // Check for correct patterns
    for (const pattern of correctPatterns) {
      if (pattern.test(content)) {
        isCorrect = true;
        console.log(`🔍 AUTO-DETECT: Matched CORRECT pattern: ${pattern}`);
        break;
      }
    }
    
    // Check for wrong patterns (takes precedence if both match due to phrasing like "Great try, but actually...")
    for (const pattern of wrongPatterns) {
      if (pattern.test(content)) {
        isCorrect = false;
        console.log(`🔍 AUTO-DETECT: Matched WRONG pattern: ${pattern}`);
        break;
      }
    }
    
    // If no evaluation detected, skip
    if (isCorrect === null) {
      console.log(`🔍 AUTO-DETECT: No evaluation pattern found in: "${content.substring(0, 80)}..."`);
      return;
    }
    
    // Try to detect the subject from context
    // Look for topic keywords in the conversation
    const contextWindow = messages.slice(-6).map(m => m.content || '').join(' ').toLowerCase();
    
    let detectedSubject = 'General Python';
    
    // Subject detection patterns (more specific patterns first)
    const subjectPatterns = [
      { pattern: /\bprint\s*\(/i, subject: 'Print Function' },
      { pattern: /\bprint\(\)/i, subject: 'Print Function' },
      { pattern: /\bprint function\b/i, subject: 'Print Function' },
      { pattern: /\binput\s*\(/i, subject: 'Input Function' },
      { pattern: /\binput\(\)/i, subject: 'Input Function' },
      { pattern: /\binput function\b/i, subject: 'Input Function' },
      { pattern: /\b(int|integer|whole numbers?)\b/i, subject: 'Data Types' },
      { pattern: /\b(float|decimal|floating[- ]point)\b/i, subject: 'Data Types' },
      { pattern: /\b(bool|boolean|true|false)\b/i, subject: 'Data Types' },
      { pattern: /\b(str|string)\b/i, subject: 'Data Types' },
      { pattern: /\btype conversion\b/i, subject: 'Type Conversion' },
      { pattern: /\b(convert|converting)\b.*\b(int|float|str)\b/i, subject: 'Type Conversion' },
      { pattern: /\bvariable/i, subject: 'Variables' },
      { pattern: /\boperator/i, subject: 'Operators' },
      { pattern: /\bdata type/i, subject: 'Data Types' },
      { pattern: /\bdisplay\b/i, subject: 'Print Function' },
      { pattern: /\boutput\b/i, subject: 'Print Function' }
    ];
    
    for (const { pattern, subject } of subjectPatterns) {
      if (pattern.test(contextWindow)) {
        detectedSubject = subject;
        console.log(`🔍 AUTO-DETECT: Subject matched pattern ${pattern} → "${subject}"`);
        break;
      }
    }
    
    // Mark as processed
    lastAutoDetectedRef.current.add(syncKey);
    if (lastAutoDetectedRef.current.size > 50) {
      const entries = Array.from(lastAutoDetectedRef.current);
      lastAutoDetectedRef.current = new Set(entries.slice(-25));
    }
    
    console.log(`🔍 AUTO-DETECT: LLM forgot [INFORMAL_RESULT] tag. Detected: ${detectedSubject} = ${isCorrect ? 'CORRECT' : 'WRONG'}`);
    console.log(`🔍 User answer was: "${userAnswer.substring(0, 50)}..."`);
    console.log(`🔍 LLM response started with: "${content.substring(0, 100)}..."`);
    
    // Save to question_answered table
    const saveAutoDetected = async () => {
      const username = localStorage.getItem('username') || 'anonymous';
      
      // Normalize the subject
      const normalizedSubject = await normalizeSubjectWithLLM(detectedSubject);
      
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/studentQuestionAnswered`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            subject: normalizedSubject,
            result: isCorrect ? 'correct' : 'wrong'
          })
        });
        
        if (res.ok) {
          console.log(`🔍 ✅ AUTO-DETECT saved: "${normalizedSubject}" = ${isCorrect ? 'correct' : 'wrong'}`);
          if (!questionAnsweredSubjects.includes(normalizedSubject)) {
            setQuestionAnsweredSubjects(prev => [...prev, normalizedSubject]);
          }
        } else {
          console.log(`🔍 ⚠️ AUTO-DETECT API returned ${res.status}`);
        }
      } catch (err) {
        console.error('🔍 ❌ AUTO-DETECT failed to save:', err);
      }
    };
    
    saveAutoDetected();
    
  }, [messages, questionAnsweredSubjects]);
  DISABLED - using LLM extraction instead */

  const handleLogout = async () => {
    const wasDemo = isDemoMode();
    setLoggingOut(true);
    // Update session on server before logout
    await clearSession();
    
    logAction('Logout', '', '');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('username');
    localStorage.removeItem('password');
    localStorage.removeItem('userRole');
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('class_section');
    localStorage.removeItem('selected_section');
    localStorage.removeItem('university');
    localStorage.removeItem('semester');
    localStorage.removeItem('subscriptionStatus');
    localStorage.removeItem('subscriptionRequired');
    localStorage.removeItem('canAccess');
    localStorage.removeItem('trialDaysLeft');
    localStorage.removeItem('historyPanelWidth');
    localStorage.removeItem('promptHintDismissed');
    localStorage.removeItem('demoMode');
    setHistoryPanelWidth(null);
    setShowPromptHint(true);
    setUserRole('user');
    
    // Reset all states to ensure a clean slate for the next user/session
    setIsLoggedIn(false);
    
    // Demo users land on the about page instead of login
    if (wasDemo) {
      window.location.href = '/about.html';
      return;
    }
    setMode('breakdown');
    setSessionProgress({});
    setPlan([]);
    setPythonCode("");
    setRunOutput('');
    setExecutionId(null);
    setIsAwaitingInput(false);
    setLastPrompt('');
    setPromptEndIndex(0);
    syncedStepsRef.current.clear();
    previousStep1Ref.current = null; // Clear step 1 tracking
    lastSyncedMessageRef.current = null;
    lastSyncedDataRef.current = {};
    sessionStartMasteryRef.current = {}; // Reset session start mastery
    setMessages([]); // Cleared messages will be re-initialized by the useEffect with the system prompt
    setShowAnalytics(false); // Close analytics on logout
    setShowStruggles(false); // Close struggles on logout
    setShowStruggleHeatmap(false); // Close struggle heatmap on logout
    setShowKnowledgeHealth(false); // Close knowledge health on logout
    setShowKnowledgeStates(false); // Close HMM states panel on logout
    setKnowledgeHealth({}); // Clear knowledge health data
    setSoftReviewsSavedToday(new Set()); // Clear soft review tracking for new session
    setQuestionAnsweredSubjects([]); // Clear question_answered subjects for new session
    setSubscriptionStatus('unknown');
    setCanAccess(true);
    setTrialDaysLeft(-1);
  };

  // Register logout callback for session expiry (auto-logout when timer hits 0)
  useEffect(() => {
    setOnSessionExpiredCallback(() => {
      handleLogout();
    });
  }, [setOnSessionExpiredCallback]); // handleLogout uses setState, doesn't need to be a dependency

  if (!isLoggedIn) {
    return <Login onLoginSuccess={() => {
      setIsLoggedIn(true);
      setUserRole(localStorage.getItem('userRole') || 'user');
      // Update username state from localStorage after login
      const loggedInUsername = localStorage.getItem('username') || '';
      setUsername(loggedInUsername);
      // Re-initialize selectedSection from the new user's class_section
      const newSections = (localStorage.getItem('class_section') || '').split(',').map(s => s.trim()).filter(Boolean);
      const newSelected = newSections[0] || '';
      if (newSelected) {
        localStorage.setItem('selected_section', newSelected);
        setSelectedSection(newSelected);
      }
      // Load subscription state
      setSubscriptionStatus(localStorage.getItem('subscriptionStatus') || 'unknown');
      setCanAccess(localStorage.getItem('canAccess') !== 'false');
      const storedTrialDays = localStorage.getItem('trialDaysLeft');
      setTrialDaysLeft(storedTrialDays !== null ? parseInt(storedTrialDays, 10) : -1);
    }} logAction={logAction} />;
  }

  // ==================== SUBSCRIPTION PAYWALL ====================
  // If user is logged in but can't access (trial expired, canceled, etc.), show paywall
  if (!canAccess && subscriptionStatus !== 'exempt' && subscriptionStatus !== 'unknown') {
    return <SubscriptionPaywall
      username={username}
      subscriptionStatus={subscriptionStatus}
      onLogout={handleLogout}
    />;
  }
  // ===============================================================

  return (
    <div className="App">
      {/* Session Timer - fixed position, shows on all pages/modals */}
      <SessionTimer onForceLogout={handleLogout} inline={true} />

      {/* Subscription Trial Banner - shown only on main page (not when any modal/panel is open) */}
      {subscriptionStatus === 'trial' && trialDaysLeft >= 0 && 
       !showAnalytics && !showStruggles && !showStruggleHeatmap && !showTutorial && 
       !showReportProblem && !showCourseContent && !showCourseSlides && !showAdminPanel && 
       !showPracticeExam && !showBadgePanel && !showClassQuizResults && !showKnowledgeHealth &&
       !showDebug && !showTurtle && !showClassCodeModal && (
        <SubscriptionBanner
          trialDaysLeft={trialDaysLeft}
          onSubscribeClick={async () => {
            try {
              const res = await apiFetch(`${API_BASE_URL}/api/subscription/create-checkout`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username }),
              });
              if (res.ok) {
                const data = await res.json();
                if (data.checkout_url) window.location.href = data.checkout_url;
              }
            } catch (err) {
              console.error('Error creating checkout:', err);
            }
          }}
        />
      )}
      
      {showDebug && renderMasteryDashboard()}
      
      {/* Proactive Knowledge Decay Alert */}
      {notificationsEnabled && (() => {
        // Filter out mastered topics from notifications
        const normalizeSubject = (s) => s ? s.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') : '';
        const criticalTopics = Object.values(knowledgeHealth).filter(s => {
          const normalized = normalizeSubject(s.subject);
          return s.urgency === 'critical' && !s.dismissed && !masteredTopics.includes(normalized);
        });
        if (criticalTopics.length === 0) return null;
        const mostCritical = criticalTopics.sort((a, b) => a.memoryStrength - b.memoryStrength)[0];
        
        return (
          <div style={{
            position: 'fixed',
            top: '10px',
            left: '350px',
            backgroundColor: '#fef2f2',
            border: '2px solid #000000',
            borderRadius: '12px',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            zIndex: 999,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            maxWidth: '400px'
          }}>
            <span style={{ fontSize: '24px' }}>🧠</span>
            <div>
              <div style={{ color: '#991b1b', fontWeight: 'bold', fontSize: '14px' }}>
                Your "{mostCritical.subject}" knowledge is fading!
              </div>
              <div style={{ color: '#b91c1c', fontSize: '12px' }}>
                Memory strength: {mostCritical.memoryStrength}% • Last practiced: {mostCritical.daysSinceReview} days ago
              </div>
            </div>
            <button
              onClick={() => {
                setMode('building-blocks');
                setPrompt(`I want to practice ${mostCritical.subject}`);
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 'bold',
                whiteSpace: 'nowrap'
              }}
            >
              Quick Review
            </button>
            <button
              onClick={() => {
                // Dismiss by marking this subject as dismissed (keeps original urgency for display)
                setKnowledgeHealth(prev => {
                  const updated = { ...prev };
                  if (updated[mostCritical.subject]) {
                    updated[mostCritical.subject] = { ...updated[mostCritical.subject], dismissed: true };
                  }
                  return updated;
                });
              }}
              style={{
                padding: '4px 8px',
                backgroundColor: 'transparent',
                color: '#991b1b',
                border: 'none',
                cursor: 'pointer',
                fontSize: '18px'
              }}
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        );
      })()}
      
      {/* Analytics Modal */}
      {showAnalytics && (
        <AnalyticsDashboard 
          username={username} 
          onClose={() => setShowAnalytics(false)} 
        />
      )}

      {/* Struggle Moments Modal */}
      {showStruggles && (
        <StruggleMomentsDashboard 
          username={username} 
          onClose={() => setShowStruggles(false)} 
        />
      )}

      {/* Struggle Heatmap Modal */}
      {showStruggleHeatmap && (
        <StruggleHeatmap 
          username={username}
          curriculumContent={curriculumContent}
          onClose={() => setShowStruggleHeatmap(false)}
          onPracticeRecommendation={(recommendation, topic) => {
            // Close the modal
            setShowStruggleHeatmap(false);
            // Switch to building-blocks mode if not already
            if (mode !== 'building-blocks') {
              setMode('building-blocks');
            }
            // Send the practice request to the tutor
            const practiceMessage = `I want to practice this: ${recommendation}`;
            sendMessage(practiceMessage, 'Practice from Progress Map');
            logAction('Practice Recommendation Clicked', topic, recommendation);
          }}
        />
      )}

      {/* Code Detected in Chat Popup */}
      {showCodeDetectedPopup && (
        <CodeDetectedPopup
          onClose={() => {
            setShowCodeDetectedPopup(false);
            setPendingCodeMessage('');
          }}
          onSendAnyway={handleSendCodeAnyway}
        />
      )}

      {/* Knowledge Health Panel */}
      {showKnowledgeHealth && (
        <KnowledgeHealthPanel
          knowledgeHealth={knowledgeHealth}
          masteredTopics={masteredTopics}
          setMasteredTopics={setMasteredTopics}
          setShowKnowledgeHealth={setShowKnowledgeHealth}
          setMode={setMode}
          setPrompt={setPrompt}
        />
      )}

      {/* HMM Knowledge States Panel */}
      {showKnowledgeStates && (
        <KnowledgeStatesPanel
          masteryHistory={masteryHistory}
          username={username}
          onClose={() => setShowKnowledgeStates(false)}
          onQuizTopic={(msg, action) => {
            setMode('building-blocks');
            sendMessage(msg, action, null, true); // true = skipAdaptiveHints
          }}
        />
      )}

      {/* Tutorial Modal */}
      {showTutorial && <Tutorial onClose={() => setShowTutorial(false)} />}

      {/* Study Advice Panel */}
      {showStudyAdvice && (
        <StudyAdvicePanel
          masteryHistory={masteryHistory}
          logAction={logAction}
          onClose={() => setShowStudyAdvice(false)}
        />
      )}

      {/* Course Content View */}
      {showCourseContent && (
        <CourseContentView
          username={username}
          onClose={() => setShowCourseContent(false)}
          logAction={logAction}
          selectedSection={selectedSection}
        />
      )}

      {/* Course Slides PDF Viewer */}
      {showCourseSlides && (
        <CourseSlidesPanel
          onClose={() => setShowCourseSlides(false)}
          selectedSection={selectedSection}
        />
      )}

      {/* Turtle Graphics Modal */}
      {showTurtle && (
        <TurtleModal onClose={() => setShowTurtle(false)} />
      )}

      {/* Admin Panel */}
      {showAdminPanel && (
        <AdminPanel 
          onClose={() => setShowAdminPanel(false)}
          selectedSection={selectedSection}
        />
      )}

      {/* Practice Exam Modal */}
      {showPracticeExam && (
        <PracticeExam
          username={username}
          syllabusMap={syllabusMap}
          selectedSyllabusDate={selectedSyllabusDate}
          onClose={() => setShowPracticeExam(false)}
          logAction={logAction}
          normalizeSubjectWithLLM={normalizeSubjectWithLLM}
          masteryAggregate={masteryAggregate}
          questionAnsweredSubjects={questionAnsweredSubjects}
          selectedSection={selectedSection}
        />
      )}

      {/* Badge & Points Panel */}
      {showBadgePanel && (
        <BadgePanel
          username={username}
          onClose={() => setShowBadgePanel(false)}
        />
      )}

      {/* Class Quiz Results */}
      {showClassQuizResults && (
        <ClassQuizResults
          username={username}
          onClose={() => setShowClassQuizResults(false)}
          selectedSection={selectedSection}
        />
      )}

      {/* Class Code Modal */}
      {showClassCodeModal && (
        <ClassCodeModal
          username={username}
          userRole={userRole}
          onClose={() => setShowClassCodeModal(false)}
          selectedSection={selectedSection}
          onLoadCode={(code) => setPythonCode(code)}
        />
      )}

      {/* Pre-supply inputs modal for Visualizer */}
      {showInputModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 12000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: '#161b22', border: '1px solid rgba(255, 240, 150, 0.6)',
            borderRadius: '12px', padding: '0', width: '780px', maxWidth: '95%', overflow: 'hidden',
          }}>
            <div style={{ backgroundColor: 'rgba(255,240,150,0.07)', borderBottom: '1px solid rgba(255,240,150,0.2)', padding: '20px 28px' }}>
              <h3 style={{ color: '#f0e68c', margin: 0, fontSize: '22px' }}>
                📥 Supply input() values
              </h3>
              <p style={{ color: '#c9ba7a', fontSize: '17px', marginBottom: '0', marginTop: '8px', lineHeight: 1.6 }}>
                Your code uses <code style={{ color: '#79c0ff' }}>input()</code>. Enter one value per line — supply as many as needed (even if the count depends on earlier inputs).
              </p>
            </div>
            <div style={{ padding: '20px 28px 28px' }}>
            <textarea
              autoFocus
              value={inputModalValues}
              onChange={e => setInputModalValues(e.target.value)}
              placeholder={`value 1\nvalue 2\nvalue 3\n...`}
              rows={16}
              style={{
                width: '100%', padding: '10px 14px',
                backgroundColor: '#0d1117', color: '#c9d1d9',
                border: '1px solid rgba(255, 240, 150, 0.5)', borderRadius: '6px',
                fontFamily: 'monospace', fontSize: '22px', outline: 'none',
                boxSizing: 'border-box', resize: 'vertical',
              }}
            />
            <p style={{ color: '#c9ba7a', fontSize: '17px', marginTop: '10px', marginBottom: '0', lineHeight: 1.5 }}>
              💡 <strong style={{ color: '#e8d48a' }}>Tip:</strong> Run the code first to see what it asks for.
              If your code uses <code style={{ color: '#79c0ff' }}>random</code>, the visualizer uses fixed values — run it once to discover them, then supply the matching inputs here.
            </p>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button
                onClick={() => handleRunTrace(pendingTraceCode, inputModalValues.split('\n'))}
                style={{
                  flex: 1, padding: '9px', backgroundColor: '#238636',
                  color: '#fff', border: 'none', borderRadius: '6px',
                  cursor: 'pointer', fontWeight: 700,
                }}
              >
                🔍 Run Visualizer
              </button>
              <button
                onClick={() => setShowInputModal(false)}
                style={{
                  padding: '9px 18px', backgroundColor: '#21262d',
                  color: '#8b949e', border: '1px solid rgba(255, 240, 150, 0.35)', borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Visual Debugger */}
      {showVisualizer && traceSteps.length > 0 && (
        <VisualDebugger
          code={pythonCode}
          steps={traceSteps}
          onClose={() => setShowVisualizer(false)}
        />
      )}

      {/* Report Problem Modal */}
      {showReportProblem && (
        <ReportProblem 
          username={username} 
          onClose={() => setShowReportProblem(false)} 
          logAction={logAction}
        />
      )}

      {/* Download Filename Modal */}
      {showDownloadModal && (
        <DownloadModal
          downloadFilename={downloadFilename}
          setDownloadFilename={setDownloadFilename}
          onConfirm={confirmDownload}
          onClose={() => setShowDownloadModal(false)}
        />
      )}

      <div className="logout-container">
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '12px',
          padding: '14px 26px',
          backgroundColor: '#1c2128',
          border: '2px solid #e5c100',
          borderRadius: '12px',
          fontSize: '18px',
          marginRight: 'auto',
          marginTop: '8px',
        }}>
          <span>📚</span>
          <span style={{ color: '#fffacd' }}>Knowledge limit:</span>
          <span style={{ color: '#e5c100', fontWeight: '700' }}>
            {selectedSyllabusDate === 'none'
              ? 'No Limit'
              : selectedSyllabusDate === 'first' || !syllabusMap[selectedSyllabusDate]
              ? 'All slides'
              : syllabusMap[selectedSyllabusDate].replace('.txt', '').replace('slide', 'Slide ')}
          </span>
        </div>
        <button
          onClick={() => {
            const newValue = !notificationsEnabled;
            logAction(newValue ? 'Notifications Enabled' : 'Notifications Disabled', '', '');
            setNotificationsEnabled(newValue);
            localStorage.setItem('notificationsEnabled', newValue.toString());
          }}
          style={{
            padding: '8px 12px',
            backgroundColor: notificationsEnabled ? '#059669' : '#6b7280',
            color: '#fff',
            border: '1px solid rgba(254,240,138,0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
          title={notificationsEnabled ? 'Click to disable notifications' : 'Click to enable notifications'}
        >
          {notificationsEnabled ? '🔔' : '🔕'}
        </button>
        <button
          onClick={() => {
            logAction('Tutorial Opened', '', '');
            setShowTutorial(true);
          }}
          style={{
            padding: '8px 12px',
            backgroundColor: '#1e40af',
            color: '#fff',
            border: '1px solid rgba(254,240,138,0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
          title="Open Tutorial - Learn how to use this app"
        >
          📖 Tutorial
        </button>
        <button
          onClick={() => {
            logAction('Class Codes Opened', '', '');
            setShowClassCodeModal(true);
          }}
          style={{
            padding: '8px 12px',
            backgroundColor: '#166534',
            color: '#fff',
            border: '1px solid rgba(254,240,138,0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
          title="View and manage class code snippets"
        >
          📁 Class Codes
        </button>
        <button
          onClick={() => {
            logAction('Course Content Opened', '', '');
            setShowCourseContent(true);
          }}
          style={{
            padding: '8px 12px',
            backgroundColor: '#6d28d9',
            color: '#fff',
            border: '1px solid rgba(254,240,138,0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
          title="Browse and learn course topics"
        >
          📚 Course Content
        </button>
        <button
          onClick={() => {
            logAction('Course Slides Opened', '', '');
            setShowCourseSlides(true);
          }}
          style={{
            padding: '8px 12px',
            backgroundColor: '#4f46e5',
            color: '#fff',
            border: '1px solid rgba(254,240,138,0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
          title="View course slides as PDF"
        >
          📑 Course Slides
        </button>
        <button
          onClick={() => {
            logAction('Turtle Graphics Opened', '', '');
            setShowTurtle(true);
          }}
          style={{
            padding: '8px 12px',
            backgroundColor: '#0f5c2e',
            color: '#fff',
            border: '1px solid rgba(254,240,138,0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
          title="Practice Python Turtle Graphics"
        >
          🐢 Turtle
        </button>
        <button
          onClick={() => {
            logAction('Report Issue Opened', '', '');
            setShowReportProblem(true);
          }}
          style={{
            padding: '8px 12px',
            backgroundColor: '#d97706',
            color: '#000',
            border: '1px solid rgba(254,240,138,0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontWeight: 'bold',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
          }}
          title="Report a bug or issue"
        >
          ⚠️ Report Issue
        </button>
        {userRole === 'admin' && (
          <button
            onClick={() => {
              logAction('Admin Page Opened', '', '');
              setShowAdminPanel(true);
            }}
            style={{
              padding: '8px 12px',
              backgroundColor: '#4c1d95',
              color: '#fff',
              border: '1px solid rgba(254,240,138,0.3)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}
            title="Admin Page - Manage curriculum files"
          >
            ⚙️ Admin Page
          </button>
        )}
        <button className="logout-btn" onClick={handleLogout}>Logout</button>
      </div>

      <header className="App-header">
        <main className="main-content">
          {/* Fixed header with mode selector */}
          <div className="main-content-fixed-header" style={{ position: 'relative' }}>
            <button
              onClick={() => setLayoutStacked(prev => !prev)}
              title={layoutStacked ? 'Switch to side-by-side layout' : 'Switch to stacked layout'}
              style={{
                position: 'absolute',
                top: '2px',
                left: '2px',
                padding: '3px 8px',
                fontSize: '11px',
                fontWeight: '600',
                backgroundColor: '#30363d',
                color: '#fef08a',
                border: '1px solid rgba(254,240,138,0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease',
                whiteSpace: 'nowrap',
                zIndex: 1
              }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#3d444d'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#30363d'}
            >
              {layoutStacked ? '⬌ Side-by-Side' : '⬍ Stacked'}
            </button>
            {userSections.length > 1 && (
              <div style={{
                position: 'absolute', top: '2px', left: '110px',
                display: 'flex', alignItems: 'center', gap: '5px', zIndex: 1
              }}>
                <span style={{ color: '#f0d060', fontSize: '11px', fontWeight: 'bold' }}>📌</span>
                <select
                  value={selectedSection}
                  onChange={(e) => {
                    const newSection = e.target.value;
                    setSelectedSection(newSection);
                    localStorage.setItem('selected_section', newSection);
                    setSyllabusMap({});
                    setSelectedSyllabusDate('first');
                    setMessages([]); setPlan([]); setPythonCode('');
                  }}
                  style={{
                    backgroundColor: '#30363d', color: '#fff5b0',
                    border: '1px solid rgba(254,240,138,0.3)', borderRadius: '6px',
                    padding: '3px 8px', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                  }}
                >
                  {userSections.map(sec => (
                    <option key={sec} value={sec}>{sec}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mode-selector">
              <button 
                className={`mode-btn ${mode === 'breakdown' ? 'active' : ''}`}
                onClick={() => handleModeChange('breakdown')}
              >
                Problem Breakdown Mode
              </button>
              <button 
                className={`mode-btn ${mode === 'debugging' ? 'active' : ''}`}
                onClick={() => handleModeChange('debugging')}
              >
                Problem Debugging Mode
              </button>
              <button 
                className={`mode-btn ${mode === 'building-blocks' ? 'active' : ''}`}
                onClick={() => handleModeChange('building-blocks')}
              >
                Problem Building Block Practice
              </button>
            </div>
          </div>

          {/* Scrollable content area */}
          <div className="main-content-scrollable">
            <div className={`chat-code-layout ${layoutStacked ? 'chat-code-stacked' : ''}`}>
            {/* LEFT: Chat Panel */}
            <div className="chat-panel-side">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="prompt-textarea" style={{ fontSize: '27px', display: 'block', textAlign: 'left' }}>
                  Ask the tutor a question about your project:
                </label>
                <textarea
                  id="prompt-textarea"
                  value={prompt}
                  onChange={(e) => {
                    if (showPromptHint) {
                      setShowPromptHint(false);
                      localStorage.setItem('promptHintDismissed', 'true');
                    }
                    setPrompt(e.target.value);
                  }}
                  placeholder={showPromptHint ? "⚠️ Always start with your problem (e.g. 'I'm writing a program that calculates grades…'). If you're not solving a specific problem, just ask your question." : ''}
                  rows="8"
                  required={localStorage.getItem('demoMode') !== 'true'}
                />
            </div>
            <div className="button-group">
              <button type="submit" disabled={loading}>
                {loading ? 'Thinking...' : 'Send Message'}
              </button>
              <button 
                type="button" 
                onClick={handleNeedHelp} 
                disabled={loading}
                className="help-button"
              >
                💡 Hint
              </button>
              {(mode === 'breakdown' || mode === 'debugging') && (
                <button 
                  type="button" 
                  onClick={handleNewProblem} 
                  disabled={loading}
                  className="new-problem-button"
                  title="Clear current problem and start fresh"
                >
                  🆕 New Problem
                </button>
              )}
            </div>
            {mode === 'building-blocks' && (
              <div className="button-group" style={{ marginTop: '8px' }}>
                <button 
                  type="button" 
                  onClick={handleQuizCurrent} 
                  disabled={loading}
                  className="quiz-button"
                  title="Quiz on current building block"
                >
                  📝 Quiz This Topic
                </button>
                <button 
                  type="button" 
                  onClick={handleQuizRandom} 
                  disabled={loading}
                  className="quiz-button random"
                  title="Quiz on a random building block"
                >
                  🎲 Quiz Any Topic
                </button>
                <button 
                  type="button" 
                  onClick={handleContinuePractice} 
                  disabled={loading}
                  className="practice-button"
                  title="Continue practicing"
                >
                  ▶️ Continue
                </button>
                <button 
                  type="button" 
                  onClick={handleNewProblem} 
                  disabled={loading}
                  className="new-problem-button"
                  title="Clear current problem and start fresh"
                >
                  🆕 New Problem
                </button>
              </div>
            )}
          </form>

          </div> {/* End chat-panel-side */}

          {/* RIGHT: Code Panel */}
          <div className="code-panel-side">
          <div className="code-runner-panel">
            
            <form onSubmit={handleRunCode}>
              <div className="form-group">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <label htmlFor="code-textarea" style={{ margin: 0, marginLeft: '10px', fontSize: '27px' }}>Write your code here:</label>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button
                      type="button"
                      onClick={() => setUseMainMonaco(prev => !prev)}
                      title={useMainMonaco ? 'Switch to Simple Editor' : 'Switch to Monaco Editor (IntelliSense)'}
                      style={{
                        backgroundColor: useMainMonaco ? '#1f6feb' : '#6e7681',
                        color: 'white',
                        border: 'none',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '11px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {useMainMonaco ? '🧠 Monaco Editor' : '📝 Simple Editor'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setMainEditorTheme(prev => prev === 'vs-dark' ? 'vs' : 'vs-dark')}
                      title={mainEditorTheme === 'vs-dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
                      style={{
                        backgroundColor: mainEditorTheme === 'vs-dark' ? '#21262d' : '#f0f0f0',
                        color: mainEditorTheme === 'vs-dark' ? '#c9d1d9' : '#1e1e1e',
                        border: '1px solid #30363d',
                        padding: '4px 10px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '11px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {mainEditorTheme === 'vs-dark' ? '☀️ Light' : '🌙 Dark'}
                    </button>
                  </div>
                </div>
                {useMainMonaco ? (
                  <div style={{ 
                    height: '350px', 
                    border: mainEditorTheme === 'vs-dark' ? '0.5px solid #ffffff' : '1px solid #30363d', 
                    borderRadius: '6px', 
                    overflow: 'hidden' 
                  }}>
                    <Editor
                      height="100%"
                      defaultLanguage="python"
                      language="python"
                      theme={mainEditorTheme}
                      value={pythonCode}
                      onChange={(value) => setPythonCode(value || '')}
                      onMount={handleMainEditorDidMount}
                      loading=""
                      options={{
                        fontSize: 23,
                        fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                        lineHeight: 1.6,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        wordWrap: 'on',
                        automaticLayout: true,
                        suggestOnTriggerCharacters: true,
                        quickSuggestions: { other: true, comments: false, strings: false },
                        tabSize: 4,
                        renderLineHighlight: 'line',
                        cursorBlinking: 'smooth',
                        padding: { top: 10 }
                      }}
                    />
                  </div>
                ) : (
                  <textarea
                    id="code-textarea"
                    value={pythonCode}
                    onChange={(e) => setPythonCode(e.target.value)}
                    style={{
                      height: '350px',
                      backgroundColor: mainEditorTheme === 'vs-dark' ? '#1e1e1e' : '#ffffff',
                      color: mainEditorTheme === 'vs-dark' ? '#d4d4d4' : '#1e1e1e',
                      fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                      resize: 'none'
                    }}
                  />
                )}
              </div>
              <div className="button-group">
                <button 
                  type="button" 
                  onClick={handleSendCodeToTutor} 
                  disabled={loading}
                  className="send-code-button"
                >
                  Audit Code with Tutor
                </button>
                <button type="submit" disabled={runLoading || isAwaitingInput}>
                  {runLoading ? 'Running...' : 'Run Code'}
                </button>
                {(runLoading || executionId) && (
                  <button 
                    type="button" 
                    onClick={handleTerminateCode}
                    style={{
                      backgroundColor: '#dc2626',
                      color: '#fff',
                      border: 'none',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                    title="Stop running code"
                  >
                    ⏹️ Stop
                  </button>
                )}
                <button
                  type="button"
                  disabled={isTracing}
                  onClick={() => {
                    if (!pythonCode.trim()) return;
                    if (isDemoMode()) {
                      setRunOutput('🔒 Step-by-step visualizer is locked in demo mode.\n\nWith an account you can step through your Python code line by line,\nwatching each variable change in real time.\n\n✨ Sign up to unlock!');
                      return;
                    }
                    // Remove all commented lines (full-line or inline) before checking for input()
                    const uncommentedCode = pythonCode.split('\n').map(line => {
                      const t = line.trimStart();
                      if (t.startsWith('#')) return ''; // full-line comment
                      // strip inline comment (naive but covers common cases)
                      const ci = line.indexOf('#');
                      return ci >= 0 ? line.slice(0, ci) : line;
                    }).join('\n');
                    const inputCount = (uncommentedCode.match(/\binput\s*\(/g) || []).length;
                    if (inputCount > 0) {
                      setPendingTraceCode(pythonCode);
                      setInputModalValues('');
                      setShowInputModal(true);
                    } else {
                      handleRunTrace(pythonCode, []);
                    }
                  }}
                  style={{
                    backgroundColor: isTracing ? '#21262d' : '#1d3a5f',
                    color: isTracing ? '#4b5563' : '#79c0ff',
                    border: isTracing ? '1px solid #388bfd55' : '1px solid rgba(255, 240, 150, 0.75)',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: isTracing ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold'
                  }}
                  title="Step through code line by line"
                >
                  {isTracing ? '⏳ Tracing...' : '🔍 Visualize'}
                </button>
                <button 
                  type="button" 
                  onClick={handleDownloadCode}
                  style={{
                    backgroundColor: '#6366f1',
                    color: '#fff',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                  title="Download code as .py file"
                >
                  ⬇️ Download
                </button>
              </div>
            </form>

            <div className="form-group">
              <label htmlFor="output-textarea" style={{ display: 'block', textAlign: 'left' }}>Output:</label>
              <textarea
                id="output-textarea"
                ref={outputRef}
                className={isAwaitingInput ? 'awaiting-input' : ''}
                value={runOutput}
                onChange={(e) => {
                  if (isAwaitingInput) {
                    const historicalOutput = runOutput.substring(0, promptEndIndex);
                    if (e.target.value.startsWith(historicalOutput)) {
                      setRunOutput(e.target.value);
                    }
                  }
                }}
                onKeyDown={handleOutputKeyDown}
                placeholder={isAwaitingInput ? "Type your input here and press Enter..." : "Code output will appear here..."}
                rows="6"
                readOnly={!isAwaitingInput}
              />
            </div>
          </div>
          </div> {/* End code-panel-side */}
          </div> {/* End chat-code-layout */}

          {false && mode === 'breakdown' && (
            <div className="plan-panel">
              <h2>Project Implementation Plan</h2>
              <div className="steps-container">
                {plan.filter(s => !s.isRevisionOnly).length === 0 ? (
                  <p className="no-plan">No plan developed yet. Discuss your project with the tutor to build a step-by-step roadmap.</p>
                ) : (
                  <ul>
                    {plan.filter(step => !step.isRevisionOnly).map((step, index) => (
                      <li key={index}>
                        <div className="step-header">
                          <span className="status-icon">⏳</span>
                          <span className="step-text">{step.text.replace(/\s*\(complete\)\s*$/i, '')}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {mode === 'building-blocks' && (
            <div className="progress-panel" ref={masteryRef}>
              <div
                onClick={() => setShowMastery(p => !p)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: '12px' }}
              >
                <h2 style={{ margin: 0 }}>
                  <span style={{ color: '#00d4aa', fontSize: '18px', marginRight: '10px' }}>{showMastery ? '▼' : '▶'}</span>
                  Building Block Mastery
                </h2>
              </div>
              {showMastery && (
                Object.entries(combinedProgress).filter(([_, data]) => {
                  const total = normalizeCount(data?.correct) + normalizeCount(data?.wrong);
                  return total > 0;
                }).length === 0 ? (
                  <p className="no-progress">No building blocks identified yet. Answer quiz questions to see your progress here.</p>
                ) : (
                  <div className="block-mastery-scroll">
                    <div className="chart-container">
                      {Object.entries(combinedProgress)
                        .filter(([_, data]) => {
                          const total = normalizeCount(data?.correct) + normalizeCount(data?.wrong);
                          return total > 0;
                        })
                        .map(([subject, data]) => {
                      const sCorrect = normalizeCount(data?.correct);
                      const sWrong = normalizeCount(data?.wrong);
                      const total = sCorrect + sWrong;
                      const correctPct = total > 0 ? (sCorrect / total) * 100 : 0;
                      
                      return (
                        <div key={subject} className="chart-column">
                        <div className="bar-wrapper">
                          <div className="bar-stack">
                            {/* Show empty state when no answers yet */}
                            {total === 0 && (
                              <div 
                                className="bar-segment empty"
                                style={{ 
                                  flexGrow: 1,
                                  minHeight: '100%',
                                  backgroundColor: '#30363d',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <span className="bar-label" style={{ color: '#8b949e' }}>0</span>
                              </div>
                            )}
                            {/* Correct (Green) - Bottom */}
                            {sCorrect > 0 && (
                              <div 
                                className="bar-segment correct" 
                                style={{ 
                                  flexGrow: sCorrect,
                                  flexBasis: total > 0 ? `${correctPct}%` : 'auto',
                                  minHeight: '18px',
                                  backgroundColor: '#2ea043'
                                }}
                              >
                                <span className="bar-label">{sCorrect}</span>
                              </div>
                            )}
                            {/* Wrong (Red) - Top */}
                            {sWrong > 0 && (
                              <div 
                                className="bar-segment wrong" 
                                style={{ 
                                  flexGrow: sWrong,
                                  flexBasis: total > 0 ? `${100 - correctPct}%` : 'auto',
                                  minHeight: '18px',
                                  backgroundColor: '#f85149'
                                }}
                              >
                                <span className="bar-label">{sWrong}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="subject-name">{subject}</div>
                        <div className="percentage-label">{total > 0 ? Math.round(correctPct) : 0}%</div>
                      </div>
                    );
                  })}
                    </div>
                  </div>
                )
              )}
            </div>
          )}
          </div> {/* End main-content-scrollable */}
        </main>

        <aside
          className="history-panel"
          style={historyPanelWidth != null ? { flex: `0 0 ${historyPanelWidth}px`, minWidth: 0, maxWidth: 'none' } : undefined}
        >
          {/* Draggable divider overlay — sits over the left border, no layout impact */}
          <div
            className="main-history-divider"
            onMouseDown={startHistoryDrag}
            onDoubleClick={resetHistoryWidth}
            title="Drag to resize • Double-click to reset"
          />
          {/* Fixed header with title and controls */}
          <div className="history-panel-fixed-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', marginBottom: '15px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <h1 style={{ margin: 0, fontSize: '24px', color: '#00d4aa' }}>PySage Tutor</h1>
                <div className="curriculum-selector" style={{ marginTop: '-20px' }}>
                  <label htmlFor="syllabus-select">Knowledge Limit:</label>
                  <select 
                    id="syllabus-select" 
                    value={selectedSyllabusDate} 
                    onChange={(e) => {
                      setSelectedSyllabusDate(e.target.value);
                    setMessages([]);
                    setPlan([]);
                    setPythonCode("");
                  }}
                >
                  <option value="none">No Limit</option>
                  {Object.entries(syllabusMap).map(([date, fileName]) => (
                    <option key={date} value={date}>
                      Up to {fileName.replace('slide', 'Slide ').replace('.txt', '')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginTop: '-10px' }}>
              <button
                onClick={() => setShowAnalyticsButtons(p => !p)}
                style={{
                  width: '100%',
                  padding: '5px',
                  marginBottom: '4px',
                  backgroundColor: '#1c2128',
                  color: '#fef08a',
                  border: '1px solid rgba(254,240,138,0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '700',
                  textAlign: 'center',
                  boxSizing: 'border-box',
                }}
              >
                {showAnalyticsButtons ? '▲ Hide Tools' : '▼ Show Tools'}
              </button>
              {showAnalyticsButtons && (
              <div className="analytics-buttons">
              <button
                className="debug-toggle-btn"
                onClick={() => setShowStudyAdvice(true)}
                title="Get personalized study advice based on your performance"
              >
                <span className="btn-icon">🎯</span><span className="btn-label">Study Advice</span>
              </button>
              <button 
                className="debug-toggle-btn" 
                onClick={() => {
                  logAction('Quiz Analytics Opened', '', '');
                  setShowDebug(true);
                }}
                title="Show Instructor Mastery Analysis"
              >
                <span className="btn-icon">📊</span><span className="btn-label">Quiz Analytics</span>
              </button>
              <button 
                className="debug-toggle-btn" 
                onClick={() => {
                  logAction('My Activity Stats Opened', '', '');
                  setShowAnalytics(true);
                }}
                title="Show Student Analytics Dashboard"
              >
                <span className="btn-icon">📈</span><span className="btn-label">My Activity Stats</span>
              </button>
              <button 
                className="debug-toggle-btn"
                onClick={() => {
                  logAction('Past Quiz Review Opened', '', '');
                  setShowStruggles(true);
                }}
                title="Show Moments of Struggle"
              >
                <span className="btn-icon">📝</span><span className="btn-label">Past Quiz Review</span>
              </button>
              <button 
                className="debug-toggle-btn"
                onClick={() => {
                  logAction('Learning Progress Map Opened', '', '');
                  setShowStruggleHeatmap(true);
                }}
                title="Show Learning Progress Map - Visual patterns"
              >
                <span className="btn-icon">🗺️</span><span className="btn-label">Learning Progress Map</span>
              </button>
              <button 
                className="debug-toggle-btn"
                onClick={() => {
                  logAction('Knowledge Health Opened', '', '');
                  logger.debug('🧠 Knowledge Health button clicked - refreshing data...');
                  fetchMasteryAnalysis(); // Recalculate knowledge health
                  setShowKnowledgeHealth(true);
                }}
                title="Show Knowledge Health - Memory Strength"
              >
                <span className="btn-icon">🧠</span><span className="btn-label">Knowledge Health</span>
              </button>
              <button
                className="debug-toggle-btn"
                onClick={() => {
                  logAction('Knowledge States Opened', '', '');
                  setShowKnowledgeStates(true);
                }}
                title="Show HMM Knowledge States - per-topic learning state"
              >
                <span className="btn-icon">🔮</span><span className="btn-label">Knowledge States</span>
              </button>
              <button 
                className="debug-toggle-btn"
                onClick={() => {
                  logAction('Practice Exam Opened', '', '');
                  setShowPracticeExam(true);
                }}
                title="Take a Practice Exam"
              >
                <span className="btn-icon">📝</span><span className="btn-label">Practice Exam</span>
              </button>
              <button 
                className="debug-toggle-btn"
                onClick={() => {
                  logAction('Badge Panel Opened', '', '');
                  setShowBadgePanel(true);
                }}
                title="View My Badge & Points"
              >
                <span className="btn-icon">🏆</span><span className="btn-label">My Badge</span>
              </button>
              <button 
                className="debug-toggle-btn"
                onClick={() => {
                  logAction('Class Quiz Results Opened', '', '');
                  setShowClassQuizResults(true);
                }}
                title="View Class Quiz Results"
              >
                <span className="btn-icon">📋</span><span className="btn-label">Class Quiz Results</span>
              </button>
            </div>
            )}
            </div>
            </div>
          </div> {/* End history-panel-fixed-header */}
          
          {/* Scrollable chat history area */}
          <div className="history-panel-scrollable">
            <div className="history-container" ref={historyRef}>
              {messages.length <= 1 ? (
                <div className="no-history">Start a conversation to see history...</div>
              ) : (
                messages.filter(msg => msg.role !== 'system').map((msg, index) => {
                  const cleanedContent = renderMessageContent(msg.content);
                  if (!cleanedContent && msg.role === 'assistant') return null;
                  
                  return (
                  <div key={index} className={`message-bubble ${msg.role}`}>
                    <div className="message-sender">{msg.role === 'user' ? 'You' : 'Tutor'}</div>
                    <div className="message-content">
                      <MessageContent content={cleanedContent} />
                    </div>
                  </div>
                );
              })
            )}
            <div ref={historyEndRef} />
            </div>
          </div> {/* End history-panel-scrollable */}
        </aside>
      </header>
    </div>
  );
}

// Wrap AppContent with SessionProvider
function App() {
  return (
    <SessionProvider>
      <AppContent />
    </SessionProvider>
  );
}

export default App;

